/**
 * Texts of the public presentation page (Landing.tsx), in the 35 languages of
 * the web. Facts and figures come from makeit.app (features, plans and prices,
 * FAQ, videos); `lt()` falls back to English.
 */
import { currentLanguage } from "../player/i18n";

export interface LandingText {
  nav: { features: string; activities: string; videos: string; plans: string; faq: string; download: string; login: string; signup: string; student: string; menu: string; language: string };
  hero: { eyebrow: string; title: string; sub: string; start: string; demo: string; codeTitle: string; codeText: string; free: string };
  stats: { downloads: string; users: string; projects: string; countries: string };
  grid: { title: string; sub: string; items: { title: string; text: string }[] };
  create: { kicker: string; title: string; text: string; bullets: string[] };
  play: { kicker: string; title: string; text: string; bullets: string[] };
  quiz: { kicker: string; title: string; text: string; bullets: string[] };
  share: { kicker: string; title: string; text: string; code: string; link: string; classroom: string };
  classes: { kicker: string; title: string; text: string; bullets: string[]; mockTitle: string; mockCols: [string, string, string] };
  activities: { title: string; sub: string; games: string; quizzes: string };
  videos: { title: string; sub: string; main: string; tutorials: string; quiz: string; game: string; share: string; all: string };
  demos: { title: string; sub: string; games: string; quizzes: string; play: string; pages: string };
  plans: {
    title: string;
    sub: string;
    perYear: string;
    free: string;
    freePrice: string;
    freeFeatures: string[];
    educators: string;
    educatorsFeatures: string[];
    teams: string;
    teamsFeatures: string[];
    schools: string;
    schoolsFeatures: string[];
    startFree: string;
    tryFree: string;
    subscribe: string;
    note: string;
  };
  faq: { title: string; sub: string; items: { q: string; a: string }[] };
  download: { title: string; sub: string; web: string; available: string };
  final: { title: string; sub: string };
  footer: { tagline: string; product: string; help: string; legal: string; contact: string; terms: string; privacy: string; cookies: string; follow: string; support: string; demos: string };
}

const en: LandingText = {
  nav: { features: "Features", activities: "Activities", videos: "Videos", plans: "Plans", faq: "FAQ", download: "Download", login: "Log in", signup: "Sign up free", student: "I'm a student", menu: "Menu", language: "Language" },
  hero: {
    eyebrow: "Educational games and quizzes",
    title: "Create learning games and quizzes in minutes",
    sub: "Design projects with more than 20 kinds of activity, share them with anyone and see the results instantly. In the browser, on a tablet or on a phone.",
    start: "Start for free",
    demo: "Try a demo",
    codeTitle: "Got a code?",
    codeText: "Type the code you were given and play",
    free: "Free to start · No credit card",
  },
  stats: { downloads: "downloads", users: "active users", projects: "projects created", countries: "countries" },
  grid: {
    title: "Everything you need to learn by playing",
    sub: "Make It brings creating, playing and following up together in one place.",
    items: [
      { title: "Create in minutes", text: "Templates, images, sounds, drawings and YouTube videos at hand. No coding needed." },
      { title: "More than 20 activities", text: "Connect, drag, find the match, order, type, paint, trace symbols and much more." },
      { title: "Share with a code", text: "Send a code or a link and play on any device, without installing anything." },
      { title: "Instant results", text: "Answers, rankings and statistics for every quiz, and the progress of every student." },
      { title: "Classrooms", text: "Students join with a code. Share projects with the whole class or just a few." },
      { title: "Always in sync", text: "Your projects are saved in the cloud and ready on the web and in the app." },
    ],
  },
  create: {
    kicker: "Create",
    title: "Design your project your way",
    text: "Pick an activity, add your images, texts and sounds, and your project is ready. Everything you do is saved automatically.",
    bullets: ["Libraries with thousands of images and sounds", "Record your own voice", "Draw straight on the page", "Add YouTube videos", "Templates, fonts and background music"],
  },
  play: {
    kicker: "Games",
    title: "Made for the little ones",
    text: "Games reward effort: mistakes are not penalised, children can try as many times as they need and they don't even have to read.",
    bullets: ["Positive reinforcement on every page", "Instructions with text and sound", "No name or account needed to play"],
  },
  quiz: {
    kicker: "Quizzes",
    title: "Quizzes with scores and rankings",
    text: "Timed questions, automatic scoring and a live ranking. You see each player's answers and the statistics of every question.",
    bullets: ["Question, true or false, type the answer, maths…", "Time limit per question", "Ranking and statistics"],
  },
  share: {
    kicker: "Share",
    title: "One code and play",
    text: "Every project has a six-digit code and a link. Students open it in the browser or in the app, without creating an account.",
    code: "Code",
    link: "Link",
    classroom: "Or share it with a classroom",
  },
  classes: {
    kicker: "Classrooms",
    title: "Follow every student",
    text: "Create a classroom, give your students its code and share projects with them, automatically with everyone or only with some.",
    bullets: ["Students join with a code", "Results of every game and every quiz", "Share with the whole class or with a few"],
    mockTitle: "Results",
    mockCols: ["Student", "Score", "Time"],
  },
  activities: { title: "More than 20 kinds of activity", sub: "For games and for quizzes, ready to fill with your own content.", games: "Games", quizzes: "Quizzes" },
  videos: { title: "Discover Make It", sub: "See how a project is made in a few minutes.", main: "What is Make It?", tutorials: "Video tutorials", quiz: "How to create a quiz", game: "How to create a game", share: "How to share a project", all: "See all the tutorials" },
  demos: { title: "Try it now", sub: "Play some of our demos, no sign-up needed.", games: "Games", quizzes: "Quizzes", play: "Play", pages: "pages" },
  plans: {
    title: "Plans for every classroom",
    sub: "Start for free and go Premium when you need more.",
    perYear: "/year",
    free: "Free",
    freePrice: "0 €",
    freeFeatures: ["2 projects", "1 classroom", "1 folder", "Almost every feature"],
    educators: "Educators",
    educatorsFeatures: ["1 educator", "Up to 100 students", "Unlimited projects, classrooms and folders", "Results and statistics of every student"],
    teams: "Teams",
    teamsFeatures: ["5 educators", "Unlimited students", "Licence manager", "Everything in Educators"],
    schools: "Schools",
    schoolsFeatures: ["20 educators", "Unlimited students", "Licence manager", "Everything in Educators"],
    startFree: "Start for free",
    tryFree: "Try it for free",
    subscribe: "Subscribe",
    note: "Students always use Make It for free.",
  },
  faq: {
    title: "Frequently asked questions",
    sub: "Any doubts? Here are the most common answers.",
    items: [
      { q: "What are quizzes?", a: "Quizzes are a fun way to assess, learn or review any subject. They record each player's answers and score automatically, and you can see the answers, the ranking and the statistics." },
      { q: "What are games?", a: "Games are designed for young children, even those who can't read yet. They focus on positive reinforcement: mistakes are not penalised and there are unlimited attempts. Children don't enter a name to play." },
      { q: "How can I see the results of games?", a: "Because children don't identify themselves when they play, the results and statistics of games can only be seen when the game is shared through a classroom, not by code or link." },
      { q: "What are the limits of the free version?", a: "You can use almost every feature. The free version lets you create 2 projects, 1 classroom and 1 folder." },
      { q: "What do I get by subscribing?", a: "No limits: as many projects, classrooms and folders as you want, and the results and statistics of all your students." },
      { q: "Do I need an account?", a: "To play, no. With an account you can create and save your projects in the cloud, sync them across devices and create classrooms to share projects with your students." },
      { q: "Do students have to pay?", a: "No. Students always use Make It for free." },
      { q: "How do I share a project?", a: "Open the project and go to Share: you'll see its six-digit code and its link. Whoever has them can play it on any device. You can also share it with a classroom so students don't need any code." },
      { q: "What are classrooms and how do they work?", a: "Classrooms let you manage groups of students. Create a classroom, give your students its code so they can join, and share projects with them: with everyone or only with some. You'll see each student's results." },
      { q: "Can I use Make It on any device?", a: "Yes. You can create and play on the web, and the app is available for iPad, iPhone, Android and Mac. Your projects are kept in sync." },
    ],
  },
  download: { title: "Make It on all your devices", sub: "Use it right here in the browser or download the app.", web: "Open on the web", available: "Get it on" },
  final: { title: "Make learning fun", sub: "Create your first project today. It's free." },
  footer: { tagline: "Create games, quizzes, activities and stories, and share them anywhere.", product: "Product", help: "Help", legal: "Legal", contact: "Contact", terms: "Terms and conditions", privacy: "Privacy", cookies: "Cookie policy", follow: "Follow us", support: "With the support of ACCIÓ", demos: "Demos" },
};

const ca: LandingText = {
  nav: { features: "Funcions", activities: "Activitats", videos: "Vídeos", plans: "Plans", faq: "Preguntes", download: "Descarrega", login: "Entra", signup: "Crea un compte", student: "Soc alumne", menu: "Menú", language: "Idioma" },
  hero: {
    eyebrow: "Jocs i tests educatius",
    title: "Crea jocs i tests educatius en pocs minuts",
    sub: "Dissenya projectes amb més de 20 tipus d'activitats, comparteix-los amb qui vulguis i consulta'n els resultats a l'instant. Al navegador, a la tauleta o al mòbil.",
    start: "Comença gratis",
    demo: "Prova una demo",
    codeTitle: "Tens un codi?",
    codeText: "Escriu el codi que t'han donat i juga",
    free: "Comença gratis · Sense targeta",
  },
  stats: { downloads: "descàrregues", users: "usuaris actius", projects: "projectes creats", countries: "països" },
  grid: {
    title: "Tot el que necessites per aprendre jugant",
    sub: "Make It reuneix en un sol lloc crear, jugar i fer-ne el seguiment.",
    items: [
      { title: "Crea en minuts", text: "Plantilles, imatges, sons, dibuixos i vídeos de YouTube a l'abast, sense saber programar." },
      { title: "Més de 20 activitats", text: "Relaciona, arrossega, troba la parella, ordena, escriu, pinta, traça símbols i molt més." },
      { title: "Comparteix amb un codi", text: "Envia un codi o un enllaç i es pot jugar a qualsevol dispositiu, sense instal·lar res." },
      { title: "Resultats a l'instant", text: "Respostes, rànquings i estadístiques de cada test, i el progrés de cada alumne." },
      { title: "Aules", text: "Els alumnes s'hi apunten amb un codi. Comparteix projectes amb tota l'aula o només amb alguns." },
      { title: "Sempre sincronitzat", text: "Els teus projectes es desen al núvol i els tens a la web i a l'app." },
    ],
  },
  create: {
    kicker: "Crea",
    title: "Dissenya el teu projecte com vulguis",
    text: "Tria una activitat, afegeix-hi imatges, textos i sons, i el projecte ja està llest. Tot el que fas es desa automàticament.",
    bullets: ["Biblioteques amb milers d'imatges i sons", "Grava la teva veu", "Dibuixa directament a la pàgina", "Afegeix vídeos de YouTube", "Plantilles, fonts i música de fons"],
  },
  play: {
    kicker: "Jocs",
    title: "Pensat per als més petits",
    text: "Els jocs premien l'esforç: no penalitzen els errors, es pot tornar a provar tantes vegades com calgui i ni tan sols cal saber llegir.",
    bullets: ["Reforç positiu a cada pàgina", "Instruccions amb text i so", "Per jugar no cal nom ni compte"],
  },
  quiz: {
    kicker: "Tests",
    title: "Tests amb puntuació i rànquing",
    text: "Preguntes amb temps, puntuació automàtica i rànquing en directe. Tu veus les respostes de cadascú i les estadístiques de cada pregunta.",
    bullets: ["Pregunta, cert o fals, escriu la resposta, matemàtiques…", "Temps per pregunta", "Rànquing i estadístiques"],
  },
  share: {
    kicker: "Comparteix",
    title: "Un codi i a jugar",
    text: "Cada projecte té un codi de sis xifres i un enllaç. Els alumnes l'obren al navegador o a l'app, sense crear cap compte.",
    code: "Codi",
    link: "Enllaç",
    classroom: "O comparteix-lo amb una aula",
  },
  classes: {
    kicker: "Aules",
    title: "Segueix cada alumne",
    text: "Crea una aula, dona'n el codi als alumnes i comparteix-hi projectes, automàticament amb tothom o només amb alguns.",
    bullets: ["Els alumnes s'apunten amb un codi", "Resultats de cada joc i de cada test", "Comparteix amb tota l'aula o amb uns quants"],
    mockTitle: "Resultats",
    mockCols: ["Alumne", "Puntuació", "Temps"],
  },
  activities: { title: "Més de 20 tipus d'activitats", sub: "Per a jocs i per a tests, a punt per omplir amb el teu contingut.", games: "Jocs", quizzes: "Tests" },
  videos: { title: "Descobreix Make It", sub: "Mira com es crea un projecte en pocs minuts.", main: "Què és Make It?", tutorials: "Videotutorials", quiz: "Com crear un test", game: "Com crear un joc", share: "Com compartir un projecte", all: "Veure tots els tutorials" },
  demos: { title: "Prova-ho ara", sub: "Juga a algunes de les nostres demos, sense registrar-te.", games: "Jocs", quizzes: "Tests", play: "Juga", pages: "pàgines" },
  plans: {
    title: "Plans per a cada aula",
    sub: "Comença gratis i passa a Premium quan en necessitis més.",
    perYear: "/any",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 projectes", "1 aula", "1 carpeta", "Gairebé totes les funcions"],
    educators: "Educadors",
    educatorsFeatures: ["1 educador", "Fins a 100 alumnes", "Projectes, aules i carpetes il·limitats", "Resultats i estadístiques de tots els alumnes"],
    teams: "Equips",
    teamsFeatures: ["5 educadors", "Alumnes il·limitats", "Gestor de llicències", "Tot el que inclou Educadors"],
    schools: "Escoles",
    schoolsFeatures: ["20 educadors", "Alumnes il·limitats", "Gestor de llicències", "Tot el que inclou Educadors"],
    startFree: "Comença gratis",
    tryFree: "Prova-ho gratis",
    subscribe: "Subscriu-te",
    note: "Els alumnes sempre fan servir Make It gratis.",
  },
  faq: {
    title: "Preguntes freqüents",
    sub: "Tens dubtes? Aquí tens les respostes més habituals.",
    items: [
      { q: "Què són els tests?", a: "Els tests són una manera divertida d'avaluar, aprendre o repassar qualsevol tema. Registren automàticament les respostes i la puntuació de cada jugador, i tu en pots veure les respostes, el rànquing i les estadístiques." },
      { q: "Què són els jocs?", a: "Els jocs estan pensats per als infants més petits, fins i tot els que encara no saben llegir. Prioritzen el reforç positiu: no penalitzen els errors i es pot tornar a provar sense límit. Per jugar no cal escriure cap nom." },
      { q: "Com puc veure els resultats dels jocs?", a: "Com que els infants no s'identifiquen quan juguen, els resultats i les estadístiques dels jocs només es poden veure quan el joc es comparteix a través d'una aula, no per codi ni per enllaç." },
      { q: "Quines limitacions té la versió gratuïta?", a: "Pots fer servir gairebé totes les funcions. La versió gratuïta permet crear 2 projectes, 1 aula i 1 carpeta." },
      { q: "Què guanyo si em subscric?", a: "Cap límit: tants projectes, aules i carpetes com vulguis, i els resultats i les estadístiques de tots els teus alumnes." },
      { q: "Cal crear un compte?", a: "Per jugar, no. Amb un compte pots crear i desar els teus projectes al núvol, sincronitzar-los entre dispositius i crear aules per compartir projectes amb els teus alumnes." },
      { q: "Els alumnes han de pagar?", a: "No. Els alumnes sempre fan servir Make It gratis." },
      { q: "Com comparteixo un projecte?", a: "Obre el projecte i ves a Comparteix: hi veuràs el codi de sis xifres i l'enllaç. Qui els tingui el podrà jugar a qualsevol dispositiu. També el pots compartir amb una aula i així els alumnes no necessiten cap codi." },
      { q: "Què són les aules i com funcionen?", a: "Les aules serveixen per gestionar grups d'alumnes. Crea una aula, dona'n el codi als alumnes perquè s'hi apuntin i comparteix-hi projectes: amb tothom o només amb alguns. Veuràs els resultats de cada alumne." },
      { q: "Puc fer servir Make It a qualsevol dispositiu?", a: "Sí. Pots crear i jugar a la web, i l'app està disponible per a iPad, iPhone, Android i Mac. Els teus projectes es mantenen sincronitzats." },
    ],
  },
  download: { title: "Make It a tots els teus dispositius", sub: "Fes-lo servir aquí mateix, al navegador, o descarrega l'app.", web: "Obre-ho a la web", available: "Disponible a" },
  final: { title: "Fes que aprendre sigui divertit", sub: "Crea el teu primer projecte avui mateix. És gratis." },
  footer: { tagline: "Crea jocs, tests, activitats i històries, i comparteix-los arreu.", product: "Producte", help: "Ajuda", legal: "Legal", contact: "Contacte", terms: "Termes i condicions", privacy: "Privacitat", cookies: "Política de galetes", follow: "Segueix-nos", support: "Amb el suport d'ACCIÓ", demos: "Demos" },
};

const es: LandingText = {
  nav: { features: "Funciones", activities: "Actividades", videos: "Vídeos", plans: "Planes", faq: "Preguntas", download: "Descargar", login: "Entrar", signup: "Crear una cuenta", student: "Soy alumno", menu: "Menú", language: "Idioma" },
  hero: {
    eyebrow: "Juegos y tests educativos",
    title: "Crea juegos y tests educativos en pocos minutos",
    sub: "Diseña proyectos con más de 20 tipos de actividades, compártelos con quien quieras y consulta los resultados al instante. En el navegador, en la tableta o en el móvil.",
    start: "Empieza gratis",
    demo: "Prueba una demo",
    codeTitle: "¿Tienes un código?",
    codeText: "Escribe el código que te han dado y juega",
    free: "Empieza gratis · Sin tarjeta",
  },
  stats: { downloads: "descargas", users: "usuarios activos", projects: "proyectos creados", countries: "países" },
  grid: {
    title: "Todo lo que necesitas para aprender jugando",
    sub: "Make It reúne en un solo lugar crear, jugar y hacer el seguimiento.",
    items: [
      { title: "Crea en minutos", text: "Plantillas, imágenes, sonidos, dibujos y vídeos de YouTube a mano, sin saber programar." },
      { title: "Más de 20 actividades", text: "Relaciona, arrastra, encuentra la pareja, ordena, escribe, pinta, traza símbolos y mucho más." },
      { title: "Comparte con un código", text: "Envía un código o un enlace y se puede jugar en cualquier dispositivo, sin instalar nada." },
      { title: "Resultados al instante", text: "Respuestas, rankings y estadísticas de cada test, y el progreso de cada alumno." },
      { title: "Aulas", text: "Los alumnos se apuntan con un código. Comparte proyectos con toda el aula o solo con algunos." },
      { title: "Siempre sincronizado", text: "Tus proyectos se guardan en la nube y los tienes en la web y en la app." },
    ],
  },
  create: {
    kicker: "Crea",
    title: "Diseña tu proyecto como quieras",
    text: "Elige una actividad, añade imágenes, textos y sonidos, y tu proyecto está listo. Todo lo que haces se guarda automáticamente.",
    bullets: ["Bibliotecas con miles de imágenes y sonidos", "Graba tu voz", "Dibuja directamente en la página", "Añade vídeos de YouTube", "Plantillas, fuentes y música de fondo"],
  },
  play: {
    kicker: "Juegos",
    title: "Pensado para los más pequeños",
    text: "Los juegos premian el esfuerzo: no penalizan los errores, se puede volver a probar tantas veces como haga falta y ni siquiera hace falta saber leer.",
    bullets: ["Refuerzo positivo en cada página", "Instrucciones con texto y sonido", "Para jugar no hace falta nombre ni cuenta"],
  },
  quiz: {
    kicker: "Tests",
    title: "Tests con puntuación y ranking",
    text: "Preguntas con tiempo, puntuación automática y ranking en directo. Tú ves las respuestas de cada uno y las estadísticas de cada pregunta.",
    bullets: ["Pregunta, verdadero o falso, escribe la respuesta, matemáticas…", "Tiempo por pregunta", "Ranking y estadísticas"],
  },
  share: {
    kicker: "Comparte",
    title: "Un código y a jugar",
    text: "Cada proyecto tiene un código de seis cifras y un enlace. Los alumnos lo abren en el navegador o en la app, sin crear ninguna cuenta.",
    code: "Código",
    link: "Enlace",
    classroom: "O compártelo con un aula",
  },
  classes: {
    kicker: "Aulas",
    title: "Sigue a cada alumno",
    text: "Crea un aula, da su código a los alumnos y comparte proyectos con ellos, automáticamente con todos o solo con algunos.",
    bullets: ["Los alumnos se apuntan con un código", "Resultados de cada juego y de cada test", "Comparte con toda el aula o con unos pocos"],
    mockTitle: "Resultados",
    mockCols: ["Alumno", "Puntuación", "Tiempo"],
  },
  activities: { title: "Más de 20 tipos de actividades", sub: "Para juegos y para tests, listas para llenar con tu contenido.", games: "Juegos", quizzes: "Tests" },
  videos: { title: "Descubre Make It", sub: "Mira cómo se crea un proyecto en pocos minutos.", main: "¿Qué es Make It?", tutorials: "Videotutoriales", quiz: "Cómo crear un test", game: "Cómo crear un juego", share: "Cómo compartir un proyecto", all: "Ver todos los tutoriales" },
  demos: { title: "Pruébalo ahora", sub: "Juega a algunas de nuestras demos, sin registrarte.", games: "Juegos", quizzes: "Tests", play: "Jugar", pages: "páginas" },
  plans: {
    title: "Planes para cada aula",
    sub: "Empieza gratis y pásate a Premium cuando necesites más.",
    perYear: "/año",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 proyectos", "1 aula", "1 carpeta", "Casi todas las funciones"],
    educators: "Educadores",
    educatorsFeatures: ["1 educador", "Hasta 100 alumnos", "Proyectos, aulas y carpetas ilimitados", "Resultados y estadísticas de todos los alumnos"],
    teams: "Equipos",
    teamsFeatures: ["5 educadores", "Alumnos ilimitados", "Gestor de licencias", "Todo lo que incluye Educadores"],
    schools: "Escuelas",
    schoolsFeatures: ["20 educadores", "Alumnos ilimitados", "Gestor de licencias", "Todo lo que incluye Educadores"],
    startFree: "Empieza gratis",
    tryFree: "Pruébalo gratis",
    subscribe: "Suscríbete",
    note: "Los alumnos siempre usan Make It gratis.",
  },
  faq: {
    title: "Preguntas frecuentes",
    sub: "¿Tienes dudas? Aquí tienes las respuestas más habituales.",
    items: [
      { q: "¿Qué son los tests?", a: "Los tests son una forma divertida de evaluar, aprender o repasar cualquier tema. Registran automáticamente las respuestas y la puntuación de cada jugador, y tú puedes ver las respuestas, el ranking y las estadísticas." },
      { q: "¿Qué son los juegos?", a: "Los juegos están pensados para los niños más pequeños, incluso los que todavía no saben leer. Priorizan el refuerzo positivo: no penalizan los errores y se puede volver a probar sin límite. Para jugar no hay que escribir ningún nombre." },
      { q: "¿Cómo puedo ver los resultados de los juegos?", a: "Como los niños no se identifican al jugar, los resultados y las estadísticas de los juegos solo se pueden ver cuando el juego se comparte a través de un aula, no por código ni por enlace." },
      { q: "¿Qué limitaciones tiene la versión gratuita?", a: "Puedes usar casi todas las funciones. La versión gratuita permite crear 2 proyectos, 1 aula y 1 carpeta." },
      { q: "¿Qué gano si me suscribo?", a: "Ningún límite: tantos proyectos, aulas y carpetas como quieras, y los resultados y las estadísticas de todos tus alumnos." },
      { q: "¿Hace falta crear una cuenta?", a: "Para jugar, no. Con una cuenta puedes crear y guardar tus proyectos en la nube, sincronizarlos entre dispositivos y crear aulas para compartir proyectos con tus alumnos." },
      { q: "¿Los alumnos tienen que pagar?", a: "No. Los alumnos siempre usan Make It gratis." },
      { q: "¿Cómo comparto un proyecto?", a: "Abre el proyecto y ve a Compartir: verás su código de seis cifras y su enlace. Quien los tenga podrá jugarlo en cualquier dispositivo. También puedes compartirlo con un aula y así los alumnos no necesitan ningún código." },
      { q: "¿Qué son las aulas y cómo funcionan?", a: "Las aulas sirven para gestionar grupos de alumnos. Crea un aula, da su código a los alumnos para que se apunten y comparte proyectos con ellos: con todos o solo con algunos. Verás los resultados de cada alumno." },
      { q: "¿Puedo usar Make It en cualquier dispositivo?", a: "Sí. Puedes crear y jugar en la web, y la app está disponible para iPad, iPhone, Android y Mac. Tus proyectos se mantienen sincronizados." },
    ],
  },
  download: { title: "Make It en todos tus dispositivos", sub: "Úsalo aquí mismo, en el navegador, o descarga la app.", web: "Ábrelo en la web", available: "Disponible en" },
  final: { title: "Haz que aprender sea divertido", sub: "Crea tu primer proyecto hoy mismo. Es gratis." },
  footer: { tagline: "Crea juegos, tests, actividades e historias, y compártelos en cualquier lugar.", product: "Producto", help: "Ayuda", legal: "Legal", contact: "Contacto", terms: "Términos y condiciones", privacy: "Privacidad", cookies: "Política de cookies", follow: "Síguenos", support: "Con el apoyo de ACCIÓ", demos: "Demos" },
};

const fr: LandingText = {
  nav: { features: "Fonctions", activities: "Activités", videos: "Vidéos", plans: "Offres", faq: "FAQ", download: "Télécharger", login: "Se connecter", signup: "Créer un compte", student: "Je suis élève", menu: "Menu", language: "Langue" },
  hero: {
    eyebrow: "Jeux et quiz éducatifs",
    title: "Créez des jeux et des quiz éducatifs en quelques minutes",
    sub: "Concevez des projets avec plus de 20 types d'activités, partagez-les avec qui vous voulez et consultez les résultats instantanément. Dans le navigateur, sur tablette ou sur mobile.",
    start: "Commencer gratuitement",
    demo: "Essayer une démo",
    codeTitle: "Vous avez un code ?",
    codeText: "Saisissez le code qu'on vous a donné et jouez",
    free: "Gratuit pour commencer · Sans carte",
  },
  stats: { downloads: "téléchargements", users: "utilisateurs actifs", projects: "projets créés", countries: "pays" },
  grid: {
    title: "Tout ce qu'il faut pour apprendre en jouant",
    sub: "Make It réunit au même endroit la création, le jeu et le suivi.",
    items: [
      { title: "Créez en quelques minutes", text: "Modèles, images, sons, dessins et vidéos YouTube à portée de main, sans programmer." },
      { title: "Plus de 20 activités", text: "Relier, glisser, trouver la paire, ordonner, écrire, colorier, tracer des symboles et bien plus." },
      { title: "Partagez avec un code", text: "Envoyez un code ou un lien et jouez sur n'importe quel appareil, sans rien installer." },
      { title: "Résultats instantanés", text: "Réponses, classements et statistiques de chaque quiz, et la progression de chaque élève." },
      { title: "Classes", text: "Les élèves rejoignent la classe avec un code. Partagez avec toute la classe ou seulement quelques élèves." },
      { title: "Toujours synchronisé", text: "Vos projets sont enregistrés dans le cloud, sur le web et dans l'application." },
    ],
  },
  create: {
    kicker: "Créer",
    title: "Concevez votre projet comme vous le souhaitez",
    text: "Choisissez une activité, ajoutez vos images, textes et sons : votre projet est prêt. Tout est enregistré automatiquement.",
    bullets: ["Bibliothèques de milliers d'images et de sons", "Enregistrez votre voix", "Dessinez directement sur la page", "Ajoutez des vidéos YouTube", "Modèles, polices et musique de fond"],
  },
  play: {
    kicker: "Jeux",
    title: "Pensé pour les plus petits",
    text: "Les jeux récompensent l'effort : les erreurs ne sont pas pénalisées, on peut réessayer autant de fois que nécessaire et il n'est même pas nécessaire de savoir lire.",
    bullets: ["Renforcement positif à chaque page", "Consignes en texte et en son", "Ni nom ni compte pour jouer"],
  },
  quiz: {
    kicker: "Quiz",
    title: "Des quiz avec score et classement",
    text: "Questions chronométrées, score automatique et classement en direct. Vous voyez les réponses de chacun et les statistiques de chaque question.",
    bullets: ["Question, vrai ou faux, réponse à écrire, maths…", "Temps par question", "Classement et statistiques"],
  },
  share: {
    kicker: "Partager",
    title: "Un code et on joue",
    text: "Chaque projet a un code à six chiffres et un lien. Les élèves l'ouvrent dans le navigateur ou dans l'application, sans créer de compte.",
    code: "Code",
    link: "Lien",
    classroom: "Ou partagez-le avec une classe",
  },
  classes: {
    kicker: "Classes",
    title: "Suivez chaque élève",
    text: "Créez une classe, donnez son code aux élèves et partagez-y des projets, automatiquement avec tous ou seulement avec certains.",
    bullets: ["Les élèves rejoignent la classe avec un code", "Résultats de chaque jeu et de chaque quiz", "Partage avec toute la classe ou quelques élèves"],
    mockTitle: "Résultats",
    mockCols: ["Élève", "Score", "Temps"],
  },
  activities: { title: "Plus de 20 types d'activités", sub: "Pour les jeux et les quiz, prêtes à remplir avec votre contenu.", games: "Jeux", quizzes: "Quiz" },
  videos: { title: "Découvrez Make It", sub: "Voyez comment créer un projet en quelques minutes.", main: "Qu'est-ce que Make It ?", tutorials: "Tutoriels vidéo", quiz: "Créer un quiz", game: "Créer un jeu", share: "Partager un projet", all: "Voir tous les tutoriels" },
  demos: { title: "Essayez maintenant", sub: "Jouez à quelques-unes de nos démos, sans inscription.", games: "Jeux", quizzes: "Quiz", play: "Jouer", pages: "pages" },
  plans: {
    title: "Une offre pour chaque classe",
    sub: "Commencez gratuitement et passez à Premium quand vous en avez besoin.",
    perYear: "/an",
    free: "Gratuit",
    freePrice: "0 €",
    freeFeatures: ["2 projets", "1 classe", "1 dossier", "Presque toutes les fonctions"],
    educators: "Enseignants",
    educatorsFeatures: ["1 enseignant", "Jusqu'à 100 élèves", "Projets, classes et dossiers illimités", "Résultats et statistiques de tous les élèves"],
    teams: "Équipes",
    teamsFeatures: ["5 enseignants", "Élèves illimités", "Gestionnaire de licences", "Tout ce qu'inclut Enseignants"],
    schools: "Écoles",
    schoolsFeatures: ["20 enseignants", "Élèves illimités", "Gestionnaire de licences", "Tout ce qu'inclut Enseignants"],
    startFree: "Commencer gratuitement",
    tryFree: "Essayer gratuitement",
    subscribe: "S'abonner",
    note: "Les élèves utilisent toujours Make It gratuitement.",
  },
  faq: {
    title: "Questions fréquentes",
    sub: "Des doutes ? Voici les réponses les plus courantes.",
    items: [
      { q: "Que sont les quiz ?", a: "Les quiz sont une façon amusante d'évaluer, d'apprendre ou de réviser n'importe quel sujet. Ils enregistrent automatiquement les réponses et le score de chaque joueur, et vous pouvez voir les réponses, le classement et les statistiques." },
      { q: "Que sont les jeux ?", a: "Les jeux sont pensés pour les jeunes enfants, même ceux qui ne savent pas encore lire. Ils privilégient le renforcement positif : les erreurs ne sont pas pénalisées et les essais sont illimités. Les enfants n'écrivent pas de nom pour jouer." },
      { q: "Comment voir les résultats des jeux ?", a: "Comme les enfants ne s'identifient pas quand ils jouent, les résultats et statistiques des jeux ne sont visibles que lorsque le jeu est partagé via une classe, pas par code ni par lien." },
      { q: "Quelles sont les limites de la version gratuite ?", a: "Vous pouvez utiliser presque toutes les fonctions. La version gratuite permet de créer 2 projets, 1 classe et 1 dossier." },
      { q: "Que m'apporte l'abonnement ?", a: "Aucune limite : autant de projets, de classes et de dossiers que vous voulez, et les résultats et statistiques de tous vos élèves." },
      { q: "Faut-il créer un compte ?", a: "Pour jouer, non. Avec un compte, vous pouvez créer et enregistrer vos projets dans le cloud, les synchroniser entre appareils et créer des classes pour partager des projets avec vos élèves." },
      { q: "Les élèves doivent-ils payer ?", a: "Non. Les élèves utilisent toujours Make It gratuitement." },
      { q: "Comment partager un projet ?", a: "Ouvrez le projet et allez dans Partager : vous verrez son code à six chiffres et son lien. Ceux qui les ont peuvent y jouer sur n'importe quel appareil. Vous pouvez aussi le partager avec une classe, sans code pour les élèves." },
      { q: "Que sont les classes et comment fonctionnent-elles ?", a: "Les classes permettent de gérer des groupes d'élèves. Créez une classe, donnez son code aux élèves pour qu'ils la rejoignent et partagez-y des projets : avec tous ou seulement certains. Vous verrez les résultats de chaque élève." },
      { q: "Puis-je utiliser Make It sur n'importe quel appareil ?", a: "Oui. Vous pouvez créer et jouer sur le web, et l'application existe pour iPad, iPhone, Android et Mac. Vos projets restent synchronisés." },
    ],
  },
  download: { title: "Make It sur tous vos appareils", sub: "Utilisez-le ici, dans le navigateur, ou téléchargez l'application.", web: "Ouvrir sur le web", available: "Disponible sur" },
  final: { title: "Rendez l'apprentissage amusant", sub: "Créez votre premier projet dès aujourd'hui. C'est gratuit." },
  footer: { tagline: "Créez des jeux, des quiz, des activités et des histoires, et partagez-les partout.", product: "Produit", help: "Aide", legal: "Mentions", contact: "Contact", terms: "Conditions générales", privacy: "Confidentialité", cookies: "Politique de cookies", follow: "Suivez-nous", support: "Avec le soutien d'ACCIÓ", demos: "Démos" },
};

const pt: LandingText = {
  nav: { features: "Funções", activities: "Atividades", videos: "Vídeos", plans: "Planos", faq: "Perguntas", download: "Descarregar", login: "Entrar", signup: "Criar conta", student: "Sou aluno", menu: "Menu", language: "Idioma" },
  hero: {
    eyebrow: "Jogos e testes educativos",
    title: "Crie jogos e testes educativos em poucos minutos",
    sub: "Desenhe projetos com mais de 20 tipos de atividades, partilhe-os com quem quiser e consulte os resultados no momento. No navegador, no tablet ou no telemóvel.",
    start: "Comece grátis",
    demo: "Experimente uma demo",
    codeTitle: "Tem um código?",
    codeText: "Escreva o código que lhe deram e jogue",
    free: "Comece grátis · Sem cartão",
  },
  stats: { downloads: "transferências", users: "utilizadores ativos", projects: "projetos criados", countries: "países" },
  grid: {
    title: "Tudo o que precisa para aprender a jogar",
    sub: "O Make It junta num só lugar criar, jogar e acompanhar.",
    items: [
      { title: "Crie em minutos", text: "Modelos, imagens, sons, desenhos e vídeos do YouTube à mão, sem saber programar." },
      { title: "Mais de 20 atividades", text: "Relacionar, arrastar, encontrar o par, ordenar, escrever, pintar, traçar símbolos e muito mais." },
      { title: "Partilhe com um código", text: "Envie um código ou uma ligação e jogue em qualquer dispositivo, sem instalar nada." },
      { title: "Resultados no momento", text: "Respostas, classificações e estatísticas de cada teste, e o progresso de cada aluno." },
      { title: "Salas de aula", text: "Os alunos entram com um código. Partilhe projetos com toda a turma ou só com alguns." },
      { title: "Sempre sincronizado", text: "Os seus projetos ficam guardados na nuvem, na web e na aplicação." },
    ],
  },
  create: {
    kicker: "Criar",
    title: "Desenhe o seu projeto como quiser",
    text: "Escolha uma atividade, junte imagens, textos e sons e o projeto está pronto. Tudo o que faz é guardado automaticamente.",
    bullets: ["Bibliotecas com milhares de imagens e sons", "Grave a sua voz", "Desenhe diretamente na página", "Junte vídeos do YouTube", "Modelos, tipos de letra e música de fundo"],
  },
  play: {
    kicker: "Jogos",
    title: "Pensado para os mais pequenos",
    text: "Os jogos premeiam o esforço: os erros não são penalizados, pode-se tentar as vezes que for preciso e nem é preciso saber ler.",
    bullets: ["Reforço positivo em cada página", "Instruções com texto e som", "Para jogar não é preciso nome nem conta"],
  },
  quiz: {
    kicker: "Testes",
    title: "Testes com pontuação e classificação",
    text: "Perguntas com tempo, pontuação automática e classificação em direto. Vê as respostas de cada um e as estatísticas de cada pergunta.",
    bullets: ["Pergunta, verdadeiro ou falso, escrever a resposta, matemática…", "Tempo por pergunta", "Classificação e estatísticas"],
  },
  share: {
    kicker: "Partilhar",
    title: "Um código e a jogar",
    text: "Cada projeto tem um código de seis algarismos e uma ligação. Os alunos abrem-no no navegador ou na aplicação, sem criar conta.",
    code: "Código",
    link: "Ligação",
    classroom: "Ou partilhe-o com uma sala de aula",
  },
  classes: {
    kicker: "Salas de aula",
    title: "Acompanhe cada aluno",
    text: "Crie uma sala de aula, dê o código aos alunos e partilhe projetos, automaticamente com todos ou só com alguns.",
    bullets: ["Os alunos entram com um código", "Resultados de cada jogo e de cada teste", "Partilhe com toda a turma ou com alguns"],
    mockTitle: "Resultados",
    mockCols: ["Aluno", "Pontuação", "Tempo"],
  },
  activities: { title: "Mais de 20 tipos de atividades", sub: "Para jogos e para testes, prontas a preencher com o seu conteúdo.", games: "Jogos", quizzes: "Testes" },
  videos: { title: "Descubra o Make It", sub: "Veja como se cria um projeto em poucos minutos.", main: "O que é o Make It?", tutorials: "Tutoriais em vídeo", quiz: "Como criar um teste", game: "Como criar um jogo", share: "Como partilhar um projeto", all: "Ver todos os tutoriais" },
  demos: { title: "Experimente agora", sub: "Jogue algumas das nossas demos, sem registo.", games: "Jogos", quizzes: "Testes", play: "Jogar", pages: "páginas" },
  plans: {
    title: "Planos para cada sala de aula",
    sub: "Comece grátis e passe a Premium quando precisar de mais.",
    perYear: "/ano",
    free: "Grátis",
    freePrice: "0 €",
    freeFeatures: ["2 projetos", "1 sala de aula", "1 pasta", "Quase todas as funções"],
    educators: "Educadores",
    educatorsFeatures: ["1 educador", "Até 100 alunos", "Projetos, salas e pastas ilimitados", "Resultados e estatísticas de todos os alunos"],
    teams: "Equipas",
    teamsFeatures: ["5 educadores", "Alunos ilimitados", "Gestor de licenças", "Tudo o que inclui Educadores"],
    schools: "Escolas",
    schoolsFeatures: ["20 educadores", "Alunos ilimitados", "Gestor de licenças", "Tudo o que inclui Educadores"],
    startFree: "Comece grátis",
    tryFree: "Experimente grátis",
    subscribe: "Subscrever",
    note: "Os alunos usam sempre o Make It gratuitamente.",
  },
  faq: {
    title: "Perguntas frequentes",
    sub: "Tem dúvidas? Aqui estão as respostas mais habituais.",
    items: [
      { q: "O que são os testes?", a: "Os testes são uma forma divertida de avaliar, aprender ou rever qualquer tema. Registam automaticamente as respostas e a pontuação de cada jogador, e pode ver as respostas, a classificação e as estatísticas." },
      { q: "O que são os jogos?", a: "Os jogos foram pensados para as crianças mais pequenas, mesmo as que ainda não sabem ler. Dão prioridade ao reforço positivo: os erros não são penalizados e pode-se tentar sem limite. Para jogar não é preciso escrever nenhum nome." },
      { q: "Como posso ver os resultados dos jogos?", a: "Como as crianças não se identificam ao jogar, os resultados e as estatísticas dos jogos só se podem ver quando o jogo é partilhado através de uma sala de aula, e não por código nem por ligação." },
      { q: "Que limitações tem a versão gratuita?", a: "Pode usar quase todas as funções. A versão gratuita permite criar 2 projetos, 1 sala de aula e 1 pasta." },
      { q: "O que ganho com a subscrição?", a: "Nenhum limite: tantos projetos, salas de aula e pastas quantos quiser, e os resultados e as estatísticas de todos os seus alunos." },
      { q: "É preciso criar uma conta?", a: "Para jogar, não. Com uma conta pode criar e guardar os seus projetos na nuvem, sincronizá-los entre dispositivos e criar salas de aula para partilhar projetos com os seus alunos." },
      { q: "Os alunos têm de pagar?", a: "Não. Os alunos usam sempre o Make It gratuitamente." },
      { q: "Como partilho um projeto?", a: "Abra o projeto e vá a Partilhar: verá o código de seis algarismos e a ligação. Quem os tiver pode jogá-lo em qualquer dispositivo. Também o pode partilhar com uma sala de aula e assim os alunos não precisam de código." },
      { q: "O que são as salas de aula e como funcionam?", a: "As salas de aula servem para gerir grupos de alunos. Crie uma sala, dê o código aos alunos para que entrem e partilhe projetos: com todos ou só com alguns. Verá os resultados de cada aluno." },
      { q: "Posso usar o Make It em qualquer dispositivo?", a: "Sim. Pode criar e jogar na web, e a aplicação está disponível para iPad, iPhone, Android e Mac. Os seus projetos ficam sincronizados." },
    ],
  },
  download: { title: "Make It em todos os seus dispositivos", sub: "Use-o aqui mesmo, no navegador, ou descarregue a aplicação.", web: "Abrir na web", available: "Disponível na" },
  final: { title: "Torne a aprendizagem divertida", sub: "Crie o seu primeiro projeto hoje mesmo. É grátis." },
  footer: { tagline: "Crie jogos, testes, atividades e histórias, e partilhe-os em qualquer lugar.", product: "Produto", help: "Ajuda", legal: "Legal", contact: "Contacto", terms: "Termos e condições", privacy: "Privacidade", cookies: "Política de cookies", follow: "Siga-nos", support: "Com o apoio da ACCIÓ", demos: "Demos" },
};

const it: LandingText = {
  nav: { features: "Funzioni", activities: "Attività", videos: "Video", plans: "Piani", faq: "Domande", download: "Scarica", login: "Accedi", signup: "Crea un account", student: "Sono uno studente", menu: "Menu", language: "Lingua" },
  hero: {
    eyebrow: "Giochi e quiz educativi",
    title: "Crea giochi e quiz educativi in pochi minuti",
    sub: "Progetta lavori con più di 20 tipi di attività, condividili con chi vuoi e consulta i risultati all'istante. Nel browser, sul tablet o sul cellulare.",
    start: "Inizia gratis",
    demo: "Prova una demo",
    codeTitle: "Hai un codice?",
    codeText: "Scrivi il codice che ti hanno dato e gioca",
    free: "Inizia gratis · Senza carta",
  },
  stats: { downloads: "download", users: "utenti attivi", projects: "progetti creati", countries: "paesi" },
  grid: {
    title: "Tutto ciò che serve per imparare giocando",
    sub: "Make It riunisce in un solo posto creare, giocare e seguire i progressi.",
    items: [
      { title: "Crea in pochi minuti", text: "Modelli, immagini, suoni, disegni e video di YouTube a portata di mano, senza programmare." },
      { title: "Più di 20 attività", text: "Collega, trascina, trova la coppia, ordina, scrivi, colora, traccia simboli e molto altro." },
      { title: "Condividi con un codice", text: "Invia un codice o un link e si gioca su qualsiasi dispositivo, senza installare nulla." },
      { title: "Risultati all'istante", text: "Risposte, classifiche e statistiche di ogni quiz, e i progressi di ogni studente." },
      { title: "Classi", text: "Gli studenti entrano con un codice. Condividi con tutta la classe o solo con alcuni." },
      { title: "Sempre sincronizzato", text: "I tuoi progetti sono salvati nel cloud, sul web e nell'app." },
    ],
  },
  create: {
    kicker: "Crea",
    title: "Progetta il tuo lavoro come vuoi",
    text: "Scegli un'attività, aggiungi immagini, testi e suoni e il progetto è pronto. Tutto viene salvato automaticamente.",
    bullets: ["Librerie con migliaia di immagini e suoni", "Registra la tua voce", "Disegna direttamente sulla pagina", "Aggiungi video di YouTube", "Modelli, caratteri e musica di sottofondo"],
  },
  play: {
    kicker: "Giochi",
    title: "Pensato per i più piccoli",
    text: "I giochi premiano l'impegno: gli errori non vengono penalizzati, si può riprovare tutte le volte che serve e non serve nemmeno saper leggere.",
    bullets: ["Rinforzo positivo in ogni pagina", "Istruzioni con testo e suono", "Per giocare non servono nome né account"],
  },
  quiz: {
    kicker: "Quiz",
    title: "Quiz con punteggio e classifica",
    text: "Domande a tempo, punteggio automatico e classifica in diretta. Vedi le risposte di ognuno e le statistiche di ogni domanda.",
    bullets: ["Domanda, vero o falso, scrivi la risposta, matematica…", "Tempo per domanda", "Classifica e statistiche"],
  },
  share: {
    kicker: "Condividi",
    title: "Un codice e si gioca",
    text: "Ogni progetto ha un codice di sei cifre e un link. Gli studenti lo aprono nel browser o nell'app, senza creare un account.",
    code: "Codice",
    link: "Link",
    classroom: "Oppure condividilo con una classe",
  },
  classes: {
    kicker: "Classi",
    title: "Segui ogni studente",
    text: "Crea una classe, dai il codice agli studenti e condividi progetti, automaticamente con tutti o solo con alcuni.",
    bullets: ["Gli studenti entrano con un codice", "Risultati di ogni gioco e di ogni quiz", "Condividi con tutta la classe o con pochi"],
    mockTitle: "Risultati",
    mockCols: ["Studente", "Punteggio", "Tempo"],
  },
  activities: { title: "Più di 20 tipi di attività", sub: "Per giochi e quiz, pronte da riempire con i tuoi contenuti.", games: "Giochi", quizzes: "Quiz" },
  videos: { title: "Scopri Make It", sub: "Guarda come si crea un progetto in pochi minuti.", main: "Che cos'è Make It?", tutorials: "Video tutorial", quiz: "Come creare un quiz", game: "Come creare un gioco", share: "Come condividere un progetto", all: "Vedi tutti i tutorial" },
  demos: { title: "Provalo adesso", sub: "Gioca ad alcune delle nostre demo, senza registrarti.", games: "Giochi", quizzes: "Quiz", play: "Gioca", pages: "pagine" },
  plans: {
    title: "Piani per ogni classe",
    sub: "Inizia gratis e passa a Premium quando ti serve di più.",
    perYear: "/anno",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 progetti", "1 classe", "1 cartella", "Quasi tutte le funzioni"],
    educators: "Docenti",
    educatorsFeatures: ["1 docente", "Fino a 100 studenti", "Progetti, classi e cartelle illimitati", "Risultati e statistiche di tutti gli studenti"],
    teams: "Team",
    teamsFeatures: ["5 docenti", "Studenti illimitati", "Gestione licenze", "Tutto ciò che include Docenti"],
    schools: "Scuole",
    schoolsFeatures: ["20 docenti", "Studenti illimitati", "Gestione licenze", "Tutto ciò che include Docenti"],
    startFree: "Inizia gratis",
    tryFree: "Prova gratis",
    subscribe: "Abbonati",
    note: "Gli studenti usano sempre Make It gratis.",
  },
  faq: {
    title: "Domande frequenti",
    sub: "Hai dubbi? Ecco le risposte più comuni.",
    items: [
      { q: "Cosa sono i quiz?", a: "I quiz sono un modo divertente per valutare, imparare o ripassare qualsiasi argomento. Registrano automaticamente le risposte e il punteggio di ogni giocatore, e puoi vedere le risposte, la classifica e le statistiche." },
      { q: "Cosa sono i giochi?", a: "I giochi sono pensati per i bambini più piccoli, anche quelli che non sanno ancora leggere. Privilegiano il rinforzo positivo: gli errori non vengono penalizzati e i tentativi sono illimitati. Per giocare non serve scrivere un nome." },
      { q: "Come posso vedere i risultati dei giochi?", a: "Poiché i bambini non si identificano quando giocano, i risultati e le statistiche dei giochi si possono vedere solo quando il gioco è condiviso tramite una classe, non con codice o link." },
      { q: "Quali limiti ha la versione gratuita?", a: "Puoi usare quasi tutte le funzioni. La versione gratuita permette di creare 2 progetti, 1 classe e 1 cartella." },
      { q: "Cosa ottengo abbonandomi?", a: "Nessun limite: tutti i progetti, le classi e le cartelle che vuoi, e i risultati e le statistiche di tutti i tuoi studenti." },
      { q: "Serve creare un account?", a: "Per giocare, no. Con un account puoi creare e salvare i tuoi progetti nel cloud, sincronizzarli tra dispositivi e creare classi per condividere progetti con i tuoi studenti." },
      { q: "Gli studenti devono pagare?", a: "No. Gli studenti usano sempre Make It gratis." },
      { q: "Come condivido un progetto?", a: "Apri il progetto e vai su Condividi: vedrai il codice di sei cifre e il link. Chi li ha può giocarci su qualsiasi dispositivo. Puoi anche condividerlo con una classe, così gli studenti non hanno bisogno di codici." },
      { q: "Cosa sono le classi e come funzionano?", a: "Le classi servono a gestire gruppi di studenti. Crea una classe, dai il codice agli studenti perché si iscrivano e condividi progetti: con tutti o solo con alcuni. Vedrai i risultati di ogni studente." },
      { q: "Posso usare Make It su qualsiasi dispositivo?", a: "Sì. Puoi creare e giocare sul web, e l'app è disponibile per iPad, iPhone, Android e Mac. I tuoi progetti restano sincronizzati." },
    ],
  },
  download: { title: "Make It su tutti i tuoi dispositivi", sub: "Usalo qui, nel browser, oppure scarica l'app.", web: "Apri sul web", available: "Disponibile su" },
  final: { title: "Rendi divertente imparare", sub: "Crea il tuo primo progetto oggi stesso. È gratis." },
  footer: { tagline: "Crea giochi, quiz, attività e storie, e condividili ovunque.", product: "Prodotto", help: "Aiuto", legal: "Legale", contact: "Contatti", terms: "Termini e condizioni", privacy: "Privacy", cookies: "Cookie policy", follow: "Seguici", support: "Con il sostegno di ACCIÓ", demos: "Demo" },
};

const de: LandingText = {
  nav: { features: "Funktionen", activities: "Aktivitäten", videos: "Videos", plans: "Preise", faq: "FAQ", download: "Download", login: "Anmelden", signup: "Konto erstellen", student: "Ich bin Schüler", menu: "Menü", language: "Sprache" },
  hero: {
    eyebrow: "Lernspiele und Quiz",
    title: "Erstelle Lernspiele und Quiz in wenigen Minuten",
    sub: "Gestalte Projekte mit über 20 Arten von Aktivitäten, teile sie mit allen und sieh die Ergebnisse sofort. Im Browser, auf dem Tablet oder auf dem Handy.",
    start: "Kostenlos starten",
    demo: "Demo ausprobieren",
    codeTitle: "Hast du einen Code?",
    codeText: "Gib den Code ein, den du bekommen hast, und spiele",
    free: "Kostenlos starten · Keine Karte nötig",
  },
  stats: { downloads: "Downloads", users: "aktive Nutzer", projects: "erstellte Projekte", countries: "Länder" },
  grid: {
    title: "Alles, was du zum spielerischen Lernen brauchst",
    sub: "Make It vereint Erstellen, Spielen und Begleiten an einem Ort.",
    items: [
      { title: "In Minuten erstellt", text: "Vorlagen, Bilder, Töne, Zeichnungen und YouTube-Videos griffbereit, ohne Programmieren." },
      { title: "Über 20 Aktivitäten", text: "Verbinden, ziehen, Paare finden, ordnen, schreiben, malen, Symbole nachfahren und vieles mehr." },
      { title: "Mit einem Code teilen", text: "Schicke einen Code oder Link und spiele auf jedem Gerät, ohne etwas zu installieren." },
      { title: "Sofortige Ergebnisse", text: "Antworten, Ranglisten und Statistiken zu jedem Quiz und der Fortschritt jedes Schülers." },
      { title: "Klassen", text: "Schüler treten mit einem Code bei. Teile mit der ganzen Klasse oder nur mit einigen." },
      { title: "Immer synchron", text: "Deine Projekte werden in der Cloud gespeichert, im Web und in der App." },
    ],
  },
  create: {
    kicker: "Erstellen",
    title: "Gestalte dein Projekt, wie du willst",
    text: "Wähle eine Aktivität, füge Bilder, Texte und Töne hinzu – fertig. Alles wird automatisch gespeichert.",
    bullets: ["Bibliotheken mit Tausenden Bildern und Tönen", "Nimm deine eigene Stimme auf", "Zeichne direkt auf der Seite", "Füge YouTube-Videos hinzu", "Vorlagen, Schriften und Hintergrundmusik"],
  },
  play: {
    kicker: "Spiele",
    title: "Gemacht für die Kleinsten",
    text: "Spiele belohnen die Mühe: Fehler werden nicht bestraft, man kann es beliebig oft versuchen und muss nicht einmal lesen können.",
    bullets: ["Positive Verstärkung auf jeder Seite", "Anweisungen mit Text und Ton", "Kein Name und kein Konto zum Spielen"],
  },
  quiz: {
    kicker: "Quiz",
    title: "Quiz mit Punkten und Rangliste",
    text: "Fragen mit Zeitlimit, automatische Punkte und eine Live-Rangliste. Du siehst die Antworten aller und die Statistik jeder Frage.",
    bullets: ["Frage, wahr oder falsch, Antwort schreiben, Mathe…", "Zeit pro Frage", "Rangliste und Statistiken"],
  },
  share: {
    kicker: "Teilen",
    title: "Ein Code und los geht's",
    text: "Jedes Projekt hat einen sechsstelligen Code und einen Link. Schüler öffnen es im Browser oder in der App, ohne Konto.",
    code: "Code",
    link: "Link",
    classroom: "Oder teile es mit einer Klasse",
  },
  classes: {
    kicker: "Klassen",
    title: "Begleite jeden Schüler",
    text: "Erstelle eine Klasse, gib deinen Schülern den Code und teile Projekte – automatisch mit allen oder nur mit einigen.",
    bullets: ["Schüler treten mit einem Code bei", "Ergebnisse jedes Spiels und jedes Quiz", "Mit der ganzen Klasse oder einigen teilen"],
    mockTitle: "Ergebnisse",
    mockCols: ["Schüler", "Punkte", "Zeit"],
  },
  activities: { title: "Über 20 Arten von Aktivitäten", sub: "Für Spiele und Quiz, bereit für deine eigenen Inhalte.", games: "Spiele", quizzes: "Quiz" },
  videos: { title: "Entdecke Make It", sub: "Sieh, wie ein Projekt in wenigen Minuten entsteht.", main: "Was ist Make It?", tutorials: "Video-Tutorials", quiz: "Ein Quiz erstellen", game: "Ein Spiel erstellen", share: "Ein Projekt teilen", all: "Alle Tutorials ansehen" },
  demos: { title: "Jetzt ausprobieren", sub: "Spiele einige unserer Demos, ohne Anmeldung.", games: "Spiele", quizzes: "Quiz", play: "Spielen", pages: "Seiten" },
  plans: {
    title: "Pläne für jede Klasse",
    sub: "Starte kostenlos und wechsle zu Premium, wenn du mehr brauchst.",
    perYear: "/Jahr",
    free: "Kostenlos",
    freePrice: "0 €",
    freeFeatures: ["2 Projekte", "1 Klasse", "1 Ordner", "Fast alle Funktionen"],
    educators: "Lehrkräfte",
    educatorsFeatures: ["1 Lehrkraft", "Bis zu 100 Schüler", "Unbegrenzte Projekte, Klassen und Ordner", "Ergebnisse und Statistiken aller Schüler"],
    teams: "Teams",
    teamsFeatures: ["5 Lehrkräfte", "Unbegrenzte Schüler", "Lizenzverwaltung", "Alles aus Lehrkräfte"],
    schools: "Schulen",
    schoolsFeatures: ["20 Lehrkräfte", "Unbegrenzte Schüler", "Lizenzverwaltung", "Alles aus Lehrkräfte"],
    startFree: "Kostenlos starten",
    tryFree: "Kostenlos testen",
    subscribe: "Abonnieren",
    note: "Schüler nutzen Make It immer kostenlos.",
  },
  faq: {
    title: "Häufige Fragen",
    sub: "Fragen? Hier sind die häufigsten Antworten.",
    items: [
      { q: "Was sind Quiz?", a: "Quiz sind eine unterhaltsame Art, jedes Thema zu bewerten, zu lernen oder zu wiederholen. Sie speichern automatisch die Antworten und Punkte jedes Spielers, und du siehst die Antworten, die Rangliste und die Statistiken." },
      { q: "Was sind Spiele?", a: "Spiele sind für kleine Kinder gedacht, auch für die, die noch nicht lesen können. Sie setzen auf positive Verstärkung: Fehler werden nicht bestraft und es gibt unbegrenzt viele Versuche. Zum Spielen muss kein Name eingegeben werden." },
      { q: "Wie sehe ich die Ergebnisse der Spiele?", a: "Da sich Kinder beim Spielen nicht identifizieren, sind die Ergebnisse und Statistiken von Spielen nur sichtbar, wenn das Spiel über eine Klasse geteilt wird – nicht per Code oder Link." },
      { q: "Welche Grenzen hat die kostenlose Version?", a: "Du kannst fast alle Funktionen nutzen. Mit der kostenlosen Version kannst du 2 Projekte, 1 Klasse und 1 Ordner erstellen." },
      { q: "Was bringt mir ein Abo?", a: "Keine Grenzen: so viele Projekte, Klassen und Ordner, wie du willst, und die Ergebnisse und Statistiken aller deiner Schüler." },
      { q: "Brauche ich ein Konto?", a: "Zum Spielen nicht. Mit einem Konto kannst du Projekte erstellen und in der Cloud speichern, sie zwischen Geräten synchronisieren und Klassen anlegen, um Projekte mit deinen Schülern zu teilen." },
      { q: "Müssen Schüler bezahlen?", a: "Nein. Schüler nutzen Make It immer kostenlos." },
      { q: "Wie teile ich ein Projekt?", a: "Öffne das Projekt und gehe zu Teilen: Dort siehst du den sechsstelligen Code und den Link. Wer sie hat, kann es auf jedem Gerät spielen. Du kannst es auch mit einer Klasse teilen, dann brauchen die Schüler keinen Code." },
      { q: "Was sind Klassen und wie funktionieren sie?", a: "Mit Klassen verwaltest du Gruppen von Schülern. Erstelle eine Klasse, gib den Schülern den Code zum Beitreten und teile Projekte – mit allen oder nur mit einigen. Du siehst die Ergebnisse jedes Schülers." },
      { q: "Kann ich Make It auf jedem Gerät nutzen?", a: "Ja. Du kannst im Web erstellen und spielen, und die App gibt es für iPad, iPhone, Android und Mac. Deine Projekte bleiben synchron." },
    ],
  },
  download: { title: "Make It auf all deinen Geräten", sub: "Nutze es direkt hier im Browser oder lade die App herunter.", web: "Im Web öffnen", available: "Erhältlich im" },
  final: { title: "Mach Lernen zum Spaß", sub: "Erstelle noch heute dein erstes Projekt. Es ist kostenlos." },
  footer: { tagline: "Erstelle Spiele, Quiz, Aktivitäten und Geschichten und teile sie überall.", product: "Produkt", help: "Hilfe", legal: "Rechtliches", contact: "Kontakt", terms: "AGB", privacy: "Datenschutz", cookies: "Cookie-Richtlinie", follow: "Folge uns", support: "Mit Unterstützung von ACCIÓ", demos: "Demos" },
};

const ar: LandingText = {
  nav: { features: "الميزات", activities: "الأنشطة", videos: "الفيديوهات", plans: "الخطط", faq: "الأسئلة", download: "تنزيل", login: "تسجيل الدخول", signup: "إنشاء حساب", student: "أنا طالب", menu: "القائمة", language: "اللغة" },
  hero: {
    eyebrow: "ألعاب واختبارات تعليمية",
    title: "أنشئ ألعابًا واختبارات تعليمية في دقائق",
    sub: "صمّم مشاريع بأكثر من 20 نوعًا من الأنشطة، وشاركها مع من تشاء، واطّلع على النتائج فورًا. في المتصفح أو على الجهاز اللوحي أو على الهاتف.",
    start: "ابدأ مجانًا",
    demo: "جرّب عرضًا تجريبيًا",
    codeTitle: "هل لديك رمز؟",
    codeText: "اكتب الرمز الذي أُعطي لك والعب",
    free: "ابدأ مجانًا · دون بطاقة",
  },
  stats: { downloads: "تنزيل", users: "مستخدم نشط", projects: "مشروع", countries: "دولة" },
  grid: {
    title: "كل ما تحتاجه للتعلّم باللعب",
    sub: "يجمع Make It الإنشاء واللعب والمتابعة في مكان واحد.",
    items: [
      { title: "أنشئ في دقائق", text: "قوالب وصور وأصوات ورسومات وفيديوهات يوتيوب في متناول يدك، دون برمجة." },
      { title: "أكثر من 20 نشاطًا", text: "التوصيل والسحب وإيجاد الأزواج والترتيب والكتابة والتلوين وتتبّع الرموز وغيرها." },
      { title: "شارك برمز", text: "أرسل رمزًا أو رابطًا ويمكن اللعب على أي جهاز دون تثبيت أي شيء." },
      { title: "نتائج فورية", text: "الإجابات والترتيب والإحصاءات لكل اختبار، وتقدّم كل طالب." },
      { title: "الفصول", text: "ينضم الطلاب برمز. شارك المشاريع مع الفصل كله أو مع بعض الطلاب فقط." },
      { title: "متزامن دائمًا", text: "تُحفظ مشاريعك في السحابة وتجدها على الويب وفي التطبيق." },
    ],
  },
  create: {
    kicker: "إنشاء",
    title: "صمّم مشروعك كما تريد",
    text: "اختر نشاطًا وأضف الصور والنصوص والأصوات، ويصبح مشروعك جاهزًا. يُحفظ كل ما تفعله تلقائيًا.",
    bullets: ["مكتبات بآلاف الصور والأصوات", "سجّل صوتك", "ارسم مباشرة على الصفحة", "أضف فيديوهات يوتيوب", "قوالب وخطوط وموسيقى خلفية"],
  },
  play: {
    kicker: "الألعاب",
    title: "مصمّم للصغار",
    text: "تكافئ الألعاب الجهد: لا تُعاقب الأخطاء، ويمكن المحاولة مرات غير محدودة، ولا حاجة حتى إلى معرفة القراءة.",
    bullets: ["تعزيز إيجابي في كل صفحة", "تعليمات بالنص والصوت", "لا حاجة إلى اسم أو حساب للعب"],
  },
  quiz: {
    kicker: "الاختبارات",
    title: "اختبارات بالنقاط والترتيب",
    text: "أسئلة بوقت محدد، وتسجيل تلقائي للنقاط، وترتيب مباشر. ترى إجابات كل لاعب وإحصاءات كل سؤال.",
    bullets: ["سؤال، صح أو خطأ، كتابة الإجابة، رياضيات…", "وقت لكل سؤال", "الترتيب والإحصاءات"],
  },
  share: {
    kicker: "المشاركة",
    title: "رمز واحد وابدأ اللعب",
    text: "لكل مشروع رمز من ستة أرقام ورابط. يفتحه الطلاب في المتصفح أو في التطبيق دون إنشاء حساب.",
    code: "الرمز",
    link: "الرابط",
    classroom: "أو شاركه مع فصل",
  },
  classes: {
    kicker: "الفصول",
    title: "تابع كل طالب",
    text: "أنشئ فصلًا، وأعطِ رمزه للطلاب، وشارك المشاريع معهم تلقائيًا مع الجميع أو مع بعضهم فقط.",
    bullets: ["ينضم الطلاب برمز", "نتائج كل لعبة وكل اختبار", "شارك مع الفصل كله أو مع بعضه"],
    mockTitle: "النتائج",
    mockCols: ["الطالب", "النقاط", "الوقت"],
  },
  activities: { title: "أكثر من 20 نوعًا من الأنشطة", sub: "للألعاب والاختبارات، جاهزة لتملأها بمحتواك.", games: "الألعاب", quizzes: "الاختبارات" },
  videos: { title: "اكتشف Make It", sub: "شاهد كيف يُنشأ مشروع في دقائق.", main: "ما هو Make It؟", tutorials: "دروس فيديو", quiz: "كيف تنشئ اختبارًا", game: "كيف تنشئ لعبة", share: "كيف تشارك مشروعًا", all: "شاهد كل الدروس" },
  demos: { title: "جرّبه الآن", sub: "العب بعض عروضنا التجريبية دون تسجيل.", games: "الألعاب", quizzes: "الاختبارات", play: "العب", pages: "صفحات" },
  plans: {
    title: "خطط لكل فصل",
    sub: "ابدأ مجانًا وانتقل إلى Premium عندما تحتاج إلى المزيد.",
    perYear: "/سنة",
    free: "مجاني",
    freePrice: "0 €",
    freeFeatures: ["مشروعان", "فصل واحد", "مجلد واحد", "معظم الميزات تقريبًا"],
    educators: "المعلمون",
    educatorsFeatures: ["معلم واحد", "حتى 100 طالب", "مشاريع وفصول ومجلدات غير محدودة", "نتائج وإحصاءات جميع الطلاب"],
    teams: "الفرق",
    teamsFeatures: ["5 معلمين", "طلاب غير محدودين", "إدارة التراخيص", "كل ما في خطة المعلمين"],
    schools: "المدارس",
    schoolsFeatures: ["20 معلمًا", "طلاب غير محدودين", "إدارة التراخيص", "كل ما في خطة المعلمين"],
    startFree: "ابدأ مجانًا",
    tryFree: "جرّبه مجانًا",
    subscribe: "اشترك",
    note: "يستخدم الطلاب Make It مجانًا دائمًا.",
  },
  faq: {
    title: "الأسئلة الشائعة",
    sub: "لديك أسئلة؟ إليك الإجابات الأكثر شيوعًا.",
    items: [
      { q: "ما هي الاختبارات؟", a: "الاختبارات طريقة ممتعة لتقييم أي موضوع أو تعلّمه أو مراجعته. تسجّل تلقائيًا إجابات كل لاعب ونقاطه، ويمكنك رؤية الإجابات والترتيب والإحصاءات." },
      { q: "ما هي الألعاب؟", a: "الألعاب مصمّمة للأطفال الصغار، حتى الذين لا يعرفون القراءة بعد. تركّز على التعزيز الإيجابي: لا تُعاقب الأخطاء والمحاولات غير محدودة. لا يكتب الأطفال أي اسم للعب." },
      { q: "كيف أرى نتائج الألعاب؟", a: "بما أن الأطفال لا يعرّفون بأنفسهم عند اللعب، لا يمكن رؤية نتائج الألعاب وإحصاءاتها إلا عند مشاركة اللعبة عبر فصل، وليس بالرمز أو الرابط." },
      { q: "ما حدود النسخة المجانية؟", a: "يمكنك استخدام معظم الميزات تقريبًا. تتيح النسخة المجانية إنشاء مشروعين وفصل واحد ومجلد واحد." },
      { q: "ماذا أحصل عليه عند الاشتراك؟", a: "لا حدود: مشاريع وفصول ومجلدات بقدر ما تريد، ونتائج جميع طلابك وإحصاءاتهم." },
      { q: "هل أحتاج إلى حساب؟", a: "للعب، لا. مع الحساب يمكنك إنشاء مشاريعك وحفظها في السحابة ومزامنتها بين الأجهزة وإنشاء فصول لمشاركة المشاريع مع طلابك." },
      { q: "هل يجب أن يدفع الطلاب؟", a: "لا. يستخدم الطلاب Make It مجانًا دائمًا." },
      { q: "كيف أشارك مشروعًا؟", a: "افتح المشروع وانتقل إلى المشاركة: سترى رمزه المكوّن من ستة أرقام ورابطه. يمكن لمن يملكهما اللعب على أي جهاز. يمكنك أيضًا مشاركته مع فصل فلا يحتاج الطلاب إلى أي رمز." },
      { q: "ما هي الفصول وكيف تعمل؟", a: "تساعدك الفصول على إدارة مجموعات الطلاب. أنشئ فصلًا، وأعطِ رمزه للطلاب لينضموا، وشارك المشاريع معهم: مع الجميع أو مع بعضهم فقط. سترى نتائج كل طالب." },
      { q: "هل يمكنني استخدام Make It على أي جهاز؟", a: "نعم. يمكنك الإنشاء واللعب على الويب، والتطبيق متاح لأجهزة iPad وiPhone وAndroid وMac. تبقى مشاريعك متزامنة." },
    ],
  },
  download: { title: "Make It على جميع أجهزتك", sub: "استخدمه هنا في المتصفح أو نزّل التطبيق.", web: "افتحه على الويب", available: "متوفر على" },
  final: { title: "اجعل التعلّم ممتعًا", sub: "أنشئ مشروعك الأول اليوم. إنه مجاني." },
  footer: { tagline: "أنشئ ألعابًا واختبارات وأنشطة وقصصًا وشاركها في كل مكان.", product: "المنتج", help: "المساعدة", legal: "قانوني", contact: "اتصل بنا", terms: "الشروط والأحكام", privacy: "الخصوصية", cookies: "سياسة ملفات تعريف الارتباط", follow: "تابعنا", support: "بدعم من ACCIÓ", demos: "عروض تجريبية" },
};

const he: LandingText = {
  nav: { features: "תכונות", activities: "פעילויות", videos: "סרטונים", plans: "תוכניות", faq: "שאלות", download: "הורדה", login: "כניסה", signup: "יצירת חשבון", student: "אני תלמיד", menu: "תפריט", language: "שפה" },
  hero: {
    eyebrow: "משחקים ומבחנים חינוכיים",
    title: "יוצרים משחקים ומבחנים חינוכיים בדקות",
    sub: "עצבו פרויקטים עם יותר מ־20 סוגי פעילויות, שתפו אותם עם מי שתרצו וצפו בתוצאות מיד. בדפדפן, בטאבלט או בטלפון.",
    start: "מתחילים בחינם",
    demo: "נסו הדגמה",
    codeTitle: "יש לכם קוד?",
    codeText: "הקלידו את הקוד שקיבלתם ושחקו",
    free: "מתחילים בחינם · בלי כרטיס אשראי",
  },
  stats: { downloads: "הורדות", users: "משתמשים פעילים", projects: "פרויקטים", countries: "מדינות" },
  grid: {
    title: "כל מה שצריך כדי ללמוד בדרך של משחק",
    sub: "Make It מאחד במקום אחד יצירה, משחק ומעקב.",
    items: [
      { title: "יוצרים בדקות", text: "תבניות, תמונות, צלילים, ציורים וסרטוני YouTube בהישג יד, בלי לתכנת." },
      { title: "יותר מ־20 פעילויות", text: "חיבור, גרירה, מציאת זוגות, סידור, כתיבה, צביעה, מעקב אחר סמלים ועוד." },
      { title: "משתפים עם קוד", text: "שלחו קוד או קישור ומשחקים בכל מכשיר, בלי להתקין כלום." },
      { title: "תוצאות מיידיות", text: "תשובות, דירוגים וסטטיסטיקות לכל מבחן, וההתקדמות של כל תלמיד." },
      { title: "כיתות", text: "התלמידים מצטרפים עם קוד. שתפו עם כל הכיתה או רק עם חלק מהתלמידים." },
      { title: "תמיד מסונכרן", text: "הפרויקטים נשמרים בענן וזמינים באתר ובאפליקציה." },
    ],
  },
  create: {
    kicker: "יצירה",
    title: "עצבו את הפרויקט כרצונכם",
    text: "בחרו פעילות, הוסיפו תמונות, טקסטים וצלילים, והפרויקט מוכן. כל מה שאתם עושים נשמר אוטומטית.",
    bullets: ["ספריות עם אלפי תמונות וצלילים", "הקליטו את הקול שלכם", "ציירו ישירות על הדף", "הוסיפו סרטוני YouTube", "תבניות, גופנים ומוזיקת רקע"],
  },
  play: {
    kicker: "משחקים",
    title: "מותאם לקטנטנים",
    text: "המשחקים מתגמלים מאמץ: טעויות לא נענשות, אפשר לנסות שוב כמה פעמים שצריך ואפילו לא צריך לדעת לקרוא.",
    bullets: ["חיזוק חיובי בכל דף", "הוראות בטקסט ובקול", "אין צורך בשם או בחשבון כדי לשחק"],
  },
  quiz: {
    kicker: "מבחנים",
    title: "מבחנים עם ניקוד ודירוג",
    text: "שאלות עם זמן, ניקוד אוטומטי ודירוג בזמן אמת. אתם רואים את התשובות של כל אחד ואת הסטטיסטיקה של כל שאלה.",
    bullets: ["שאלה, נכון או לא נכון, כתיבת תשובה, חשבון…", "זמן לכל שאלה", "דירוג וסטטיסטיקות"],
  },
  share: {
    kicker: "שיתוף",
    title: "קוד אחד ומשחקים",
    text: "לכל פרויקט יש קוד בן שש ספרות וקישור. התלמידים פותחים אותו בדפדפן או באפליקציה, בלי ליצור חשבון.",
    code: "קוד",
    link: "קישור",
    classroom: "או שתפו אותו עם כיתה",
  },
  classes: {
    kicker: "כיתות",
    title: "עוקבים אחרי כל תלמיד",
    text: "צרו כיתה, תנו לתלמידים את הקוד ושתפו איתם פרויקטים, אוטומטית עם כולם או רק עם חלקם.",
    bullets: ["התלמידים מצטרפים עם קוד", "תוצאות של כל משחק ושל כל מבחן", "שיתוף עם כל הכיתה או עם חלק ממנה"],
    mockTitle: "תוצאות",
    mockCols: ["תלמיד", "ניקוד", "זמן"],
  },
  activities: { title: "יותר מ־20 סוגי פעילויות", sub: "למשחקים ולמבחנים, מוכנות לתוכן שלכם.", games: "משחקים", quizzes: "מבחנים" },
  videos: { title: "הכירו את Make It", sub: "ראו איך יוצרים פרויקט בכמה דקות.", main: "מה זה Make It?", tutorials: "מדריכי וידאו", quiz: "איך יוצרים מבחן", game: "איך יוצרים משחק", share: "איך משתפים פרויקט", all: "לכל המדריכים" },
  demos: { title: "נסו עכשיו", sub: "שחקו בכמה מההדגמות שלנו, בלי הרשמה.", games: "משחקים", quizzes: "מבחנים", play: "שחקו", pages: "דפים" },
  plans: {
    title: "תוכניות לכל כיתה",
    sub: "מתחילים בחינם ועוברים ל־Premium כשצריך יותר.",
    perYear: "/שנה",
    free: "חינם",
    freePrice: "0 €",
    freeFeatures: ["2 פרויקטים", "כיתה אחת", "תיקייה אחת", "כמעט כל התכונות"],
    educators: "מורים",
    educatorsFeatures: ["מורה אחד", "עד 100 תלמידים", "פרויקטים, כיתות ותיקיות ללא הגבלה", "תוצאות וסטטיסטיקות של כל התלמידים"],
    teams: "צוותים",
    teamsFeatures: ["5 מורים", "תלמידים ללא הגבלה", "ניהול רישיונות", "כל מה שבתוכנית מורים"],
    schools: "בתי ספר",
    schoolsFeatures: ["20 מורים", "תלמידים ללא הגבלה", "ניהול רישיונות", "כל מה שבתוכנית מורים"],
    startFree: "מתחילים בחינם",
    tryFree: "נסו בחינם",
    subscribe: "להרשמה",
    note: "תלמידים משתמשים ב־Make It תמיד בחינם.",
  },
  faq: {
    title: "שאלות נפוצות",
    sub: "יש לכם שאלות? הנה התשובות הנפוצות ביותר.",
    items: [
      { q: "מה הם מבחנים?", a: "מבחנים הם דרך מהנה להעריך, ללמוד או לחזור על כל נושא. הם רושמים אוטומטית את התשובות והניקוד של כל שחקן, ואתם יכולים לראות את התשובות, הדירוג והסטטיסטיקות." },
      { q: "מה הם משחקים?", a: "המשחקים מיועדים לילדים צעירים, גם לאלה שעוד לא יודעים לקרוא. הם מתמקדים בחיזוק חיובי: טעויות לא נענשות ומספר הניסיונות אינו מוגבל. הילדים לא כותבים שם כדי לשחק." },
      { q: "איך רואים את תוצאות המשחקים?", a: "מכיוון שהילדים לא מזדהים כשהם משחקים, אפשר לראות את התוצאות והסטטיסטיקות של משחקים רק כשהמשחק משותף דרך כיתה, ולא באמצעות קוד או קישור." },
      { q: "מהן המגבלות של הגרסה החינמית?", a: "אפשר להשתמש כמעט בכל התכונות. הגרסה החינמית מאפשרת ליצור 2 פרויקטים, כיתה אחת ותיקייה אחת." },
      { q: "מה מקבלים במינוי?", a: "בלי מגבלות: כמה פרויקטים, כיתות ותיקיות שתרצו, ואת התוצאות והסטטיסטיקות של כל התלמידים." },
      { q: "צריך ליצור חשבון?", a: "כדי לשחק, לא. עם חשבון אפשר ליצור ולשמור פרויקטים בענן, לסנכרן אותם בין מכשירים וליצור כיתות כדי לשתף פרויקטים עם התלמידים." },
      { q: "התלמידים צריכים לשלם?", a: "לא. תלמידים משתמשים ב־Make It תמיד בחינם." },
      { q: "איך משתפים פרויקט?", a: "פתחו את הפרויקט ועברו לשיתוף: תראו את הקוד בן שש הספרות ואת הקישור. מי שיש לו אותם יכול לשחק בכל מכשיר. אפשר גם לשתף עם כיתה, וכך התלמידים לא צריכים קוד." },
      { q: "מה הן כיתות ואיך הן עובדות?", a: "כיתות עוזרות לנהל קבוצות של תלמידים. צרו כיתה, תנו לתלמידים את הקוד כדי שיצטרפו ושתפו איתם פרויקטים: עם כולם או רק עם חלקם. תראו את התוצאות של כל תלמיד." },
      { q: "אפשר להשתמש ב־Make It בכל מכשיר?", a: "כן. אפשר ליצור ולשחק באתר, והאפליקציה זמינה ל־iPad, iPhone, Android ו־Mac. הפרויקטים נשארים מסונכרנים." },
    ],
  },
  download: { title: "Make It בכל המכשירים שלכם", sub: "השתמשו בו כאן בדפדפן או הורידו את האפליקציה.", web: "פתיחה באתר", available: "זמין ב־" },
  final: { title: "הופכים את הלמידה למהנה", sub: "צרו את הפרויקט הראשון שלכם היום. זה בחינם." },
  footer: { tagline: "צרו משחקים, מבחנים, פעילויות וסיפורים ושתפו אותם בכל מקום.", product: "מוצר", help: "עזרה", legal: "משפטי", contact: "יצירת קשר", terms: "תנאים והגבלות", privacy: "פרטיות", cookies: "מדיניות קובצי Cookie", follow: "עקבו אחרינו", support: "בתמיכת ACCIÓ", demos: "הדגמות" },
};

const eu: LandingText = {
  nav: {
    features: "Ezaugarriak",
    activities: "Jarduerak",
    videos: "Bideoak",
    plans: "Planak",
    faq: "Ohiko galderak",
    download: "Deskargatu",
    login: "Hasi saioa",
    signup: "Erregistratu doan",
    student: "Ikaslea naiz",
    menu: "Menua",
    language: "Hizkuntza",
  },
  hero: {
    eyebrow: "Joko eta galdetegi hezigarriak",
    title: "Sortu ikasteko jokoak eta galdetegiak minutu gutxitan",
    sub: "Diseinatu proiektuak 20 jarduera mota baino gehiagorekin, partekatu edonorekin eta ikusi emaitzak berehala. Nabigatzailean, tabletan edo mugikorrean.",
    start: "Hasi doan",
    demo: "Probatu demo bat",
    codeTitle: "Kode bat duzu?",
    codeText: "Idatzi eman dizuten kodea eta jolastu",
    free: "Doan hasteko · Kreditu-txartelik gabe",
  },
  stats: { downloads: "deskarga", users: "erabiltzaile aktibo", projects: "proiektu sortuta", countries: "herrialde" },
  grid: {
    title: "Jolasean ikasteko behar duzun guztia",
    sub: "Make It-ek leku bakarrean biltzen ditu sortzea, jolastea eta jarraipena egitea.",
    items: [
      { title: "Sortu minutu gutxitan", text: "Txantiloiak, irudiak, soinuak, marrazkiak eta YouTubeko bideoak eskura. Ez da programatu beharrik." },
      { title: "20 jarduera baino gehiago", text: "Lotu, arrastatu, bilatu bikotea, ordenatu, idatzi, margotu, trazatu ikurrak eta askoz gehiago." },
      { title: "Partekatu kode batekin", text: "Bidali kode bat edo esteka bat, eta jolastu edozein gailutan, ezer instalatu gabe." },
      { title: "Berehalako emaitzak", text: "Galdetegi bakoitzaren erantzunak, sailkapenak eta estatistikak, eta ikasle bakoitzaren aurrerapena." },
      { title: "Ikasgelak", text: "Ikasleak kode batekin sartzen dira. Partekatu proiektuak gela osoarekin edo ikasle batzuekin bakarrik." },
      { title: "Beti sinkronizatuta", text: "Zure proiektuak hodeian gordetzen dira, eta prest daude webean zein aplikazioan." },
    ],
  },
  create: {
    kicker: "Sortu",
    title: "Diseinatu proiektua zure erara",
    text: "Aukeratu jarduera bat, gehitu zure irudiak, testuak eta soinuak, eta prest duzu proiektua. Egiten duzun guztia automatikoki gordetzen da.",
    bullets: [
      "Milaka irudi eta soinu dituzten liburutegiak",
      "Grabatu zure ahotsa",
      "Marraztu zuzenean orrian",
      "Gehitu YouTubeko bideoak",
      "Txantiloiak, letra-tipoak eta atzeko musika",
    ],
  },
  play: {
    kicker: "Jokoak",
    title: "Txikienentzat pentsatuta",
    text: "Jokoek ahalegina saritzen dute: akatsak ez dira zigortzen, haurrek behar adina aldiz saia daitezke eta ez dute irakurtzen jakin beharrik ere.",
    bullets: ["Indartze positiboa orri guztietan", "Jarraibideak testuarekin eta soinuarekin", "Jolasteko ez da izenik ez konturik behar"],
  },
  quiz: {
    kicker: "Galdetegiak",
    title: "Puntuazioa eta sailkapena duten galdetegiak",
    text: "Denbora mugatuko galderak, puntuazio automatikoa eta zuzeneko sailkapena. Jokalari bakoitzaren erantzunak eta galdera bakoitzaren estatistikak ikusiko dituzu.",
    bullets: ["Galdera, egia ala gezurra, idatzi erantzuna, matematika…", "Denbora-muga galdera bakoitzeko", "Sailkapena eta estatistikak"],
  },
  share: {
    kicker: "Partekatu",
    title: "Kode bat, eta jolastera",
    text: "Proiektu bakoitzak sei digituko kode bat eta esteka bat ditu. Ikasleek nabigatzailean edo aplikazioan irekitzen dute, konturik sortu gabe.",
    code: "Kodea",
    link: "Esteka",
    classroom: "Edo partekatu ikasgela batekin",
  },
  classes: {
    kicker: "Ikasgelak",
    title: "Egin ikasle bakoitzaren jarraipena",
    text: "Sortu ikasgela bat, eman haren kodea zure ikasleei eta partekatu proiektuak haiekin, automatikoki guztiekin edo batzuekin bakarrik.",
    bullets: ["Ikasleak kode batekin sartzen dira", "Joko eta galdetegi guztien emaitzak", "Partekatu gela osoarekin edo ikasle batzuekin"],
    mockTitle: "Emaitzak",
    mockCols: ["Ikaslea", "Puntuazioa", "Denbora"],
  },
  activities: {
    title: "20 jarduera mota baino gehiago",
    sub: "Jokoetarako eta galdetegietarako, zure edukiarekin betetzeko prest.",
    games: "Jokoak",
    quizzes: "Galdetegiak",
  },
  videos: {
    title: "Ezagutu Make It",
    sub: "Ikusi nola egiten den proiektu bat minutu gutxitan.",
    main: "Zer da Make It?",
    tutorials: "Bideo-tutorialak",
    quiz: "Nola sortu galdetegi bat",
    game: "Nola sortu joko bat",
    share: "Nola partekatu proiektu bat",
    all: "Ikusi tutorial guztiak",
  },
  demos: {
    title: "Probatu orain",
    sub: "Jolastu gure demoetako batzuekin, erregistratu beharrik gabe.",
    games: "Jokoak",
    quizzes: "Galdetegiak",
    play: "Jolastu",
    pages: "orri",
  },
  plans: {
    title: "Ikasgela guztientzako planak",
    sub: "Hasi doan, eta hartu Premium gehiago behar duzunean.",
    perYear: "/urtean",
    free: "Doakoa",
    freePrice: "0 €",
    freeFeatures: ["2 proiektu", "Ikasgela bat", "Karpeta bat", "Ia ezaugarri guztiak"],
    educators: "Hezitzaileak",
    educatorsFeatures: ["Hezitzaile bat", "Gehienez 100 ikasle", "Proiektuak, ikasgelak eta karpetak mugarik gabe", "Ikasle guztien emaitzak eta estatistikak"],
    teams: "Taldeak",
    teamsFeatures: ["5 hezitzaile", "Ikasleak mugarik gabe", "Lizentzia-kudeatzailea", "«Hezitzaileak» planeko guztia"],
    schools: "Ikastetxeak",
    schoolsFeatures: ["20 hezitzaile", "Ikasleak mugarik gabe", "Lizentzia-kudeatzailea", "«Hezitzaileak» planeko guztia"],
    startFree: "Hasi doan",
    tryFree: "Probatu doan",
    subscribe: "Harpidetu",
    note: "Ikasleek beti doan erabiltzen dute Make It.",
  },
  faq: {
    title: "Ohiko galderak",
    sub: "Zalantzarik? Hemen dituzu erantzun ohikoenak.",
    items: [
      {
        q: "Zer dira galdetegiak?",
        a: "Galdetegiak edozein gai ebaluatzeko, ikasteko edo errepasatzeko modu dibertigarria dira. Jokalari bakoitzaren erantzunak eta puntuazioa automatikoki erregistratzen dituzte, eta erantzunak, sailkapena eta estatistikak ikus ditzakezu.",
      },
      {
        q: "Zer dira jokoak?",
        a: "Jokoak haur txikientzat pentsatuta daude, baita oraindik irakurtzen ez dakitenentzat ere. Indartze positiboan oinarritzen dira: akatsak ez dira zigortzen eta saiakerak mugagabeak dira. Haurrek ez dute izenik idazten jolasteko.",
      },
      {
        q: "Nola ikus ditzaket jokoen emaitzak?",
        a: "Haurrek jolastean ez dutenez beren burua identifikatzen, jokoen emaitzak eta estatistikak jokoa ikasgela baten bidez partekatzen denean bakarrik ikus daitezke, ez kode edo esteka bidez partekatzen denean.",
      },
      {
        q: "Zer muga ditu doako bertsioak?",
        a: "Ia ezaugarri guztiak erabil ditzakezu. Doako bertsioarekin 2 proiektu, ikasgela bat eta karpeta bat sor ditzakezu.",
      },
      {
        q: "Zer lortzen dut harpidetuta?",
        a: "Mugarik ez: nahi beste proiektu, ikasgela eta karpeta, eta zure ikasle guztien emaitzak eta estatistikak.",
      },
      {
        q: "Kontu bat behar dut?",
        a: "Jolasteko, ez. Kontu batekin proiektuak sortu eta hodeian gorde ditzakezu, gailuen artean sinkronizatu, eta ikasgelak sortu proiektuak zure ikasleekin partekatzeko.",
      },
      { q: "Ikasleek ordaindu behar dute?", a: "Ez. Ikasleek beti doan erabiltzen dute Make It." },
      {
        q: "Nola partekatzen dut proiektu bat?",
        a: "Ireki proiektua eta joan «Partekatu» atalera: sei digituko kodea eta esteka ikusiko dituzu. Horiek dituenak edozein gailutan jolastu dezake. Ikasgela batekin ere parteka dezakezu, ikasleek koderik behar ez dezaten.",
      },
      {
        q: "Zer dira ikasgelak eta nola funtzionatzen dute?",
        a: "Ikasgelekin ikasle taldeak kudea ditzakezu. Sortu ikasgela bat, eman haren kodea ikasleei sar daitezen, eta partekatu proiektuak haiekin: guztiekin edo batzuekin bakarrik. Ikasle bakoitzaren emaitzak ikusiko dituzu.",
      },
      {
        q: "Edozein gailutan erabil dezaket Make It?",
        a: "Bai. Webean sortu eta jolastu dezakezu, eta aplikazioa iPad, iPhone, Android eta Mac gailuetarako dago eskuragarri. Zure proiektuak sinkronizatuta egoten dira.",
      },
    ],
  },
  download: {
    title: "Make It zure gailu guztietan",
    sub: "Erabili hemen bertan nabigatzailean, edo deskargatu aplikazioa.",
    web: "Ireki webean",
    available: "Eskuratu hemen:",
  },
  final: { title: "Egin ikastea dibertigarria", sub: "Sortu zure lehen proiektua gaur. Doakoa da." },
  footer: {
    tagline: "Sortu jokoak, galdetegiak, jarduerak eta istorioak, eta partekatu edonon.",
    product: "Produktua",
    help: "Laguntza",
    legal: "Lege-oharrak",
    contact: "Kontaktua",
    terms: "Termino eta baldintzak",
    privacy: "Pribatutasuna",
    cookies: "Cookie-politika",
    follow: "Jarraitu gaitzazu",
    support: "ACCIÓren laguntzarekin",
    demos: "Demoak",
  },
};

const gl: LandingText = {
  nav: {
    features: "Funcións",
    activities: "Actividades",
    videos: "Vídeos",
    plans: "Plans",
    faq: "Preguntas frecuentes",
    download: "Descargar",
    login: "Iniciar sesión",
    signup: "Rexístrate gratis",
    student: "Son alumno",
    menu: "Menú",
    language: "Idioma",
  },
  hero: {
    eyebrow: "Xogos e cuestionarios educativos",
    title: "Crea xogos e cuestionarios educativos en minutos",
    sub: "Deseña proxectos con máis de 20 tipos de actividade, compárteos con quen queiras e mira os resultados ao momento. No navegador, na tableta ou no móbil.",
    start: "Comeza gratis",
    demo: "Proba unha demo",
    codeTitle: "Tes un código?",
    codeText: "Escribe o código que che deron e xoga",
    free: "Gratis para comezar · Sen tarxeta de crédito",
  },
  stats: { downloads: "descargas", users: "usuarios activos", projects: "proxectos creados", countries: "países" },
  grid: {
    title: "Todo o que precisas para aprender xogando",
    sub: "Make It reúne nun só lugar a creación, o xogo e o seguimento.",
    items: [
      { title: "Crea en minutos", text: "Modelos, imaxes, sons, debuxos e vídeos de YouTube á man. Sen necesidade de programar." },
      { title: "Máis de 20 actividades", text: "Conectar, arrastrar, atopar a parella, ordenar, escribir, pintar, repasar símbolos e moito máis." },
      { title: "Comparte cun código", text: "Envía un código ou unha ligazón e xoga en calquera dispositivo, sen instalar nada." },
      { title: "Resultados ao instante", text: "Respostas, clasificacións e estatísticas de cada cuestionario, e o progreso de cada alumno." },
      { title: "Aulas", text: "O alumnado entra cun código. Comparte proxectos con toda a clase ou só con algúns alumnos." },
      { title: "Sempre sincronizado", text: "Os teus proxectos gárdanse na nube e están listos na web e na aplicación." },
    ],
  },
  create: {
    kicker: "Crea",
    title: "Deseña o teu proxecto ao teu xeito",
    text: "Escolle unha actividade, engade as túas imaxes, textos e sons, e o teu proxecto xa está listo. Todo o que fas gárdase automaticamente.",
    bullets: [
      "Bibliotecas con miles de imaxes e sons",
      "Grava a túa propia voz",
      "Debuxa directamente na páxina",
      "Engade vídeos de YouTube",
      "Modelos, tipos de letra e música de fondo",
    ],
  },
  play: {
    kicker: "Xogos",
    title: "Pensado para os máis pequenos",
    text: "Os xogos premian o esforzo: os erros non penalizan, os nenos poden tentalo tantas veces como precisen e nin sequera teñen que saber ler.",
    bullets: ["Reforzo positivo en cada páxina", "Instrucións con texto e son", "Non fai falta nome nin conta para xogar"],
  },
  quiz: {
    kicker: "Cuestionarios",
    title: "Cuestionarios con puntuacións e clasificacións",
    text: "Preguntas con tempo, puntuación automática e clasificación en directo. Ves as respostas de cada xogador e as estatísticas de cada pregunta.",
    bullets: ["Pregunta, verdadeiro ou falso, escribir a resposta, matemáticas…", "Tempo límite por pregunta", "Clasificación e estatísticas"],
  },
  share: {
    kicker: "Comparte",
    title: "Un código e a xogar",
    text: "Cada proxecto ten un código de seis díxitos e unha ligazón. O alumnado ábreo no navegador ou na aplicación, sen crear ningunha conta.",
    code: "Código",
    link: "Ligazón",
    classroom: "Ou compárteo cunha aula",
  },
  classes: {
    kicker: "Aulas",
    title: "Segue a evolución de cada alumno",
    text: "Crea unha aula, dálle o código ao teu alumnado e comparte proxectos con el, automaticamente con todos ou só con algúns.",
    bullets: ["O alumnado entra cun código", "Resultados de cada xogo e de cada cuestionario", "Comparte con toda a clase ou só con algúns"],
    mockTitle: "Resultados",
    mockCols: ["Alumno", "Puntuación", "Tempo"],
  },
  activities: {
    title: "Máis de 20 tipos de actividade",
    sub: "Para xogos e cuestionarios, listas para encher co teu propio contido.",
    games: "Xogos",
    quizzes: "Cuestionarios",
  },
  videos: {
    title: "Descobre Make It",
    sub: "Mira como se fai un proxecto en poucos minutos.",
    main: "Que é Make It?",
    tutorials: "Videotitoriais",
    quiz: "Como crear un cuestionario",
    game: "Como crear un xogo",
    share: "Como compartir un proxecto",
    all: "Ver todos os titoriais",
  },
  demos: {
    title: "Próbao agora",
    sub: "Xoga a algunhas das nosas demos, sen necesidade de rexistrarte.",
    games: "Xogos",
    quizzes: "Cuestionarios",
    play: "Xogar",
    pages: "páxinas",
  },
  plans: {
    title: "Plans para cada aula",
    sub: "Comeza gratis e pásate a Premium cando precises máis.",
    perYear: "/ano",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 proxectos", "1 aula", "1 cartafol", "Case todas as funcións"],
    educators: "Docentes",
    educatorsFeatures: ["1 docente", "Ata 100 alumnos", "Proxectos, aulas e cartafoles ilimitados", "Resultados e estatísticas de cada alumno"],
    teams: "Equipos",
    teamsFeatures: ["5 docentes", "Alumnado ilimitado", "Xestor de licenzas", "Todo o do plan Docentes"],
    schools: "Centros",
    schoolsFeatures: ["20 docentes", "Alumnado ilimitado", "Xestor de licenzas", "Todo o do plan Docentes"],
    startFree: "Comeza gratis",
    tryFree: "Próbao gratis",
    subscribe: "Subscríbete",
    note: "O alumnado sempre usa Make It gratis.",
  },
  faq: {
    title: "Preguntas frecuentes",
    sub: "Tes algunha dúbida? Aquí tes as respostas máis habituais.",
    items: [
      {
        q: "Que son os cuestionarios?",
        a: "Os cuestionarios son unha forma divertida de avaliar, aprender ou repasar calquera materia. Rexistran automaticamente as respostas e a puntuación de cada xogador, e podes ver as respostas, a clasificación e as estatísticas.",
      },
      {
        q: "Que son os xogos?",
        a: "Os xogos están pensados para os nenos máis pequenos, mesmo para os que aínda non saben ler. Baséanse no reforzo positivo: os erros non penalizan e os intentos son ilimitados. Os nenos non escriben ningún nome para xogar.",
      },
      {
        q: "Como podo ver os resultados dos xogos?",
        a: "Como os nenos non se identifican cando xogan, os resultados e as estatísticas dos xogos só se poden ver se o xogo se comparte a través dunha aula, non cun código nin cunha ligazón.",
      },
      {
        q: "Cales son os límites da versión gratuíta?",
        a: "Podes usar case todas as funcións. A versión gratuíta permíteche crear 2 proxectos, 1 aula e 1 cartafol.",
      },
      {
        q: "Que obteño ao subscribirme?",
        a: "Sen límites: tantos proxectos, aulas e cartafoles como queiras, e os resultados e as estatísticas de todo o teu alumnado.",
      },
      {
        q: "Necesito unha conta?",
        a: "Para xogar, non. Cunha conta podes crear e gardar os teus proxectos na nube, sincronizalos entre dispositivos e crear aulas para compartir proxectos co teu alumnado.",
      },
      { q: "O alumnado ten que pagar?", a: "Non. O alumnado sempre usa Make It gratis." },
      {
        q: "Como comparto un proxecto?",
        a: "Abre o proxecto e vai a Compartir: verás o seu código de seis díxitos e a súa ligazón. Quen os teña pode xogalo en calquera dispositivo. Tamén podes compartilo cunha aula para que o alumnado non necesite ningún código.",
      },
      {
        q: "Que son as aulas e como funcionan?",
        a: "As aulas permítenche xestionar grupos de alumnos. Crea unha aula, dálle o código ao teu alumnado para que entre e comparte proxectos con el: con todos ou só con algúns. Verás os resultados de cada alumno.",
      },
      {
        q: "Podo usar Make It en calquera dispositivo?",
        a: "Si. Podes crear e xogar na web, e a aplicación está dispoñible para iPad, iPhone, Android e Mac. Os teus proxectos mantéñense sincronizados.",
      },
    ],
  },
  download: {
    title: "Make It en todos os teus dispositivos",
    sub: "Úsao aquí mesmo no navegador ou descarga a aplicación.",
    web: "Abrir na web",
    available: "Dispoñible en",
  },
  final: { title: "Fai que aprender sexa divertido", sub: "Crea o teu primeiro proxecto hoxe. É gratis." },
  footer: {
    tagline: "Crea xogos, cuestionarios, actividades e contos, e compárteos onde queiras.",
    product: "Produto",
    help: "Axuda",
    legal: "Legal",
    contact: "Contacto",
    terms: "Termos e condicións",
    privacy: "Privacidade",
    cookies: "Política de cookies",
    follow: "Síguenos",
    support: "Co apoio de ACCIÓ",
    demos: "Demos",
  },
};

const nl: LandingText = {
  nav: {
    features: "Functies",
    activities: "Activiteiten",
    videos: "Video's",
    plans: "Abonnementen",
    faq: "Vragen",
    download: "Downloaden",
    login: "Inloggen",
    signup: "Gratis aanmelden",
    student: "Ik ben leerling",
    menu: "Menu",
    language: "Taal",
  },
  hero: {
    eyebrow: "Educatieve spellen en quizzen",
    title: "Maak in een paar minuten leerzame spellen en quizzen",
    sub: "Ontwerp projecten met meer dan 20 soorten activiteiten, deel ze met wie je maar wilt en bekijk meteen de resultaten. In de browser, op een tablet of op een telefoon.",
    start: "Gratis beginnen",
    demo: "Probeer een demo",
    codeTitle: "Heb je een code?",
    codeText: "Typ de code die je hebt gekregen en speel",
    free: "Gratis beginnen · Geen creditcard nodig",
  },
  stats: { downloads: "downloads", users: "actieve gebruikers", projects: "gemaakte projecten", countries: "landen" },
  grid: {
    title: "Alles wat je nodig hebt om spelenderwijs te leren",
    sub: "Make It brengt maken, spelen en de voortgang volgen samen op één plek.",
    items: [
      {
        title: "In een paar minuten klaar",
        text: "Sjablonen, afbeeldingen, geluiden, tekeningen en YouTube-video's binnen handbereik. Je hoeft niet te programmeren.",
      },
      { title: "Meer dan 20 activiteiten", text: "Verbinden, slepen, paren zoeken, ordenen, typen, schilderen, symbolen natekenen en nog veel meer." },
      { title: "Delen met een code", text: "Stuur een code of een link en speel op elk apparaat, zonder iets te installeren." },
      { title: "Direct resultaat", text: "Antwoorden, ranglijsten en statistieken voor elke quiz, en de voortgang van elke leerling." },
      { title: "Klassen", text: "Leerlingen doen mee met een code. Deel projecten met de hele klas of met maar een paar leerlingen." },
      { title: "Altijd gesynchroniseerd", text: "Je projecten worden in de cloud bewaard en staan klaar op het web en in de app." },
    ],
  },
  create: {
    kicker: "Maken",
    title: "Ontwerp je project zoals jij het wilt",
    text: "Kies een activiteit, voeg je afbeeldingen, teksten en geluiden toe en je project is klaar. Alles wat je doet, wordt automatisch opgeslagen.",
    bullets: [
      "Bibliotheken met duizenden afbeeldingen en geluiden",
      "Neem je eigen stem op",
      "Teken direct op de pagina",
      "Voeg YouTube-video's toe",
      "Sjablonen, lettertypen en achtergrondmuziek",
    ],
  },
  play: {
    kicker: "Spellen",
    title: "Gemaakt voor de kleintjes",
    text: "Spellen belonen inzet: fouten worden niet bestraft, kinderen mogen het zo vaak proberen als ze nodig hebben en ze hoeven niet eens te kunnen lezen.",
    bullets: ["Positieve bekrachtiging op elke pagina", "Instructies met tekst en geluid", "Geen naam of account nodig om te spelen"],
  },
  quiz: {
    kicker: "Quizzen",
    title: "Quizzen met scores en ranglijsten",
    text: "Vragen met een tijdslimiet, automatische scores en een live ranglijst. Je ziet de antwoorden van elke speler en de statistieken van elke vraag.",
    bullets: ["Vraag, waar of niet waar, antwoord typen, rekenen…", "Tijdslimiet per vraag", "Ranglijst en statistieken"],
  },
  share: {
    kicker: "Delen",
    title: "Eén code en spelen maar",
    text: "Elk project heeft een code van zes cijfers en een link. Leerlingen openen het in de browser of in de app, zonder een account aan te maken.",
    code: "Code",
    link: "Link",
    classroom: "Of deel het met een klas",
  },
  classes: {
    kicker: "Klassen",
    title: "Volg elke leerling",
    text: "Maak een klas, geef je leerlingen de code en deel projecten met hen: automatisch met iedereen of alleen met sommigen.",
    bullets: ["Leerlingen doen mee met een code", "Resultaten van elk spel en elke quiz", "Deel met de hele klas of met een paar leerlingen"],
    mockTitle: "Resultaten",
    mockCols: ["Leerling", "Score", "Tijd"],
  },
  activities: {
    title: "Meer dan 20 soorten activiteiten",
    sub: "Voor spellen en voor quizzen, klaar om te vullen met je eigen inhoud.",
    games: "Spellen",
    quizzes: "Quizzen",
  },
  videos: {
    title: "Ontdek Make It",
    sub: "Zie hoe je in een paar minuten een project maakt.",
    main: "Wat is Make It?",
    tutorials: "Videotutorials",
    quiz: "Zo maak je een quiz",
    game: "Zo maak je een spel",
    share: "Zo deel je een project",
    all: "Bekijk alle tutorials",
  },
  demos: {
    title: "Probeer het nu",
    sub: "Speel een paar van onze demo's, zonder je aan te melden.",
    games: "Spellen",
    quizzes: "Quizzen",
    play: "Spelen",
    pages: "pagina's",
  },
  plans: {
    title: "Een abonnement voor elke klas",
    sub: "Begin gratis en stap over op Premium als je meer nodig hebt.",
    perYear: "/jaar",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 projecten", "1 klas", "1 map", "Bijna alle functies"],
    educators: "Leerkrachten",
    educatorsFeatures: ["1 leerkracht", "Tot 100 leerlingen", "Onbeperkt projecten, klassen en mappen", "Resultaten en statistieken van elke leerling"],
    teams: "Teams",
    teamsFeatures: ["5 leerkrachten", "Onbeperkt leerlingen", "Licentiebeheerder", "Alles uit Leerkrachten"],
    schools: "Scholen",
    schoolsFeatures: ["20 leerkrachten", "Onbeperkt leerlingen", "Licentiebeheerder", "Alles uit Leerkrachten"],
    startFree: "Gratis beginnen",
    tryFree: "Gratis proberen",
    subscribe: "Abonneren",
    note: "Leerlingen gebruiken Make It altijd gratis.",
  },
  faq: {
    title: "Veelgestelde vragen",
    sub: "Twijfel je nog? Hier vind je de antwoorden op de meest gestelde vragen.",
    items: [
      {
        q: "Wat zijn quizzen?",
        a: "Quizzen zijn een leuke manier om elk vak te toetsen, te leren of te herhalen. Ze registreren automatisch de antwoorden en de score van elke speler, en jij kunt de antwoorden, de ranglijst en de statistieken bekijken.",
      },
      {
        q: "Wat zijn spellen?",
        a: "Spellen zijn bedoeld voor jonge kinderen, ook voor kinderen die nog niet kunnen lezen. Ze draaien om positieve bekrachtiging: fouten worden niet bestraft en kinderen mogen het onbeperkt proberen. Ze hoeven geen naam in te voeren om te spelen.",
      },
      {
        q: "Hoe kan ik de resultaten van spellen zien?",
        a: "Omdat kinderen zich niet identificeren als ze spelen, zijn de resultaten en statistieken van spellen alleen te zien als het spel via een klas wordt gedeeld, niet via een code of link.",
      },
      {
        q: "Wat zijn de beperkingen van de gratis versie?",
        a: "Je kunt bijna alle functies gebruiken. Met de gratis versie kun je 2 projecten, 1 klas en 1 map maken.",
      },
      {
        q: "Wat krijg ik met een abonnement?",
        a: "Geen beperkingen: zoveel projecten, klassen en mappen als je wilt, en de resultaten en statistieken van al je leerlingen.",
      },
      {
        q: "Heb ik een account nodig?",
        a: "Om te spelen niet. Met een account kun je projecten maken en in de cloud bewaren, ze synchroniseren tussen je apparaten en klassen maken om projecten met je leerlingen te delen.",
      },
      { q: "Moeten leerlingen betalen?", a: "Nee. Leerlingen gebruiken Make It altijd gratis." },
      {
        q: "Hoe deel ik een project?",
        a: "Open het project en ga naar Delen: daar zie je de code van zes cijfers en de link. Wie die heeft, kan het op elk apparaat spelen. Je kunt het ook met een klas delen, zodat leerlingen geen code nodig hebben.",
      },
      {
        q: "Wat zijn klassen en hoe werken ze?",
        a: "Met klassen beheer je groepen leerlingen. Maak een klas, geef je leerlingen de code zodat ze kunnen meedoen, en deel projecten met hen: met iedereen of alleen met sommigen. Je ziet de resultaten van elke leerling.",
      },
      {
        q: "Kan ik Make It op elk apparaat gebruiken?",
        a: "Ja. Je kunt maken en spelen op het web, en de app is beschikbaar voor iPad, iPhone, Android en Mac. Je projecten blijven gesynchroniseerd.",
      },
    ],
  },
  download: {
    title: "Make It op al je apparaten",
    sub: "Gebruik het hier in de browser of download de app.",
    web: "Openen op het web",
    available: "Verkrijgbaar in",
  },
  final: { title: "Maak leren leuk", sub: "Maak vandaag nog je eerste project. Het is gratis." },
  footer: {
    tagline: "Maak spellen, quizzen, activiteiten en verhalen, en deel ze overal.",
    product: "Product",
    help: "Help",
    legal: "Juridisch",
    contact: "Contact",
    terms: "Algemene voorwaarden",
    privacy: "Privacy",
    cookies: "Cookiebeleid",
    follow: "Volg ons",
    support: "Met de steun van ACCIÓ",
    demos: "Demo's",
  },
};

const pl: LandingText = {
  nav: {
    features: "Funkcje",
    activities: "Aktywności",
    videos: "Filmy",
    plans: "Plany",
    faq: "Pytania",
    download: "Pobierz",
    login: "Zaloguj się",
    signup: "Załóż konto za darmo",
    student: "Jestem uczniem",
    menu: "Menu",
    language: "Język",
  },
  hero: {
    eyebrow: "Gry i quizy edukacyjne",
    title: "Twórz gry i quizy edukacyjne w kilka minut",
    sub: "Korzystaj z ponad 20 rodzajów aktywności, udostępniaj projekty komu chcesz i od razu sprawdzaj wyniki. W przeglądarce, na tablecie lub na telefonie.",
    start: "Zacznij za darmo",
    demo: "Wypróbuj demo",
    codeTitle: "Masz kod?",
    codeText: "Wpisz otrzymany kod i graj",
    free: "Za darmo na start · Bez karty płatniczej",
  },
  stats: { downloads: "pobrań", users: "aktywnych użytkowników", projects: "utworzonych projektów", countries: "krajów" },
  grid: {
    title: "Wszystko, czego potrzebujesz do nauki przez zabawę",
    sub: "Make It łączy tworzenie, granie i śledzenie postępów w jednym miejscu.",
    items: [
      { title: "Twórz w kilka minut", text: "Szablony, obrazy, dźwięki, rysunki i filmy z YouTube pod ręką. Bez programowania." },
      { title: "Ponad 20 aktywności", text: "Łącz, przeciągaj, szukaj par, porządkuj, wpisuj, maluj, rysuj po śladzie i wiele więcej." },
      { title: "Udostępniaj kodem", text: "Wyślij kod lub link i graj na dowolnym urządzeniu, bez instalowania czegokolwiek." },
      { title: "Wyniki od razu", text: "Odpowiedzi, rankingi i statystyki każdego quizu oraz postępy każdego ucznia." },
      { title: "Klasy", text: "Uczniowie dołączają za pomocą kodu. Udostępniaj projekty całej klasie albo tylko wybranym osobom." },
      { title: "Zawsze zsynchronizowane", text: "Twoje projekty są zapisane w chmurze i gotowe w przeglądarce oraz w aplikacji." },
    ],
  },
  create: {
    kicker: "Twórz",
    title: "Stwórz projekt po swojemu",
    text: "Wybierz aktywność, dodaj obrazy, teksty i dźwięki – i projekt gotowy. Wszystko, co robisz, zapisuje się automatycznie.",
    bullets: [
      "Biblioteki z tysiącami obrazów i dźwięków",
      "Nagrywaj własny głos",
      "Rysuj bezpośrednio na stronie",
      "Dodawaj filmy z YouTube",
      "Szablony, czcionki i muzyka w tle",
    ],
  },
  play: {
    kicker: "Gry",
    title: "Stworzone dla najmłodszych",
    text: "Gry nagradzają wysiłek: błędy nie są karane, dzieci mogą próbować tyle razy, ile potrzebują, i nie muszą nawet umieć czytać.",
    bullets: ["Pozytywne wzmocnienie na każdej stronie", "Instrukcje w formie tekstu i dźwięku", "Do gry nie trzeba imienia ani konta"],
  },
  quiz: {
    kicker: "Quizy",
    title: "Quizy z punktacją i rankingiem",
    text: "Pytania na czas, automatyczne punktowanie i ranking na żywo. Widzisz odpowiedzi każdego gracza i statystyki każdego pytania.",
    bullets: ["Pytanie, prawda czy fałsz, wpisz odpowiedź, matematyka…", "Limit czasu na każde pytanie", "Ranking i statystyki"],
  },
  share: {
    kicker: "Udostępnianie",
    title: "Jeden kod i grasz",
    text: "Każdy projekt ma sześciocyfrowy kod i link. Uczniowie otwierają go w przeglądarce lub w aplikacji, bez zakładania konta.",
    code: "Kod",
    link: "Link",
    classroom: "Albo udostępnij go klasie",
  },
  classes: {
    kicker: "Klasy",
    title: "Śledź postępy każdego ucznia",
    text: "Utwórz klasę, podaj uczniom jej kod i udostępniaj im projekty – automatycznie wszystkim albo tylko wybranym.",
    bullets: ["Uczniowie dołączają za pomocą kodu", "Wyniki każdej gry i każdego quizu", "Udostępniaj całej klasie lub wybranym uczniom"],
    mockTitle: "Wyniki",
    mockCols: ["Uczeń", "Wynik", "Czas"],
  },
  activities: { title: "Ponad 20 rodzajów aktywności", sub: "Do gier i quizów, gotowe do wypełnienia twoimi treściami.", games: "Gry", quizzes: "Quizy" },
  videos: {
    title: "Poznaj Make It",
    sub: "Zobacz, jak w kilka minut powstaje projekt.",
    main: "Czym jest Make It?",
    tutorials: "Filmy instruktażowe",
    quiz: "Jak utworzyć quiz",
    game: "Jak utworzyć grę",
    share: "Jak udostępnić projekt",
    all: "Zobacz wszystkie filmy instruktażowe",
  },
  demos: {
    title: "Wypróbuj teraz",
    sub: "Zagraj w nasze przykładowe gry i quizy, bez rejestracji.",
    games: "Gry",
    quizzes: "Quizy",
    play: "Graj",
    pages: "str.",
  },
  plans: {
    title: "Plany dla każdej klasy",
    sub: "Zacznij za darmo i przejdź na Premium, gdy potrzebujesz więcej.",
    perYear: "/rok",
    free: "Bezpłatny",
    freePrice: "0 €",
    freeFeatures: ["2 projekty", "1 klasa", "1 folder", "Prawie wszystkie funkcje"],
    educators: "Nauczyciele",
    educatorsFeatures: ["1 nauczyciel", "Do 100 uczniów", "Nieograniczona liczba projektów, klas i folderów", "Wyniki i statystyki każdego ucznia"],
    teams: "Zespoły",
    teamsFeatures: ["5 nauczycieli", "Nieograniczona liczba uczniów", "Zarządzanie licencją", "Wszystko z planu Nauczyciele"],
    schools: "Szkoły",
    schoolsFeatures: ["20 nauczycieli", "Nieograniczona liczba uczniów", "Zarządzanie licencją", "Wszystko z planu Nauczyciele"],
    startFree: "Zacznij za darmo",
    tryFree: "Wypróbuj za darmo",
    subscribe: "Subskrybuj",
    note: "Uczniowie zawsze korzystają z Make It za darmo.",
  },
  faq: {
    title: "Najczęściej zadawane pytania",
    sub: "Masz wątpliwości? Oto odpowiedzi na najczęstsze pytania.",
    items: [
      {
        q: "Czym są quizy?",
        a: "Quizy to zabawny sposób na sprawdzanie wiedzy, naukę lub powtórkę z dowolnego przedmiotu. Automatycznie zapisują odpowiedzi i wynik każdego gracza, a ty możesz zobaczyć odpowiedzi, ranking i statystyki.",
      },
      {
        q: "Czym są gry?",
        a: "Gry są przeznaczone dla małych dzieci, także tych, które jeszcze nie umieją czytać. Stawiają na pozytywne wzmocnienie: błędy nie są karane, a liczba prób jest nieograniczona. Dzieci nie wpisują imienia, żeby zagrać.",
      },
      {
        q: "Jak mogę zobaczyć wyniki gier?",
        a: "Ponieważ dzieci podczas gry nie podają swojego imienia, wyniki i statystyki gier są dostępne tylko wtedy, gdy gra jest udostępniona przez klasę, a nie za pomocą kodu lub linku.",
      },
      {
        q: "Jakie są ograniczenia wersji bezpłatnej?",
        a: "Możesz korzystać z prawie wszystkich funkcji. Wersja bezpłatna pozwala utworzyć 2 projekty, 1 klasę i 1 folder.",
      },
      {
        q: "Co daje subskrypcja?",
        a: "Brak ograniczeń: tyle projektów, klas i folderów, ile chcesz, oraz wyniki i statystyki wszystkich twoich uczniów.",
      },
      {
        q: "Czy potrzebuję konta?",
        a: "Do grania – nie. Z kontem możesz tworzyć projekty i zapisywać je w chmurze, synchronizować je między urządzeniami i tworzyć klasy, aby udostępniać projekty uczniom.",
      },
      { q: "Czy uczniowie muszą płacić?", a: "Nie. Uczniowie zawsze korzystają z Make It za darmo." },
      {
        q: "Jak udostępnić projekt?",
        a: "Otwórz projekt i przejdź do zakładki „Udostępnij”: zobaczysz jego sześciocyfrowy kod i link. Każdy, kto je ma, może w niego zagrać na dowolnym urządzeniu. Możesz go też udostępnić klasie – wtedy uczniowie nie potrzebują żadnego kodu.",
      },
      {
        q: "Czym są klasy i jak działają?",
        a: "Klasy pozwalają zarządzać grupami uczniów. Utwórz klasę, podaj uczniom jej kod, żeby mogli dołączyć, i udostępniaj im projekty: wszystkim albo tylko wybranym. Zobaczysz wyniki każdego ucznia.",
      },
      {
        q: "Czy mogę używać Make It na każdym urządzeniu?",
        a: "Tak. Możesz tworzyć i grać w przeglądarce, a aplikacja jest dostępna na iPada, iPhone'a, Androida i Maca. Twoje projekty są synchronizowane.",
      },
    ],
  },
  download: {
    title: "Make It na wszystkich twoich urządzeniach",
    sub: "Korzystaj z niego tutaj, w przeglądarce, albo pobierz aplikację.",
    web: "Otwórz w przeglądarce",
    available: "Dostępne w",
  },
  final: { title: "Niech nauka będzie zabawą", sub: "Utwórz swój pierwszy projekt już dziś. To nic nie kosztuje." },
  footer: {
    tagline: "Twórz gry, quizy, aktywności i historyjki i udostępniaj je wszędzie.",
    product: "Produkt",
    help: "Pomoc",
    legal: "Informacje prawne",
    contact: "Kontakt",
    terms: "Regulamin",
    privacy: "Prywatność",
    cookies: "Polityka plików cookie",
    follow: "Obserwuj nas",
    support: "Przy wsparciu ACCIÓ",
    demos: "Przykłady",
  },
};

const ro: LandingText = {
  nav: {
    features: "Funcții",
    activities: "Activități",
    videos: "Videoclipuri",
    plans: "Planuri",
    faq: "Întrebări",
    download: "Descarcă",
    login: "Conectează-te",
    signup: "Creează un cont",
    student: "Sunt elev",
    menu: "Meniu",
    language: "Limbă",
  },
  hero: {
    eyebrow: "Jocuri și chestionare educative",
    title: "Creează jocuri și chestionare educative în câteva minute",
    sub: "Concepe proiecte cu peste 20 de tipuri de activități, partajează-le cu oricine și vezi rezultatele pe loc. În browser, pe tabletă sau pe telefon.",
    start: "Începe gratuit",
    demo: "Încearcă un demo",
    codeTitle: "Ai un cod?",
    codeText: "Scrie codul primit și joacă",
    free: "Începi gratuit · Fără card",
  },
  stats: { downloads: "descărcări", users: "utilizatori activi", projects: "proiecte create", countries: "țări" },
  grid: {
    title: "Tot ce îți trebuie ca să înveți prin joacă",
    sub: "Make It reunește într-un singur loc crearea, joaca și urmărirea progresului.",
    items: [
      {
        title: "Creează în câteva minute",
        text: "Șabloane, imagini, sunete, desene și videoclipuri YouTube la îndemână. Nu trebuie să știi programare.",
      },
      { title: "Peste 20 de activități", text: "Unește, trage, găsește perechea, ordonează, scrie, colorează, trasează simboluri și multe altele." },
      { title: "Partajează cu un cod", text: "Trimite un cod sau un link și joacă pe orice dispozitiv, fără să instalezi nimic." },
      { title: "Rezultate pe loc", text: "Răspunsuri, clasamente și statistici pentru fiecare chestionar, plus progresul fiecărui elev." },
      { title: "Clase", text: "Elevii intră cu un cod. Partajează proiecte cu toată clasa sau doar cu câțiva elevi." },
      { title: "Mereu sincronizat", text: "Proiectele tale sunt salvate în cloud și sunt gata de folosit pe web și în aplicație." },
    ],
  },
  create: {
    kicker: "Creează",
    title: "Creează-ți proiectul cum vrei tu",
    text: "Alege o activitate, adaugă imaginile, textele și sunetele tale, și proiectul e gata. Tot ce faci se salvează automat.",
    bullets: [
      "Biblioteci cu mii de imagini și sunete",
      "Înregistrează-ți propria voce",
      "Desenează direct pe pagină",
      "Adaugă videoclipuri YouTube",
      "Șabloane, fonturi și muzică de fundal",
    ],
  },
  play: {
    kicker: "Jocuri",
    title: "Făcute pentru cei mici",
    text: "Jocurile răsplătesc efortul: greșelile nu sunt penalizate, copiii pot încerca de câte ori au nevoie și nici măcar nu trebuie să știe să citească.",
    bullets: ["Întărire pozitivă pe fiecare pagină", "Instrucțiuni cu text și sunet", "Nu e nevoie de nume sau de cont ca să joci"],
  },
  quiz: {
    kicker: "Chestionare",
    title: "Chestionare cu scor și clasament",
    text: "Întrebări cu timp limită, punctaj automat și clasament în timp real. Vezi răspunsurile fiecărui jucător și statisticile fiecărei întrebări.",
    bullets: ["Întrebare, adevărat sau fals, scrie răspunsul, matematică…", "Timp limită pentru fiecare întrebare", "Clasament și statistici"],
  },
  share: {
    kicker: "Partajează",
    title: "Un cod și gata de joacă",
    text: "Fiecare proiect are un cod din șase cifre și un link. Elevii îl deschid în browser sau în aplicație, fără să-și creeze cont.",
    code: "Cod",
    link: "Link",
    classroom: "Sau partajează-l cu o clasă",
  },
  classes: {
    kicker: "Clase",
    title: "Urmărește progresul fiecărui elev",
    text: "Creează o clasă, dă-le elevilor codul ei și partajează proiecte cu ei, automat cu toată lumea sau doar cu unii.",
    bullets: ["Elevii intră cu un cod", "Rezultatele fiecărui joc și ale fiecărui chestionar", "Partajează cu toată clasa sau doar cu câțiva"],
    mockTitle: "Rezultate",
    mockCols: ["Elev", "Scor", "Timp"],
  },
  activities: {
    title: "Peste 20 de tipuri de activități",
    sub: "Pentru jocuri și pentru chestionare, gata să le completezi cu propriul conținut.",
    games: "Jocuri",
    quizzes: "Chestionare",
  },
  videos: {
    title: "Descoperă Make It",
    sub: "Vezi cum se face un proiect în câteva minute.",
    main: "Ce este Make It?",
    tutorials: "Tutoriale video",
    quiz: "Cum creezi un chestionar",
    game: "Cum creezi un joc",
    share: "Cum partajezi un proiect",
    all: "Vezi toate tutorialele",
  },
  demos: {
    title: "Încearcă acum",
    sub: "Joacă unele dintre demo-urile noastre, fără să te înregistrezi.",
    games: "Jocuri",
    quizzes: "Chestionare",
    play: "Joacă",
    pages: "pagini",
  },
  plans: {
    title: "Planuri pentru fiecare clasă",
    sub: "Începe gratuit și treci la Premium când ai nevoie de mai mult.",
    perYear: "/an",
    free: "Gratuit",
    freePrice: "0 €",
    freeFeatures: ["2 proiecte", "1 clasă", "1 dosar", "Aproape toate funcțiile"],
    educators: "Profesori",
    educatorsFeatures: ["1 profesor", "Până la 100 de elevi", "Proiecte, clase și dosare nelimitate", "Rezultatele și statisticile fiecărui elev"],
    teams: "Echipe",
    teamsFeatures: ["5 profesori", "Elevi nelimitați", "Administrator de licențe", "Tot ce include planul Profesori"],
    schools: "Școli",
    schoolsFeatures: ["20 de profesori", "Elevi nelimitați", "Administrator de licențe", "Tot ce include planul Profesori"],
    startFree: "Începe gratuit",
    tryFree: "Încearcă gratuit",
    subscribe: "Abonează-te",
    note: "Elevii folosesc mereu Make It gratuit.",
  },
  faq: {
    title: "Întrebări frecvente",
    sub: "Ai nelămuriri? Iată răspunsurile la cele mai frecvente întrebări.",
    items: [
      {
        q: "Ce sunt chestionarele?",
        a: "Chestionarele sunt o modalitate distractivă de a evalua, de a învăța sau de a recapitula orice materie. Ele înregistrează automat răspunsurile și scorul fiecărui jucător, iar tu poți vedea răspunsurile, clasamentul și statisticile.",
      },
      {
        q: "Ce sunt jocurile?",
        a: "Jocurile sunt gândite pentru copiii mici, chiar și pentru cei care încă nu știu să citească. Pun accent pe întărirea pozitivă: greșelile nu sunt penalizate, iar încercările sunt nelimitate. Copiii nu trebuie să-și scrie numele ca să joace.",
      },
      {
        q: "Cum pot vedea rezultatele jocurilor?",
        a: "Pentru că, atunci când joacă, copiii nu se identifică, rezultatele și statisticile jocurilor pot fi văzute doar dacă jocul este partajat printr-o clasă, nu prin cod sau link.",
      },
      {
        q: "Care sunt limitele versiunii gratuite?",
        a: "Poți folosi aproape toate funcțiile. Versiunea gratuită îți permite să creezi 2 proiecte, 1 clasă și 1 dosar.",
      },
      {
        q: "Ce primesc dacă mă abonez?",
        a: "Fără limite: oricâte proiecte, clase și dosare vrei, plus rezultatele și statisticile tuturor elevilor tăi.",
      },
      {
        q: "Am nevoie de cont?",
        a: "Ca să joci, nu. Cu un cont îți poți crea și salva proiectele în cloud, le poți sincroniza pe toate dispozitivele și poți crea clase ca să partajezi proiecte cu elevii tăi.",
      },
      { q: "Elevii trebuie să plătească?", a: "Nu. Elevii folosesc mereu Make It gratuit." },
      {
        q: "Cum partajez un proiect?",
        a: "Deschide proiectul și mergi la „Partajează”: vei vedea codul lui din șase cifre și linkul. Oricine le are îl poate juca pe orice dispozitiv. Îl poți partaja și cu o clasă, ca elevii să nu aibă nevoie de niciun cod.",
      },
      {
        q: "Ce sunt clasele și cum funcționează?",
        a: "Clasele te ajută să gestionezi grupuri de elevi. Creează o clasă, dă-le elevilor codul ei ca să poată intra și partajează proiecte cu ei: cu toți sau doar cu unii. Vei vedea rezultatele fiecărui elev.",
      },
      {
        q: "Pot folosi Make It pe orice dispozitiv?",
        a: "Da. Poți crea și juca pe web, iar aplicația este disponibilă pentru iPad, iPhone, Android și Mac. Proiectele tale rămân sincronizate.",
      },
    ],
  },
  download: {
    title: "Make It pe toate dispozitivele tale",
    sub: "Folosește-l chiar aici, în browser, sau descarcă aplicația.",
    web: "Deschide pe web",
    available: "Disponibil pe",
  },
  final: { title: "Fă din învățare o joacă", sub: "Creează-ți primul proiect chiar azi. Este gratuit." },
  footer: {
    tagline: "Creează jocuri, chestionare, activități și povești și partajează-le oriunde.",
    product: "Produs",
    help: "Ajutor",
    legal: "Informații legale",
    contact: "Contact",
    terms: "Termeni și condiții",
    privacy: "Confidențialitate",
    cookies: "Politica privind cookie-urile",
    follow: "Urmărește-ne",
    support: "Cu sprijinul ACCIÓ",
    demos: "Demo-uri",
  },
};

const sv: LandingText = {
  nav: {
    features: "Funktioner",
    activities: "Aktiviteter",
    videos: "Videor",
    plans: "Priser",
    faq: "Vanliga frågor",
    download: "Ladda ner",
    login: "Logga in",
    signup: "Registrera dig gratis",
    student: "Jag är elev",
    menu: "Meny",
    language: "Språk",
  },
  hero: {
    eyebrow: "Pedagogiska spel och test",
    title: "Skapa lärorika spel och test på några minuter",
    sub: "Skapa projekt med fler än 20 typer av aktiviteter, dela dem med vem du vill och se resultaten direkt. I webbläsaren, på surfplattan eller i mobilen.",
    start: "Kom igång gratis",
    demo: "Prova en demo",
    codeTitle: "Har du en kod?",
    codeText: "Skriv koden du har fått och spela",
    free: "Gratis att börja · Inget kort behövs",
  },
  stats: { downloads: "nedladdningar", users: "aktiva användare", projects: "skapade projekt", countries: "länder" },
  grid: {
    title: "Allt du behöver för att lära genom lek",
    sub: "Make It samlar skapande, spel och uppföljning på ett och samma ställe.",
    items: [
      { title: "Skapa på några minuter", text: "Mallar, bilder, ljud, teckningar och YouTube-videor nära till hands. Ingen programmering behövs." },
      { title: "Fler än 20 aktiviteter", text: "Koppla ihop, dra, hitta paret, ordna, skriv, måla, spåra symboler och mycket mer." },
      { title: "Dela med en kod", text: "Skicka en kod eller en länk och spela på vilken enhet som helst, utan att installera något." },
      { title: "Resultat direkt", text: "Svar, topplistor och statistik för varje test, och varje elevs framsteg." },
      { title: "Klasser", text: "Eleverna går med via en kod. Dela projekt med hela klassen eller bara med några." },
      { title: "Alltid synkat", text: "Dina projekt sparas i molnet och finns redo på webben och i appen." },
    ],
  },
  create: {
    kicker: "Skapa",
    title: "Utforma ditt projekt på ditt sätt",
    text: "Välj en aktivitet, lägg till dina bilder, texter och ljud, så är ditt projekt klart. Allt du gör sparas automatiskt.",
    bullets: [
      "Bibliotek med tusentals bilder och ljud",
      "Spela in din egen röst",
      "Rita direkt på sidan",
      "Lägg till YouTube-videor",
      "Mallar, typsnitt och bakgrundsmusik",
    ],
  },
  play: {
    kicker: "Spel",
    title: "Gjort för de minsta",
    text: "Spelen belönar ansträngning: misstag bestraffas inte, barnen kan försöka så många gånger de behöver och de behöver inte ens kunna läsa.",
    bullets: ["Positiv förstärkning på varje sida", "Instruktioner med text och ljud", "Inget namn eller konto behövs för att spela"],
  },
  quiz: {
    kicker: "Test",
    title: "Test med poäng och topplistor",
    text: "Frågor på tid, automatisk poängräkning och en topplista i realtid. Du ser varje spelares svar och statistiken för varje fråga.",
    bullets: ["Fråga, sant eller falskt, skriv svaret, matte…", "Tidsgräns per fråga", "Topplista och statistik"],
  },
  share: {
    kicker: "Dela",
    title: "En kod – sedan är det bara att spela",
    text: "Varje projekt har en sexsiffrig kod och en länk. Eleverna öppnar det i webbläsaren eller i appen, utan att skapa något konto.",
    code: "Kod",
    link: "Länk",
    classroom: "Eller dela det med en klass",
  },
  classes: {
    kicker: "Klasser",
    title: "Följ varje elev",
    text: "Skapa en klass, ge dina elever klassens kod och dela projekt med dem, automatiskt med alla eller bara med några.",
    bullets: ["Eleverna går med via en kod", "Resultat från varje spel och varje test", "Dela med hela klassen eller med några få"],
    mockTitle: "Resultat",
    mockCols: ["Elev", "Poäng", "Tid"],
  },
  activities: { title: "Fler än 20 typer av aktiviteter", sub: "För spel och för test, redo att fyllas med ditt eget innehåll.", games: "Spel", quizzes: "Test" },
  videos: {
    title: "Upptäck Make It",
    sub: "Se hur ett projekt skapas på några minuter.",
    main: "Vad är Make It?",
    tutorials: "Videoguider",
    quiz: "Så skapar du ett test",
    game: "Så skapar du ett spel",
    share: "Så delar du ett projekt",
    all: "Se alla guider",
  },
  demos: {
    title: "Prova nu",
    sub: "Spela några av våra exempel direkt, utan att registrera dig.",
    games: "Spel",
    quizzes: "Test",
    play: "Spela",
    pages: "sidor",
  },
  plans: {
    title: "Planer för alla klassrum",
    sub: "Börja gratis och uppgradera till Premium när du behöver mer.",
    perYear: "/år",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 projekt", "1 klass", "1 mapp", "Nästan alla funktioner"],
    educators: "Pedagoger",
    educatorsFeatures: ["1 pedagog", "Upp till 100 elever", "Obegränsat antal projekt, klasser och mappar", "Resultat och statistik för varje elev"],
    teams: "Team",
    teamsFeatures: ["5 pedagoger", "Obegränsat antal elever", "Licenshantering", "Allt som ingår i Pedagoger"],
    schools: "Skolor",
    schoolsFeatures: ["20 pedagoger", "Obegränsat antal elever", "Licenshantering", "Allt som ingår i Pedagoger"],
    startFree: "Kom igång gratis",
    tryFree: "Prova gratis",
    subscribe: "Prenumerera",
    note: "Eleverna använder alltid Make It gratis.",
  },
  faq: {
    title: "Vanliga frågor",
    sub: "Undrar du något? Här är svaren på de vanligaste frågorna.",
    items: [
      {
        q: "Vad är test?",
        a: "Test är ett roligt sätt att bedöma, lära sig eller repetera vilket ämne som helst. De registrerar automatiskt varje spelares svar och poäng, och du kan se svaren, topplistan och statistiken.",
      },
      {
        q: "Vad är spel?",
        a: "Spelen är gjorda för små barn, även de som ännu inte kan läsa. De fokuserar på positiv förstärkning: misstag bestraffas inte och man kan försöka hur många gånger som helst. Barnen behöver inte skriva något namn för att spela.",
      },
      {
        q: "Hur kan jag se resultaten från spel?",
        a: "Eftersom barnen inte identifierar sig när de spelar kan resultat och statistik från spel bara ses när spelet delas via en klass, inte via kod eller länk.",
      },
      {
        q: "Vilka begränsningar har gratisversionen?",
        a: "Du kan använda nästan alla funktioner. Med gratisversionen kan du skapa 2 projekt, 1 klass och 1 mapp.",
      },
      {
        q: "Vad får jag om jag prenumererar?",
        a: "Inga begränsningar: så många projekt, klasser och mappar du vill, och resultat och statistik för alla dina elever.",
      },
      {
        q: "Behöver jag ett konto?",
        a: "För att spela, nej. Med ett konto kan du skapa och spara dina projekt i molnet, synka dem mellan enheter och skapa klasser för att dela projekt med dina elever.",
      },
      { q: "Måste eleverna betala?", a: "Nej. Eleverna använder alltid Make It gratis." },
      {
        q: "Hur delar jag ett projekt?",
        a: "Öppna projektet och gå till Dela: där ser du dess sexsiffriga kod och dess länk. Den som har dem kan spela projektet på vilken enhet som helst. Du kan också dela det med en klass, så behöver eleverna ingen kod.",
      },
      {
        q: "Vad är klasser och hur fungerar de?",
        a: "Med klasser kan du hantera grupper av elever. Skapa en klass, ge dina elever klassens kod så att de kan gå med och dela projekt med dem: med alla eller bara med några. Du ser varje elevs resultat.",
      },
      {
        q: "Kan jag använda Make It på alla enheter?",
        a: "Ja. Du kan skapa och spela på webben, och appen finns för iPad, iPhone, Android och Mac. Dina projekt hålls synkroniserade.",
      },
    ],
  },
  download: {
    title: "Make It på alla dina enheter",
    sub: "Använd det direkt här i webbläsaren eller ladda ner appen.",
    web: "Öppna på webben",
    available: "Finns på",
  },
  final: { title: "Gör lärandet roligt", sub: "Skapa ditt första projekt i dag. Det är gratis." },
  footer: {
    tagline: "Skapa spel, test, aktiviteter och berättelser och dela dem var som helst.",
    product: "Produkt",
    help: "Hjälp",
    legal: "Juridiskt",
    contact: "Kontakt",
    terms: "Villkor",
    privacy: "Integritet",
    cookies: "Cookiepolicy",
    follow: "Följ oss",
    support: "Med stöd från ACCIÓ",
    demos: "Demo",
  },
};

const el: LandingText = {
  nav: {
    features: "Δυνατότητες",
    activities: "Δραστηριότητες",
    videos: "Βίντεο",
    plans: "Πακέτα",
    faq: "Συχνές ερωτήσεις",
    download: "Λήψη",
    login: "Σύνδεση",
    signup: "Δωρεάν εγγραφή",
    student: "Είμαι μαθητής",
    menu: "Μενού",
    language: "Γλώσσα",
  },
  hero: {
    eyebrow: "Εκπαιδευτικά παιχνίδια και κουίζ",
    title: "Φτιάξε παιχνίδια μάθησης και κουίζ σε λίγα λεπτά",
    sub: "Σχεδίασε έργα με περισσότερα από 20 είδη δραστηριοτήτων, μοιράσου τα με όποιον θέλεις και δες τα αποτελέσματα αμέσως. Στο πρόγραμμα περιήγησης, σε τάμπλετ ή σε κινητό.",
    start: "Ξεκίνα δωρεάν",
    demo: "Δοκίμασε ένα παράδειγμα",
    codeTitle: "Έχεις κωδικό;",
    codeText: "Γράψε τον κωδικό που σου έδωσαν και παίξε",
    free: "Ξεκινάς δωρεάν · Χωρίς πιστωτική κάρτα",
  },
  stats: { downloads: "λήψεις", users: "ενεργοί χρήστες", projects: "έργα που δημιουργήθηκαν", countries: "χώρες" },
  grid: {
    title: "Ό,τι χρειάζεσαι για να μαθαίνεις παίζοντας",
    sub: "Το Make It συγκεντρώνει σε ένα μέρος τη δημιουργία, το παιχνίδι και την παρακολούθηση.",
    items: [
      {
        title: "Δημιουργία σε λίγα λεπτά",
        text: "Πρότυπα, εικόνες, ήχοι, ζωγραφιές και βίντεο του YouTube στη διάθεσή σου. Δεν χρειάζεται προγραμματισμός.",
      },
      {
        title: "Περισσότερες από 20 δραστηριότητες",
        text: "Ένωσε, σύρε, βρες το ζευγάρι, βάλε σε σειρά, γράψε, ζωγράφισε, ιχνηλάτησε σύμβολα και πολλά ακόμα.",
      },
      {
        title: "Κοινοποίηση με κωδικό",
        text: "Στείλε έναν κωδικό ή έναν σύνδεσμο και παίζουν σε οποιαδήποτε συσκευή, χωρίς να εγκαταστήσουν τίποτα.",
      },
      { title: "Άμεσα αποτελέσματα", text: "Απαντήσεις, κατατάξεις και στατιστικά για κάθε κουίζ, και η πρόοδος κάθε μαθητή." },
      { title: "Τάξεις", text: "Οι μαθητές μπαίνουν με έναν κωδικό. Μοιράσου έργα με όλη την τάξη ή μόνο με μερικούς." },
      { title: "Πάντα συγχρονισμένα", text: "Τα έργα σου αποθηκεύονται στο νέφος και είναι έτοιμα στον ιστότοπο και στην εφαρμογή." },
    ],
  },
  create: {
    kicker: "Δημιουργία",
    title: "Σχεδίασε το έργο σου όπως θέλεις",
    text: "Διάλεξε μια δραστηριότητα, πρόσθεσε τις εικόνες, τα κείμενα και τους ήχους σου, και το έργο σου είναι έτοιμο. Ό,τι κάνεις αποθηκεύεται αυτόματα.",
    bullets: [
      "Βιβλιοθήκες με χιλιάδες εικόνες και ήχους",
      "Ηχογράφησε τη δική σου φωνή",
      "Ζωγράφισε απευθείας στη σελίδα",
      "Πρόσθεσε βίντεο του YouTube",
      "Πρότυπα, γραμματοσειρές και μουσική υπόκρουση",
    ],
  },
  play: {
    kicker: "Παιχνίδια",
    title: "Φτιαγμένα για τους μικρούς",
    text: "Τα παιχνίδια επιβραβεύουν την προσπάθεια: τα λάθη δεν τιμωρούνται, τα παιδιά μπορούν να προσπαθήσουν όσες φορές χρειάζονται και δεν χρειάζεται καν να ξέρουν να διαβάζουν.",
    bullets: ["Θετική ενίσχυση σε κάθε σελίδα", "Οδηγίες με κείμενο και ήχο", "Δεν χρειάζεται όνομα ή λογαριασμός για να παίξουν"],
  },
  quiz: {
    kicker: "Κουίζ",
    title: "Κουίζ με βαθμολογία και κατάταξη",
    text: "Ερωτήσεις με χρονόμετρο, αυτόματη βαθμολόγηση και κατάταξη σε πραγματικό χρόνο. Βλέπεις τις απαντήσεις κάθε παίκτη και τα στατιστικά κάθε ερώτησης.",
    bullets: ["Ερώτηση, σωστό ή λάθος, γράψε την απάντηση, μαθηματικά…", "Όριο χρόνου ανά ερώτηση", "Κατάταξη και στατιστικά"],
  },
  share: {
    kicker: "Κοινοποίηση",
    title: "Ένας κωδικός και παίζεις",
    text: "Κάθε έργο έχει έναν εξαψήφιο κωδικό και έναν σύνδεσμο. Οι μαθητές το ανοίγουν στο πρόγραμμα περιήγησης ή στην εφαρμογή, χωρίς να δημιουργήσουν λογαριασμό.",
    code: "Κωδικός",
    link: "Σύνδεσμος",
    classroom: "Ή μοιράσου το με μια τάξη",
  },
  classes: {
    kicker: "Τάξεις",
    title: "Παρακολούθησε κάθε μαθητή",
    text: "Δημιούργησε μια τάξη, δώσε τον κωδικό της στους μαθητές σου και μοιράσου έργα μαζί τους, αυτόματα με όλους ή μόνο με μερικούς.",
    bullets: ["Οι μαθητές μπαίνουν με έναν κωδικό", "Αποτελέσματα κάθε παιχνιδιού και κάθε κουίζ", "Μοιράσου με όλη την τάξη ή με μερικούς"],
    mockTitle: "Αποτελέσματα",
    mockCols: ["Μαθητής", "Βαθμολογία", "Χρόνος"],
  },
  activities: {
    title: "Περισσότερα από 20 είδη δραστηριοτήτων",
    sub: "Για παιχνίδια και για κουίζ, έτοιμα να τα γεμίσεις με το δικό σου περιεχόμενο.",
    games: "Παιχνίδια",
    quizzes: "Κουίζ",
  },
  videos: {
    title: "Γνώρισε το Make It",
    sub: "Δες πώς φτιάχνεται ένα έργο σε λίγα λεπτά.",
    main: "Τι είναι το Make It;",
    tutorials: "Οδηγοί σε βίντεο",
    quiz: "Πώς να φτιάξεις ένα κουίζ",
    game: "Πώς να φτιάξεις ένα παιχνίδι",
    share: "Πώς να μοιραστείς ένα έργο",
    all: "Δες όλους τους οδηγούς",
  },
  demos: {
    title: "Δοκίμασέ το τώρα",
    sub: "Παίξε μερικά από τα παραδείγματά μας, χωρίς εγγραφή.",
    games: "Παιχνίδια",
    quizzes: "Κουίζ",
    play: "Παίξε",
    pages: "σελίδες",
  },
  plans: {
    title: "Πακέτα για κάθε τάξη",
    sub: "Ξεκίνα δωρεάν και πέρασε στο Premium όταν χρειαστείς περισσότερα.",
    perYear: "/έτος",
    free: "Δωρεάν",
    freePrice: "0 €",
    freeFeatures: ["2 έργα", "1 τάξη", "1 φάκελος", "Σχεδόν όλες οι λειτουργίες"],
    educators: "Εκπαιδευτικοί",
    educatorsFeatures: ["1 εκπαιδευτικός", "Έως 100 μαθητές", "Απεριόριστα έργα, τάξεις και φάκελοι", "Αποτελέσματα και στατιστικά κάθε μαθητή"],
    teams: "Ομάδες",
    teamsFeatures: ["5 εκπαιδευτικοί", "Απεριόριστοι μαθητές", "Διαχείριση αδειών", "Όλα όσα περιλαμβάνει το πακέτο Εκπαιδευτικοί"],
    schools: "Σχολεία",
    schoolsFeatures: ["20 εκπαιδευτικοί", "Απεριόριστοι μαθητές", "Διαχείριση αδειών", "Όλα όσα περιλαμβάνει το πακέτο Εκπαιδευτικοί"],
    startFree: "Ξεκίνα δωρεάν",
    tryFree: "Δοκίμασέ το δωρεάν",
    subscribe: "Γίνε συνδρομητής",
    note: "Οι μαθητές χρησιμοποιούν πάντα το Make It δωρεάν.",
  },
  faq: {
    title: "Συχνές ερωτήσεις",
    sub: "Έχεις απορίες; Εδώ θα βρεις τις πιο συνηθισμένες απαντήσεις.",
    items: [
      {
        q: "Τι είναι τα κουίζ;",
        a: "Τα κουίζ είναι ένας διασκεδαστικός τρόπος για να αξιολογήσεις, να μάθεις ή να επαναλάβεις οποιοδήποτε μάθημα. Καταγράφουν αυτόματα τις απαντήσεις και τη βαθμολογία κάθε παίκτη, και μπορείς να δεις τις απαντήσεις, την κατάταξη και τα στατιστικά.",
      },
      {
        q: "Τι είναι τα παιχνίδια;",
        a: "Τα παιχνίδια είναι σχεδιασμένα για μικρά παιδιά, ακόμα και για όσα δεν ξέρουν ακόμα να διαβάζουν. Δίνουν έμφαση στη θετική ενίσχυση: τα λάθη δεν τιμωρούνται και οι προσπάθειες είναι απεριόριστες. Τα παιδιά δεν γράφουν όνομα για να παίξουν.",
      },
      {
        q: "Πώς μπορώ να δω τα αποτελέσματα των παιχνιδιών;",
        a: "Επειδή τα παιδιά δεν δηλώνουν ποια είναι όταν παίζουν, τα αποτελέσματα και τα στατιστικά των παιχνιδιών φαίνονται μόνο όταν το παιχνίδι κοινοποιείται μέσω τάξης, όχι με κωδικό ή σύνδεσμο.",
      },
      {
        q: "Ποια είναι τα όρια της δωρεάν έκδοσης;",
        a: "Μπορείς να χρησιμοποιήσεις σχεδόν όλες τις λειτουργίες. Η δωρεάν έκδοση σού επιτρέπει να δημιουργήσεις 2 έργα, 1 τάξη και 1 φάκελο.",
      },
      {
        q: "Τι κερδίζω με τη συνδρομή;",
        a: "Κανένα όριο: όσα έργα, τάξεις και φακέλους θέλεις, καθώς και τα αποτελέσματα και τα στατιστικά όλων των μαθητών σου.",
      },
      {
        q: "Χρειάζομαι λογαριασμό;",
        a: "Για να παίξεις, όχι. Με λογαριασμό μπορείς να δημιουργείς και να αποθηκεύεις τα έργα σου στο νέφος, να τα συγχρονίζεις σε όλες τις συσκευές σου και να δημιουργείς τάξεις για να μοιράζεσαι έργα με τους μαθητές σου.",
      },
      { q: "Πρέπει να πληρώσουν οι μαθητές;", a: "Όχι. Οι μαθητές χρησιμοποιούν πάντα το Make It δωρεάν." },
      {
        q: "Πώς μοιράζομαι ένα έργο;",
        a: "Άνοιξε το έργο και πήγαινε στην «Κοινοποίηση»: θα δεις τον εξαψήφιο κωδικό του και τον σύνδεσμό του. Όποιος τα έχει μπορεί να το παίξει σε οποιαδήποτε συσκευή. Μπορείς επίσης να το μοιραστείς με μια τάξη, ώστε οι μαθητές να μη χρειάζονται κωδικό.",
      },
      {
        q: "Τι είναι οι τάξεις και πώς λειτουργούν;",
        a: "Οι τάξεις σού επιτρέπουν να διαχειρίζεσαι ομάδες μαθητών. Δημιούργησε μια τάξη, δώσε τον κωδικό της στους μαθητές σου για να μπουν και μοιράσου έργα μαζί τους: με όλους ή μόνο με μερικούς. Θα βλέπεις τα αποτελέσματα κάθε μαθητή.",
      },
      {
        q: "Μπορώ να χρησιμοποιήσω το Make It σε οποιαδήποτε συσκευή;",
        a: "Ναι. Μπορείς να δημιουργείς και να παίζεις στον ιστότοπο, και η εφαρμογή είναι διαθέσιμη για iPad, iPhone, Android και Mac. Τα έργα σου μένουν συγχρονισμένα.",
      },
    ],
  },
  download: {
    title: "Το Make It σε όλες τις συσκευές σου",
    sub: "Χρησιμοποίησέ το εδώ, στο πρόγραμμα περιήγησης, ή κατέβασε την εφαρμογή.",
    web: "Άνοιγμα στον ιστότοπο",
    available: "Διαθέσιμο στο",
  },
  final: { title: "Κάνε τη μάθηση διασκεδαστική", sub: "Δημιούργησε το πρώτο σου έργο σήμερα. Είναι δωρεάν." },
  footer: {
    tagline: "Δημιούργησε παιχνίδια, κουίζ, δραστηριότητες και ιστορίες, και μοιράσου τα παντού.",
    product: "Προϊόν",
    help: "Βοήθεια",
    legal: "Νομικά",
    contact: "Επικοινωνία",
    terms: "Όροι και προϋποθέσεις",
    privacy: "Απόρρητο",
    cookies: "Πολιτική cookies",
    follow: "Ακολούθησέ μας",
    support: "Με την υποστήριξη του ACCIÓ",
    demos: "Παραδείγματα",
  },
};

const tr: LandingText = {
  nav: {
    features: "Özellikler",
    activities: "Etkinlikler",
    videos: "Videolar",
    plans: "Planlar",
    faq: "SSS",
    download: "İndir",
    login: "Giriş yap",
    signup: "Ücretsiz kaydolun",
    student: "Öğrenciyim",
    menu: "Menü",
    language: "Dil",
  },
  hero: {
    eyebrow: "Eğitici oyunlar ve testler",
    title: "Birkaç dakikada eğitici oyunlar ve testler oluşturun",
    sub: "20'den fazla etkinlik türüyle projeler tasarlayın, istediğiniz kişiyle paylaşın ve sonuçları anında görün. Tarayıcıda, tablette veya telefonda.",
    start: "Ücretsiz başlayın",
    demo: "Bir demo deneyin",
    codeTitle: "Kodunuz var mı?",
    codeText: "Size verilen kodu yazın ve oynayın",
    free: "Başlamak ücretsiz · Kredi kartı gerekmez",
  },
  stats: { downloads: "indirme", users: "aktif kullanıcı", projects: "oluşturulan proje", countries: "ülke" },
  grid: {
    title: "Oynayarak öğrenmek için ihtiyacınız olan her şey",
    sub: "Make It; oluşturmayı, oynamayı ve takibi tek bir yerde bir araya getirir.",
    items: [
      {
        title: "Dakikalar içinde oluşturun",
        text: "Şablonlar, görseller, sesler, çizimler ve YouTube videoları elinizin altında. Kodlama bilgisi gerekmez.",
      },
      { title: "20'den fazla etkinlik", text: "Birleştirme, sürükleme, eşini bulma, sıralama, yazma, boyama, sembol çizme ve çok daha fazlası." },
      { title: "Kodla paylaşın", text: "Bir kod ya da bağlantı gönderin; hiçbir şey yüklemeden her cihazda oynanabilir." },
      { title: "Anında sonuçlar", text: "Her test için cevaplar, sıralamalar ve istatistikler; ayrıca her öğrencinin ilerlemesi." },
      { title: "Sınıflar", text: "Öğrenciler bir kodla katılır. Projeleri tüm sınıfla ya da yalnızca birkaç öğrenciyle paylaşın." },
      { title: "Her zaman eşitlenmiş", text: "Projeleriniz bulutta kaydedilir; web'de ve uygulamada her an hazırdır." },
    ],
  },
  create: {
    kicker: "Oluşturun",
    title: "Projenizi istediğiniz gibi tasarlayın",
    text: "Bir etkinlik seçin, görsellerinizi, metinlerinizi ve seslerinizi ekleyin; projeniz hazır. Yaptığınız her şey otomatik olarak kaydedilir.",
    bullets: [
      "Binlerce görsel ve ses içeren kütüphaneler",
      "Kendi sesinizi kaydedin",
      "Doğrudan sayfanın üzerine çizin",
      "YouTube videoları ekleyin",
      "Şablonlar, yazı tipleri ve arka plan müziği",
    ],
  },
  play: {
    kicker: "Oyunlar",
    title: "Küçükler için tasarlandı",
    text: "Oyunlar çabayı ödüllendirir: hatalar cezalandırılmaz, çocuklar istedikleri kadar deneyebilir ve okuma bilmeleri bile gerekmez.",
    bullets: ["Her sayfada olumlu pekiştirme", "Yazılı ve sesli yönergeler", "Oynamak için ad veya hesap gerekmez"],
  },
  quiz: {
    kicker: "Testler",
    title: "Puanlı ve sıralamalı testler",
    text: "Süreli sorular, otomatik puanlama ve canlı sıralama. Her oyuncunun cevaplarını ve her sorunun istatistiklerini görürsünüz.",
    bullets: ["Soru, doğru / yanlış, cevabı yaz, matematik…", "Soru başına süre sınırı", "Sıralama ve istatistikler"],
  },
  share: {
    kicker: "Paylaşın",
    title: "Tek kodla hemen oynayın",
    text: "Her projenin altı haneli bir kodu ve bir bağlantısı vardır. Öğrenciler projeyi hesap oluşturmadan tarayıcıda veya uygulamada açar.",
    code: "Kod",
    link: "Bağlantı",
    classroom: "Ya da bir sınıfla paylaşın",
  },
  classes: {
    kicker: "Sınıflar",
    title: "Her öğrenciyi takip edin",
    text: "Bir sınıf oluşturun, öğrencilerinize sınıfın kodunu verin ve projeleri onlarla paylaşın: otomatik olarak herkesle ya da yalnızca bazılarıyla.",
    bullets: ["Öğrenciler bir kodla katılır", "Her oyunun ve her testin sonuçları", "Tüm sınıfla ya da birkaç öğrenciyle paylaşın"],
    mockTitle: "Sonuçlar",
    mockCols: ["Öğrenci", "Puan", "Süre"],
  },
  activities: {
    title: "20'den fazla etkinlik türü",
    sub: "Oyunlar ve testler için; kendi içeriğinizle doldurulmaya hazır.",
    games: "Oyunlar",
    quizzes: "Testler",
  },
  videos: {
    title: "Make It'i keşfedin",
    sub: "Bir projenin birkaç dakikada nasıl hazırlandığını görün.",
    main: "Make It nedir?",
    tutorials: "Eğitim videoları",
    quiz: "Test nasıl oluşturulur",
    game: "Oyun nasıl oluşturulur",
    share: "Proje nasıl paylaşılır",
    all: "Tüm eğitim videolarını görün",
  },
  demos: {
    title: "Hemen deneyin",
    sub: "Kaydolmadan demolarımızdan bazılarını oynayın.",
    games: "Oyunlar",
    quizzes: "Testler",
    play: "Oyna",
    pages: "sayfa",
  },
  plans: {
    title: "Her sınıfa uygun planlar",
    sub: "Ücretsiz başlayın, daha fazlasına ihtiyacınız olduğunda Premium'a geçin.",
    perYear: "/yıl",
    free: "Ücretsiz",
    freePrice: "0 €",
    freeFeatures: ["2 proje", "1 sınıf", "1 klasör", "Neredeyse tüm özellikler"],
    educators: "Eğitimciler",
    educatorsFeatures: ["1 eğitimci", "100 öğrenciye kadar", "Sınırsız proje, sınıf ve klasör", "Her öğrencinin sonuçları ve istatistikleri"],
    teams: "Ekipler",
    teamsFeatures: ["5 eğitimci", "Sınırsız öğrenci", "Lisans yöneticisi", "Eğitimciler planındaki her şey"],
    schools: "Okullar",
    schoolsFeatures: ["20 eğitimci", "Sınırsız öğrenci", "Lisans yöneticisi", "Eğitimciler planındaki her şey"],
    startFree: "Ücretsiz başlayın",
    tryFree: "Ücretsiz deneyin",
    subscribe: "Abone ol",
    note: "Öğrenciler Make It'i her zaman ücretsiz kullanır.",
  },
  faq: {
    title: "Sıkça sorulan sorular",
    sub: "Aklınıza takılan bir şey mi var? İşte en sık sorulan soruların cevapları.",
    items: [
      {
        q: "Testler nedir?",
        a: "Testler, herhangi bir konuyu eğlenceli bir şekilde değerlendirmenin, öğrenmenin veya tekrar etmenin bir yoludur. Her oyuncunun cevaplarını ve puanını otomatik olarak kaydeder; siz de cevapları, sıralamayı ve istatistikleri görebilirsiniz.",
      },
      {
        q: "Oyunlar nedir?",
        a: "Oyunlar, henüz okuma bilmeyenler de dahil olmak üzere küçük çocuklar için tasarlanmıştır. Olumlu pekiştirmeye odaklanır: hatalar cezalandırılmaz ve deneme hakkı sınırsızdır. Çocuklar oynamak için ad girmez.",
      },
      {
        q: "Oyunların sonuçlarını nasıl görebilirim?",
        a: "Çocuklar oynarken kimliklerini belirtmediği için oyunların sonuçları ve istatistikleri yalnızca oyun bir sınıf aracılığıyla paylaşıldığında görülebilir; kod veya bağlantıyla paylaşıldığında görülemez.",
      },
      {
        q: "Ücretsiz sürümün sınırları nelerdir?",
        a: "Neredeyse tüm özellikleri kullanabilirsiniz. Ücretsiz sürümle 2 proje, 1 sınıf ve 1 klasör oluşturabilirsiniz.",
      },
      {
        q: "Abone olunca ne kazanırım?",
        a: "Hiçbir sınır yok: istediğiniz kadar proje, sınıf ve klasör; ayrıca tüm öğrencilerinizin sonuçları ve istatistikleri.",
      },
      {
        q: "Hesaba ihtiyacım var mı?",
        a: "Oynamak için hayır. Hesapla projelerinizi oluşturup bulutta kaydedebilir, cihazlarınız arasında eşitleyebilir ve projeleri öğrencilerinizle paylaşmak için sınıflar oluşturabilirsiniz.",
      },
      { q: "Öğrenciler ödeme yapmak zorunda mı?", a: "Hayır. Öğrenciler Make It'i her zaman ücretsiz kullanır." },
      {
        q: "Bir projeyi nasıl paylaşırım?",
        a: "Projeyi açın ve “Paylaş” bölümüne gidin: altı haneli kodunu ve bağlantısını göreceksiniz. Bunlara sahip olan herkes projeyi her cihazda oynayabilir. Projeyi bir sınıfla da paylaşabilirsiniz; böylece öğrencilerin hiçbir koda ihtiyacı olmaz.",
      },
      {
        q: "Sınıflar nedir ve nasıl çalışır?",
        a: "Sınıflar, öğrenci gruplarını yönetmenizi sağlar. Bir sınıf oluşturun, katılabilmeleri için öğrencilerinize sınıfın kodunu verin ve projeleri onlarla paylaşın: herkesle ya da yalnızca bazılarıyla. Her öğrencinin sonuçlarını görürsünüz.",
      },
      {
        q: "Make It'i her cihazda kullanabilir miyim?",
        a: "Evet. Web'de oluşturabilir ve oynayabilirsiniz; uygulama ise iPad, iPhone, Android ve Mac için mevcuttur. Projeleriniz her zaman eşitlenir.",
      },
    ],
  },
  download: {
    title: "Make It tüm cihazlarınızda",
    sub: "Hemen burada, tarayıcıda kullanın ya da uygulamayı indirin.",
    web: "Web'de açın",
    available: "Şuradan indirin",
  },
  final: { title: "Öğrenmeyi eğlenceli hâle getirin", sub: "İlk projenizi bugün oluşturun. Ücretsiz." },
  footer: {
    tagline: "Oyunlar, testler, etkinlikler ve hikâyeler oluşturun ve bunları her yerde paylaşın.",
    product: "Ürün",
    help: "Yardım",
    legal: "Yasal",
    contact: "İletişim",
    terms: "Şartlar ve koşullar",
    privacy: "Gizlilik",
    cookies: "Çerez politikası",
    follow: "Bizi takip edin",
    support: "ACCIÓ desteğiyle",
    demos: "Demolar",
  },
};

const ru: LandingText = {
  nav: {
    features: "Возможности",
    activities: "Задания",
    videos: "Видео",
    plans: "Тарифы",
    faq: "Вопросы",
    download: "Скачать",
    login: "Войти",
    signup: "Создать аккаунт",
    student: "Я ученик",
    menu: "Меню",
    language: "Язык",
  },
  hero: {
    eyebrow: "Обучающие игры и викторины",
    title: "Создавайте обучающие игры и викторины за считаные минуты",
    sub: "Создавайте проекты с более чем 20 видами заданий, делитесь ими с кем угодно и сразу смотрите результаты. В браузере, на планшете или на телефоне.",
    start: "Начать бесплатно",
    demo: "Попробовать демо",
    codeTitle: "Есть код?",
    codeText: "Введите полученный код и играйте",
    free: "Начните бесплатно · Без банковской карты",
  },
  stats: { downloads: "скачиваний", users: "активных пользователей", projects: "созданных проектов", countries: "стран" },
  grid: {
    title: "Всё, чтобы учиться играя",
    sub: "Make It объединяет создание, игру и отслеживание результатов в одном месте.",
    items: [
      { title: "Создавайте за минуты", text: "Шаблоны, изображения, звуки, рисунки и видео с YouTube всегда под рукой. Программировать не нужно." },
      {
        title: "Более 20 видов заданий",
        text: "Соединяйте, перетаскивайте, находите пары, упорядочивайте, пишите, раскрашивайте, обводите символы и многое другое.",
      },
      { title: "Делитесь по коду", text: "Отправьте код или ссылку — играть можно на любом устройстве, ничего не устанавливая." },
      { title: "Мгновенные результаты", text: "Ответы, рейтинги и статистика по каждой викторине, а также успехи каждого ученика." },
      { title: "Классы", text: "Ученики входят в класс по коду. Делитесь проектами со всем классом или только с некоторыми учениками." },
      { title: "Всегда синхронизировано", text: "Ваши проекты хранятся в облаке и доступны на сайте и в приложении." },
    ],
  },
  create: {
    kicker: "Создание",
    title: "Создайте проект по-своему",
    text: "Выберите задание, добавьте свои изображения, тексты и звуки — и проект готов. Всё, что вы делаете, сохраняется автоматически.",
    bullets: [
      "Библиотеки с тысячами изображений и звуков",
      "Запись собственного голоса",
      "Рисование прямо на странице",
      "Видео с YouTube",
      "Шаблоны, шрифты и фоновая музыка",
    ],
  },
  play: {
    kicker: "Игры",
    title: "Для самых маленьких",
    text: "Игры поощряют старание: за ошибки не наказывают, дети могут пробовать сколько угодно раз, и им даже не нужно уметь читать.",
    bullets: ["Положительное подкрепление на каждой странице", "Инструкции с текстом и звуком", "Для игры не нужны ни имя, ни аккаунт"],
  },
  quiz: {
    kicker: "Викторины",
    title: "Викторины с баллами и рейтингом",
    text: "Вопросы на время, автоматический подсчёт баллов и рейтинг в реальном времени. Вы видите ответы каждого игрока и статистику по каждому вопросу.",
    bullets: ["Вопрос, верно или неверно, ввод ответа, математика…", "Ограничение времени на каждый вопрос", "Рейтинг и статистика"],
  },
  share: {
    kicker: "Поделиться",
    title: "Один код — и можно играть",
    text: "У каждого проекта есть шестизначный код и ссылка. Ученики открывают его в браузере или в приложении без регистрации.",
    code: "Код",
    link: "Ссылка",
    classroom: "Или поделитесь им с классом",
  },
  classes: {
    kicker: "Классы",
    title: "Следите за каждым учеником",
    text: "Создайте класс, дайте ученикам его код и делитесь с ними проектами — автоматически со всеми или только с некоторыми.",
    bullets: ["Ученики входят по коду", "Результаты каждой игры и каждой викторины", "Делитесь со всем классом или с несколькими учениками"],
    mockTitle: "Результаты",
    mockCols: ["Ученик", "Баллы", "Время"],
  },
  activities: {
    title: "Более 20 видов заданий",
    sub: "Для игр и для викторин — остаётся только наполнить их своим содержанием.",
    games: "Игры",
    quizzes: "Викторины",
  },
  videos: {
    title: "Познакомьтесь с Make It",
    sub: "Посмотрите, как за несколько минут создаётся проект.",
    main: "Что такое Make It?",
    tutorials: "Видеоуроки",
    quiz: "Как создать викторину",
    game: "Как создать игру",
    share: "Как поделиться проектом",
    all: "Все видеоуроки",
  },
  demos: {
    title: "Попробуйте прямо сейчас",
    sub: "Сыграйте в наши демо — регистрация не нужна.",
    games: "Игры",
    quizzes: "Викторины",
    play: "Играть",
    pages: "стр.",
  },
  plans: {
    title: "Тарифы для любого класса",
    sub: "Начните бесплатно и переходите на Premium, когда понадобится больше.",
    perYear: "/год",
    free: "Бесплатно",
    freePrice: "0 €",
    freeFeatures: ["2 проекта", "1 класс", "1 папка", "Почти все функции"],
    educators: "Педагоги",
    educatorsFeatures: ["1 педагог", "До 100 учеников", "Неограниченное число проектов, классов и папок", "Результаты и статистика каждого ученика"],
    teams: "Команды",
    teamsFeatures: ["5 педагогов", "Неограниченное число учеников", "Управление лицензиями", "Всё из тарифа «Педагоги»"],
    schools: "Школы",
    schoolsFeatures: ["20 педагогов", "Неограниченное число учеников", "Управление лицензиями", "Всё из тарифа «Педагоги»"],
    startFree: "Начать бесплатно",
    tryFree: "Попробовать бесплатно",
    subscribe: "Подписаться",
    note: "Для учеников Make It всегда бесплатен.",
  },
  faq: {
    title: "Частые вопросы",
    sub: "Есть сомнения? Вот ответы на самые частые вопросы.",
    items: [
      {
        q: "Что такое викторины?",
        a: "Викторины — увлекательный способ проверить знания, изучить или повторить любой предмет. Они автоматически сохраняют ответы и баллы каждого игрока, а вы можете посмотреть ответы, рейтинг и статистику.",
      },
      {
        q: "Что такое игры?",
        a: "Игры созданы для маленьких детей, даже для тех, кто ещё не умеет читать. Главное в них — положительное подкрепление: за ошибки не наказывают, а попыток сколько угодно. Чтобы играть, детям не нужно вводить имя.",
      },
      {
        q: "Как посмотреть результаты игр?",
        a: "Поскольку дети не называют себя во время игры, результаты и статистику игр можно увидеть, только если вы поделились игрой через класс, а не по коду или ссылке.",
      },
      {
        q: "Какие ограничения у бесплатной версии?",
        a: "Вы можете пользоваться почти всеми функциями. В бесплатной версии можно создать 2 проекта, 1 класс и 1 папку.",
      },
      {
        q: "Что даёт подписка?",
        a: "Никаких ограничений: сколько угодно проектов, классов и папок, а также результаты и статистика всех ваших учеников.",
      },
      {
        q: "Нужен ли аккаунт?",
        a: "Для игры — нет. С аккаунтом вы можете создавать проекты и сохранять их в облаке, синхронизировать их между устройствами и создавать классы, чтобы делиться проектами с учениками.",
      },
      { q: "Должны ли ученики платить?", a: "Нет. Для учеников Make It всегда бесплатен." },
      {
        q: "Как поделиться проектом?",
        a: "Откройте проект и перейдите на вкладку «Поделиться»: там вы увидите его шестизначный код и ссылку. Любой, у кого они есть, может играть на любом устройстве. Также можно поделиться проектом с классом — тогда ученикам не понадобится никакой код.",
      },
      {
        q: "Что такое классы и как они работают?",
        a: "Классы позволяют управлять группами учеников. Создайте класс, дайте ученикам его код, чтобы они присоединились, и делитесь с ними проектами — со всеми или только с некоторыми. Вы будете видеть результаты каждого ученика.",
      },
      {
        q: "Можно ли пользоваться Make It на любом устройстве?",
        a: "Да. Создавать проекты и играть можно на сайте, а приложение доступно для iPad, iPhone, Android и Mac. Ваши проекты синхронизируются.",
      },
    ],
  },
  download: {
    title: "Make It на всех ваших устройствах",
    sub: "Пользуйтесь прямо здесь, в браузере, или скачайте приложение.",
    web: "Открыть на сайте",
    available: "Доступно в",
  },
  final: { title: "Сделайте учёбу увлекательной", sub: "Создайте свой первый проект уже сегодня. Это бесплатно." },
  footer: {
    tagline: "Создавайте игры, викторины, задания и истории и делитесь ими где угодно.",
    product: "Продукт",
    help: "Помощь",
    legal: "Правовая информация",
    contact: "Контакты",
    terms: "Условия использования",
    privacy: "Конфиденциальность",
    cookies: "Политика в отношении файлов cookie",
    follow: "Мы в соцсетях",
    support: "При поддержке ACCIÓ",
    demos: "Демо",
  },
};

const uk: LandingText = {
  nav: {
    features: "Можливості",
    activities: "Завдання",
    videos: "Відео",
    plans: "Тарифи",
    faq: "Запитання",
    download: "Завантажити",
    login: "Увійти",
    signup: "Безкоштовна реєстрація",
    student: "Я учень",
    menu: "Меню",
    language: "Мова",
  },
  hero: {
    eyebrow: "Освітні ігри та вікторини",
    title: "Створюйте навчальні ігри та вікторини за лічені хвилини",
    sub: "Створюйте проєкти з понад 20 типами завдань, діліться ними з ким завгодно й одразу отримуйте результати. У браузері, на планшеті чи на телефоні.",
    start: "Почати безкоштовно",
    demo: "Спробувати приклад",
    codeTitle: "Маєте код?",
    codeText: "Введіть отриманий код і грайте",
    free: "Почати можна безкоштовно · Без банківської картки",
  },
  stats: { downloads: "завантажень", users: "активних користувачів", projects: "створених проєктів", countries: "країн" },
  grid: {
    title: "Усе, що потрібно, щоб навчатися граючись",
    sub: "Make It поєднує створення, гру та відстеження результатів в одному місці.",
    items: [
      {
        title: "Створюйте за лічені хвилини",
        text: "Шаблони, зображення, звуки, малюнки й відео з YouTube завжди під рукою. Програмувати не потрібно.",
      },
      {
        title: "Понад 20 типів завдань",
        text: "З’єднуйте, перетягуйте, шукайте пари, упорядковуйте, пишіть, розфарбовуйте, обводьте символи та багато іншого.",
      },
      { title: "Діліться за кодом", text: "Надішліть код або посилання — і грайте на будь-якому пристрої, нічого не встановлюючи." },
      { title: "Миттєві результати", text: "Відповіді, рейтинги та статистика кожної вікторини, а також прогрес кожного учня." },
      { title: "Класи", text: "Учні приєднуються за кодом. Діліться проєктами з усім класом або лише з кількома учнями." },
      { title: "Завжди синхронізовано", text: "Ваші проєкти зберігаються в хмарі й доступні на сайті та в застосунку." },
    ],
  },
  create: {
    kicker: "Створення",
    title: "Створіть проєкт на свій смак",
    text: "Виберіть завдання, додайте свої зображення, тексти й звуки — і проєкт готовий. Усе, що ви робите, зберігається автоматично.",
    bullets: [
      "Бібліотеки з тисячами зображень і звуків",
      "Записуйте власний голос",
      "Малюйте прямо на сторінці",
      "Додавайте відео з YouTube",
      "Шаблони, шрифти й фонова музика",
    ],
  },
  play: {
    kicker: "Ігри",
    title: "Створено для найменших",
    text: "Ігри винагороджують зусилля: за помилки не карають, діти можуть пробувати стільки разів, скільки потрібно, і їм навіть не обов’язково вміти читати.",
    bullets: ["Позитивне підкріплення на кожній сторінці", "Інструкції з текстом і звуком", "Для гри не потрібні ні ім’я, ні обліковий запис"],
  },
  quiz: {
    kicker: "Вікторини",
    title: "Вікторини з балами та рейтингом",
    text: "Запитання на час, автоматичне нарахування балів і рейтинг у реальному часі. Ви бачите відповіді кожного гравця та статистику кожного запитання.",
    bullets: ["Запитання, правда чи неправда, введення відповіді, математика…", "Обмеження часу на запитання", "Рейтинг і статистика"],
  },
  share: {
    kicker: "Поширення",
    title: "Один код — і можна грати",
    text: "Кожен проєкт має шестизначний код і посилання. Учні відкривають його в браузері або в застосунку, не створюючи облікового запису.",
    code: "Код",
    link: "Посилання",
    classroom: "Або поділіться ним із класом",
  },
  classes: {
    kicker: "Класи",
    title: "Стежте за успіхами кожного учня",
    text: "Створіть клас, дайте учням його код і діліться з ними проєктами — автоматично з усіма або лише з деякими.",
    bullets: ["Учні приєднуються за кодом", "Результати кожної гри та кожної вікторини", "Діліться з усім класом або з кількома учнями"],
    mockTitle: "Результати",
    mockCols: ["Учень", "Бали", "Час"],
  },
  activities: { title: "Понад 20 типів завдань", sub: "Для ігор і вікторин — готові до наповнення вашим власним вмістом.", games: "Ігри", quizzes: "Вікторини" },
  videos: {
    title: "Відкрийте для себе Make It",
    sub: "Подивіться, як створити проєкт за кілька хвилин.",
    main: "Що таке Make It?",
    tutorials: "Відеоуроки",
    quiz: "Як створити вікторину",
    game: "Як створити гру",
    share: "Як поділитися проєктом",
    all: "Переглянути всі відеоуроки",
  },
  demos: {
    title: "Спробуйте зараз",
    sub: "Пограйте в деякі з наших прикладів — без реєстрації.",
    games: "Ігри",
    quizzes: "Вікторини",
    play: "Грати",
    pages: "стор.",
  },
  plans: {
    title: "Тарифи для будь-якого класу",
    sub: "Почніть безкоштовно й переходьте на Premium, коли знадобиться більше.",
    perYear: "/рік",
    free: "Безкоштовно",
    freePrice: "0 €",
    freeFeatures: ["2 проєкти", "1 клас", "1 папка", "Майже всі функції"],
    educators: "Педагоги",
    educatorsFeatures: ["1 педагог", "До 100 учнів", "Необмежена кількість проєктів, класів і папок", "Результати та статистика кожного учня"],
    teams: "Команди",
    teamsFeatures: ["5 педагогів", "Необмежена кількість учнів", "Керування ліцензіями", "Усе з тарифу «Педагоги»"],
    schools: "Школи",
    schoolsFeatures: ["20 педагогів", "Необмежена кількість учнів", "Керування ліцензіями", "Усе з тарифу «Педагоги»"],
    startFree: "Почати безкоштовно",
    tryFree: "Спробувати безкоштовно",
    subscribe: "Підписатися",
    note: "Для учнів Make It завжди безкоштовний.",
  },
  faq: {
    title: "Поширені запитання",
    sub: "Маєте сумніви? Ось відповіді на найпоширеніші запитання.",
    items: [
      {
        q: "Що таке вікторини?",
        a: "Вікторини — це цікавий спосіб оцінювати, вивчати чи повторювати будь-який предмет. Вони автоматично фіксують відповіді та бали кожного гравця, а ви можете переглядати відповіді, рейтинг і статистику.",
      },
      {
        q: "Що таке ігри?",
        a: "Ігри створено для маленьких дітей, навіть для тих, хто ще не вміє читати. Вони зосереджені на позитивному підкріпленні: за помилки не карають, а кількість спроб не обмежена. Щоб грати, дітям не потрібно вводити ім’я.",
      },
      {
        q: "Як переглянути результати ігор?",
        a: "Оскільки під час гри діти не вказують, хто вони, результати та статистику ігор можна побачити, лише якщо гру поширено через клас, а не за кодом чи посиланням.",
      },
      {
        q: "Які обмеження безкоштовної версії?",
        a: "Ви можете користуватися майже всіма функціями. Безкоштовна версія дає змогу створити 2 проєкти, 1 клас і 1 папку.",
      },
      { q: "Що дає підписка?", a: "Жодних обмежень: скільки завгодно проєктів, класів і папок, а також результати й статистика всіх ваших учнів." },
      {
        q: "Чи потрібен обліковий запис?",
        a: "Щоб грати — ні. З обліковим записом ви можете створювати й зберігати проєкти в хмарі, синхронізувати їх між пристроями та створювати класи, щоб ділитися проєктами з учнями.",
      },
      { q: "Чи мають учні платити?", a: "Ні. Для учнів Make It завжди безкоштовний." },
      {
        q: "Як поділитися проєктом?",
        a: "Відкрийте проєкт і перейдіть до розділу «Поділитися»: ви побачите його шестизначний код і посилання. Будь-хто, хто їх має, може грати на будь-якому пристрої. Також можна поділитися проєктом із класом — тоді учням не знадобиться жоден код.",
      },
      {
        q: "Що таке класи та як вони працюють?",
        a: "Класи допомагають керувати групами учнів. Створіть клас, дайте учням його код, щоб вони приєдналися, і діліться з ними проєктами — з усіма або лише з деякими. Ви бачитимете результати кожного учня.",
      },
      {
        q: "Чи можна користуватися Make It на будь-якому пристрої?",
        a: "Так. Створювати й грати можна на сайті, а застосунок доступний для iPad, iPhone, Android і Mac. Ваші проєкти синхронізуються між пристроями.",
      },
    ],
  },
  download: {
    title: "Make It на всіх ваших пристроях",
    sub: "Користуйтеся прямо тут, у браузері, або завантажте застосунок.",
    web: "Відкрити на сайті",
    available: "Доступно в",
  },
  final: { title: "Зробіть навчання цікавим", sub: "Створіть свій перший проєкт уже сьогодні. Це безкоштовно." },
  footer: {
    tagline: "Створюйте ігри, вікторини, завдання й історії та діліться ними будь-де.",
    product: "Продукт",
    help: "Довідка",
    legal: "Правова інформація",
    contact: "Контакти",
    terms: "Правила та умови",
    privacy: "Конфіденційність",
    cookies: "Політика щодо файлів cookie",
    follow: "Стежте за нами",
    support: "За підтримки ACCIÓ",
    demos: "Приклади",
  },
};

const id: LandingText = {
  nav: {
    features: "Fitur",
    activities: "Aktivitas",
    videos: "Video",
    plans: "Paket",
    faq: "Tanya Jawab",
    download: "Unduh",
    login: "Masuk",
    signup: "Daftar gratis",
    student: "Saya siswa",
    menu: "Menu",
    language: "Bahasa",
  },
  hero: {
    eyebrow: "Permainan dan kuis edukatif",
    title: "Buat permainan edukatif dan kuis dalam hitungan menit",
    sub: "Rancang proyek dengan lebih dari 20 jenis aktivitas, bagikan kepada siapa saja, dan lihat hasilnya seketika. Di browser, tablet, atau ponsel.",
    start: "Mulai gratis",
    demo: "Coba demo",
    codeTitle: "Punya kode?",
    codeText: "Ketik kode yang Anda terima, lalu mainkan",
    free: "Gratis untuk memulai · Tanpa kartu kredit",
  },
  stats: { downloads: "unduhan", users: "pengguna aktif", projects: "proyek dibuat", countries: "negara" },
  grid: {
    title: "Semua yang Anda butuhkan untuk belajar sambil bermain",
    sub: "Make It menyatukan kegiatan membuat, bermain, dan memantau dalam satu tempat.",
    items: [
      {
        title: "Buat dalam hitungan menit",
        text: "Templat, gambar, suara, alat menggambar, dan video YouTube siap digunakan. Tidak perlu pemrograman.",
      },
      { title: "Lebih dari 20 aktivitas", text: "Hubungkan, seret, cari pasangan, urutkan, ketik, warnai, tebalkan simbol, dan masih banyak lagi." },
      { title: "Bagikan dengan kode", text: "Kirim kode atau tautan dan mainkan di perangkat apa pun, tanpa perlu memasang apa-apa." },
      { title: "Hasil seketika", text: "Jawaban, peringkat, dan statistik untuk setiap kuis, serta perkembangan setiap siswa." },
      { title: "Kelas", text: "Siswa bergabung dengan kode. Bagikan proyek kepada seluruh kelas atau hanya kepada beberapa siswa." },
      { title: "Selalu tersinkron", text: "Proyek Anda tersimpan di cloud dan siap digunakan di web maupun di aplikasi." },
    ],
  },
  create: {
    kicker: "Buat",
    title: "Rancang proyek sesuai keinginan Anda",
    text: "Pilih aktivitas, tambahkan gambar, teks, dan suara Anda, dan proyek Anda pun siap. Semua yang Anda kerjakan tersimpan secara otomatis.",
    bullets: [
      "Pustaka dengan ribuan gambar dan suara",
      "Rekam suara Anda sendiri",
      "Menggambar langsung di halaman",
      "Tambahkan video YouTube",
      "Templat, font, dan musik latar",
    ],
  },
  play: {
    kicker: "Permainan",
    title: "Dibuat untuk si kecil",
    text: "Permainan menghargai usaha: kesalahan tidak dihukum, anak-anak bisa mencoba sebanyak yang mereka perlukan, dan mereka bahkan tidak perlu bisa membaca.",
    bullets: ["Penguatan positif di setiap halaman", "Instruksi dengan teks dan suara", "Tidak perlu nama atau akun untuk bermain"],
  },
  quiz: {
    kicker: "Kuis",
    title: "Kuis dengan skor dan peringkat",
    text: "Pertanyaan berbatas waktu, penilaian otomatis, dan peringkat langsung. Anda dapat melihat jawaban setiap pemain dan statistik setiap pertanyaan.",
    bullets: ["Pertanyaan, benar atau salah, ketik jawaban, matematika…", "Batas waktu per pertanyaan", "Peringkat dan statistik"],
  },
  share: {
    kicker: "Bagikan",
    title: "Satu kode, langsung main",
    text: "Setiap proyek memiliki kode enam digit dan sebuah tautan. Siswa membukanya di browser atau di aplikasi, tanpa perlu membuat akun.",
    code: "Kode",
    link: "Tautan",
    classroom: "Atau bagikan ke kelas",
  },
  classes: {
    kicker: "Kelas",
    title: "Pantau setiap siswa",
    text: "Buat kelas, berikan kodenya kepada siswa Anda, dan bagikan proyek kepada mereka, secara otomatis kepada semua atau hanya kepada beberapa siswa.",
    bullets: ["Siswa bergabung dengan kode", "Hasil setiap permainan dan setiap kuis", "Bagikan kepada seluruh kelas atau beberapa siswa"],
    mockTitle: "Hasil",
    mockCols: ["Siswa", "Skor", "Waktu"],
  },
  activities: {
    title: "Lebih dari 20 jenis aktivitas",
    sub: "Untuk permainan dan kuis, siap diisi dengan konten Anda sendiri.",
    games: "Permainan",
    quizzes: "Kuis",
  },
  videos: {
    title: "Kenali Make It",
    sub: "Lihat cara membuat proyek hanya dalam beberapa menit.",
    main: "Apa itu Make It?",
    tutorials: "Video tutorial",
    quiz: "Cara membuat kuis",
    game: "Cara membuat permainan",
    share: "Cara membagikan proyek",
    all: "Lihat semua tutorial",
  },
  demos: {
    title: "Coba sekarang",
    sub: "Mainkan beberapa demo kami, tanpa perlu mendaftar.",
    games: "Permainan",
    quizzes: "Kuis",
    play: "Main",
    pages: "halaman",
  },
  plans: {
    title: "Paket untuk setiap kelas",
    sub: "Mulai gratis dan beralih ke Premium saat Anda membutuhkan lebih banyak.",
    perYear: "/tahun",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 proyek", "1 kelas", "1 folder", "Hampir semua fitur"],
    educators: "Pendidik",
    educatorsFeatures: ["1 pendidik", "Hingga 100 siswa", "Proyek, kelas, dan folder tanpa batas", "Hasil dan statistik setiap siswa"],
    teams: "Tim",
    teamsFeatures: ["5 pendidik", "Siswa tanpa batas", "Pengelola lisensi", "Semua fitur paket Pendidik"],
    schools: "Sekolah",
    schoolsFeatures: ["20 pendidik", "Siswa tanpa batas", "Pengelola lisensi", "Semua fitur paket Pendidik"],
    startFree: "Mulai gratis",
    tryFree: "Coba gratis",
    subscribe: "Berlangganan",
    note: "Siswa selalu menggunakan Make It secara gratis.",
  },
  faq: {
    title: "Pertanyaan yang sering diajukan",
    sub: "Ada yang ingin ditanyakan? Berikut jawaban atas pertanyaan yang paling umum.",
    items: [
      {
        q: "Apa itu kuis?",
        a: "Kuis adalah cara yang menyenangkan untuk menilai, mempelajari, atau mengulas materi apa pun. Kuis mencatat jawaban dan skor setiap pemain secara otomatis, dan Anda dapat melihat jawaban, peringkat, serta statistiknya.",
      },
      {
        q: "Apa itu permainan?",
        a: "Permainan dirancang untuk anak-anak kecil, bahkan yang belum bisa membaca. Permainan mengutamakan penguatan positif: kesalahan tidak dihukum dan percobaannya tidak terbatas. Anak-anak tidak perlu memasukkan nama untuk bermain.",
      },
      {
        q: "Bagaimana cara melihat hasil permainan?",
        a: "Karena anak-anak tidak mengidentifikasi diri saat bermain, hasil dan statistik permainan hanya dapat dilihat jika permainan dibagikan melalui kelas, bukan melalui kode atau tautan.",
      },
      {
        q: "Apa batasan versi gratis?",
        a: "Anda dapat menggunakan hampir semua fitur. Versi gratis memungkinkan Anda membuat 2 proyek, 1 kelas, dan 1 folder.",
      },
      {
        q: "Apa yang saya dapatkan dengan berlangganan?",
        a: "Tanpa batas: proyek, kelas, dan folder sebanyak yang Anda inginkan, serta hasil dan statistik semua siswa Anda.",
      },
      {
        q: "Apakah saya perlu akun?",
        a: "Untuk bermain, tidak. Dengan akun, Anda dapat membuat dan menyimpan proyek di cloud, menyinkronkannya di berbagai perangkat, dan membuat kelas untuk membagikan proyek kepada siswa Anda.",
      },
      { q: "Apakah siswa harus membayar?", a: "Tidak. Siswa selalu menggunakan Make It secara gratis." },
      {
        q: "Bagaimana cara membagikan proyek?",
        a: "Buka proyek dan masuk ke Bagikan: Anda akan melihat kode enam digit dan tautannya. Siapa pun yang memilikinya dapat memainkannya di perangkat apa pun. Anda juga dapat membagikannya ke kelas sehingga siswa tidak memerlukan kode apa pun.",
      },
      {
        q: "Apa itu kelas dan bagaimana cara kerjanya?",
        a: "Kelas memungkinkan Anda mengelola kelompok siswa. Buat kelas, berikan kodenya kepada siswa agar mereka dapat bergabung, dan bagikan proyek kepada mereka: kepada semua atau hanya kepada beberapa siswa. Anda akan melihat hasil setiap siswa.",
      },
      {
        q: "Bisakah saya menggunakan Make It di perangkat apa pun?",
        a: "Ya. Anda dapat membuat dan bermain di web, dan aplikasinya tersedia untuk iPad, iPhone, Android, dan Mac. Proyek Anda selalu tersinkron.",
      },
    ],
  },
  download: {
    title: "Make It di semua perangkat Anda",
    sub: "Gunakan langsung di browser ini atau unduh aplikasinya.",
    web: "Buka di web",
    available: "Dapatkan di",
  },
  final: { title: "Jadikan belajar menyenangkan", sub: "Buat proyek pertama Anda hari ini. Gratis." },
  footer: {
    tagline: "Buat permainan, kuis, aktivitas, dan cerita, lalu bagikan di mana saja.",
    product: "Produk",
    help: "Bantuan",
    legal: "Hukum",
    contact: "Kontak",
    terms: "Syarat dan ketentuan",
    privacy: "Privasi",
    cookies: "Kebijakan cookie",
    follow: "Ikuti kami",
    support: "Dengan dukungan ACCIÓ",
    demos: "Demo",
  },
};

const vi: LandingText = {
  nav: {
    features: "Tính năng",
    activities: "Hoạt động",
    videos: "Video",
    plans: "Gói",
    faq: "Hỏi đáp",
    download: "Tải về",
    login: "Đăng nhập",
    signup: "Đăng ký miễn phí",
    student: "Tôi là học sinh",
    menu: "Menu",
    language: "Ngôn ngữ",
  },
  hero: {
    eyebrow: "Trò chơi và câu đố giáo dục",
    title: "Tạo trò chơi và câu đố học tập chỉ trong vài phút",
    sub: "Thiết kế dự án với hơn 20 loại hoạt động, chia sẻ với bất kỳ ai và xem kết quả ngay lập tức. Trên trình duyệt, máy tính bảng hoặc điện thoại.",
    start: "Bắt đầu miễn phí",
    demo: "Chơi thử bản mẫu",
    codeTitle: "Bạn có mã?",
    codeText: "Nhập mã bạn nhận được và chơi ngay",
    free: "Bắt đầu miễn phí · Không cần thẻ tín dụng",
  },
  stats: { downloads: "lượt tải", users: "người dùng hoạt động", projects: "dự án đã tạo", countries: "quốc gia" },
  grid: {
    title: "Mọi thứ bạn cần để vừa học vừa chơi",
    sub: "Make It gom việc tạo, chơi và theo dõi vào cùng một nơi.",
    items: [
      { title: "Tạo trong vài phút", text: "Mẫu giao diện, hình ảnh, âm thanh, hình vẽ và video YouTube luôn sẵn sàng. Không cần biết lập trình." },
      { title: "Hơn 20 hoạt động", text: "Nối, kéo thả, tìm cặp, sắp xếp, gõ chữ, tô màu, đồ theo ký hiệu và còn nhiều nữa." },
      { title: "Chia sẻ bằng mã", text: "Gửi mã hoặc liên kết và chơi trên mọi thiết bị, không cần cài đặt gì." },
      { title: "Kết quả tức thì", text: "Câu trả lời, bảng xếp hạng và thống kê cho từng câu đố, cùng tiến độ của từng học sinh." },
      { title: "Lớp học", text: "Học sinh tham gia bằng mã. Chia sẻ dự án với cả lớp hoặc chỉ với một vài em." },
      { title: "Luôn đồng bộ", text: "Dự án của bạn được lưu trên đám mây, luôn sẵn sàng trên web và trong ứng dụng." },
    ],
  },
  create: {
    kicker: "Tạo",
    title: "Thiết kế dự án theo cách của bạn",
    text: "Chọn một hoạt động, thêm hình ảnh, văn bản và âm thanh, thế là dự án đã sẵn sàng. Mọi thao tác đều được lưu tự động.",
    bullets: [
      "Thư viện với hàng nghìn hình ảnh và âm thanh",
      "Ghi âm giọng nói của chính bạn",
      "Vẽ trực tiếp lên trang",
      "Thêm video YouTube",
      "Mẫu giao diện, phông chữ và nhạc nền",
    ],
  },
  play: {
    kicker: "Trò chơi",
    title: "Dành cho các bé",
    text: "Trò chơi khen ngợi sự cố gắng: lỗi sai không bị phạt, trẻ có thể thử bao nhiêu lần tùy thích và thậm chí không cần biết đọc.",
    bullets: ["Khích lệ tích cực ở mỗi trang", "Hướng dẫn bằng chữ và âm thanh", "Không cần tên hay tài khoản để chơi"],
  },
  quiz: {
    kicker: "Câu đố",
    title: "Câu đố có điểm số và bảng xếp hạng",
    text: "Câu hỏi có giới hạn thời gian, chấm điểm tự động và bảng xếp hạng trực tiếp. Bạn xem được câu trả lời của từng người chơi và thống kê của từng câu hỏi.",
    bullets: ["Câu hỏi, đúng hay sai, nhập câu trả lời, toán…", "Giới hạn thời gian cho mỗi câu hỏi", "Bảng xếp hạng và thống kê"],
  },
  share: {
    kicker: "Chia sẻ",
    title: "Chỉ cần một mã là chơi",
    text: "Mỗi dự án có một mã gồm sáu chữ số và một liên kết. Học sinh mở trong trình duyệt hoặc trong ứng dụng, không cần tạo tài khoản.",
    code: "Mã",
    link: "Liên kết",
    classroom: "Hoặc chia sẻ với một lớp học",
  },
  classes: {
    kicker: "Lớp học",
    title: "Theo dõi từng học sinh",
    text: "Tạo lớp học, đưa mã lớp cho học sinh và chia sẻ dự án với các em: tự động với tất cả hoặc chỉ với một số em.",
    bullets: ["Học sinh tham gia bằng mã", "Kết quả của mọi trò chơi và mọi câu đố", "Chia sẻ với cả lớp hoặc với một vài em"],
    mockTitle: "Kết quả",
    mockCols: ["Học sinh", "Điểm", "Thời gian"],
  },
  activities: {
    title: "Hơn 20 loại hoạt động",
    sub: "Dành cho trò chơi và câu đố, sẵn sàng để bạn thêm nội dung của riêng mình.",
    games: "Trò chơi",
    quizzes: "Câu đố",
  },
  videos: {
    title: "Khám phá Make It",
    sub: "Xem cách tạo một dự án chỉ trong vài phút.",
    main: "Make It là gì?",
    tutorials: "Video hướng dẫn",
    quiz: "Cách tạo câu đố",
    game: "Cách tạo trò chơi",
    share: "Cách chia sẻ dự án",
    all: "Xem tất cả video hướng dẫn",
  },
  demos: {
    title: "Dùng thử ngay",
    sub: "Chơi thử một số dự án mẫu của chúng tôi, không cần đăng ký.",
    games: "Trò chơi",
    quizzes: "Câu đố",
    play: "Chơi",
    pages: "trang",
  },
  plans: {
    title: "Gói cho mọi lớp học",
    sub: "Bắt đầu miễn phí và nâng cấp lên Premium khi bạn cần thêm.",
    perYear: "/năm",
    free: "Miễn phí",
    freePrice: "0 €",
    freeFeatures: ["2 dự án", "1 lớp học", "1 thư mục", "Gần như mọi tính năng"],
    educators: "Giáo viên",
    educatorsFeatures: ["1 giáo viên", "Tối đa 100 học sinh", "Không giới hạn dự án, lớp học và thư mục", "Kết quả và thống kê của mọi học sinh"],
    teams: "Nhóm",
    teamsFeatures: ["5 giáo viên", "Không giới hạn học sinh", "Quản lý giấy phép", "Mọi thứ trong gói Giáo viên"],
    schools: "Trường học",
    schoolsFeatures: ["20 giáo viên", "Không giới hạn học sinh", "Quản lý giấy phép", "Mọi thứ trong gói Giáo viên"],
    startFree: "Bắt đầu miễn phí",
    tryFree: "Dùng thử miễn phí",
    subscribe: "Đăng ký",
    note: "Học sinh luôn dùng Make It miễn phí.",
  },
  faq: {
    title: "Câu hỏi thường gặp",
    sub: "Bạn còn thắc mắc? Đây là những câu trả lời phổ biến nhất.",
    items: [
      {
        q: "Câu đố là gì?",
        a: "Câu đố là cách thú vị để đánh giá, học hoặc ôn tập bất kỳ môn học nào. Câu đố tự động ghi lại câu trả lời và điểm của từng người chơi, và bạn có thể xem câu trả lời, bảng xếp hạng và thống kê.",
      },
      {
        q: "Trò chơi là gì?",
        a: "Trò chơi được thiết kế cho trẻ nhỏ, kể cả những bé chưa biết đọc. Trò chơi tập trung vào khích lệ tích cực: lỗi sai không bị phạt và được thử không giới hạn số lần. Trẻ không cần nhập tên để chơi.",
      },
      {
        q: "Làm sao để xem kết quả của trò chơi?",
        a: "Vì trẻ không cần cho biết mình là ai khi chơi, kết quả và thống kê của trò chơi chỉ xem được khi trò chơi được chia sẻ qua lớp học, không phải qua mã hay liên kết.",
      },
      {
        q: "Bản miễn phí có những giới hạn gì?",
        a: "Bạn có thể dùng gần như mọi tính năng. Bản miễn phí cho phép bạn tạo 2 dự án, 1 lớp học và 1 thư mục.",
      },
      {
        q: "Đăng ký thì tôi được gì?",
        a: "Không giới hạn: bao nhiêu dự án, lớp học và thư mục tùy thích, cùng kết quả và thống kê của tất cả học sinh.",
      },
      {
        q: "Tôi có cần tài khoản không?",
        a: "Để chơi thì không. Với tài khoản, bạn có thể tạo và lưu dự án trên đám mây, đồng bộ giữa các thiết bị và tạo lớp học để chia sẻ dự án với học sinh.",
      },
      { q: "Học sinh có phải trả phí không?", a: "Không. Học sinh luôn dùng Make It miễn phí." },
      {
        q: "Làm sao để chia sẻ dự án?",
        a: "Mở dự án và vào mục “Chia sẻ”: bạn sẽ thấy mã sáu chữ số và liên kết của dự án. Ai có mã hoặc liên kết đều chơi được trên mọi thiết bị. Bạn cũng có thể chia sẻ dự án với một lớp học để học sinh không cần nhập mã.",
      },
      {
        q: "Lớp học là gì và hoạt động thế nào?",
        a: "Lớp học giúp bạn quản lý các nhóm học sinh. Tạo lớp học, đưa mã lớp cho học sinh để các em tham gia, rồi chia sẻ dự án với các em: với tất cả hoặc chỉ với một số em. Bạn sẽ xem được kết quả của từng học sinh.",
      },
      {
        q: "Tôi có thể dùng Make It trên mọi thiết bị không?",
        a: "Có. Bạn có thể tạo và chơi trên web, và ứng dụng có sẵn cho iPad, iPhone, Android và Mac. Các dự án của bạn luôn được đồng bộ.",
      },
    ],
  },
  download: {
    title: "Make It trên mọi thiết bị của bạn",
    sub: "Dùng ngay tại đây trên trình duyệt hoặc tải ứng dụng về.",
    web: "Mở trên web",
    available: "Tải về trên",
  },
  final: { title: "Biến việc học thành niềm vui", sub: "Tạo dự án đầu tiên ngay hôm nay. Hoàn toàn miễn phí." },
  footer: {
    tagline: "Tạo trò chơi, câu đố, hoạt động và câu chuyện, rồi chia sẻ ở bất cứ đâu.",
    product: "Sản phẩm",
    help: "Trợ giúp",
    legal: "Pháp lý",
    contact: "Liên hệ",
    terms: "Điều khoản và điều kiện",
    privacy: "Quyền riêng tư",
    cookies: "Chính sách cookie",
    follow: "Theo dõi chúng tôi",
    support: "Với sự hỗ trợ của ACCIÓ",
    demos: "Dự án mẫu",
  },
};

const cs: LandingText = {
  nav: {
    features: "Funkce",
    activities: "Aktivity",
    videos: "Videa",
    plans: "Tarify",
    faq: "Časté dotazy",
    download: "Stáhnout",
    login: "Přihlásit se",
    signup: "Registrace zdarma",
    student: "Jsem žák",
    menu: "Nabídka",
    language: "Jazyk",
  },
  hero: {
    eyebrow: "Vzdělávací hry a kvízy",
    title: "Vytvářejte výukové hry a kvízy během pár minut",
    sub: "Navrhujte projekty s více než 20 typy aktivit, sdílejte je s kýmkoli a výsledky uvidíte okamžitě. V prohlížeči, na tabletu i na telefonu.",
    start: "Začít zdarma",
    demo: "Vyzkoušet ukázku",
    codeTitle: "Máte kód?",
    codeText: "Zadejte kód, který jste dostali, a hrajte",
    free: "Začněte zdarma · Bez platební karty",
  },
  stats: { downloads: "stažení", users: "aktivních uživatelů", projects: "vytvořených projektů", countries: "zemí" },
  grid: {
    title: "Vše, co potřebujete k učení hrou",
    sub: "Make It spojuje tvorbu, hraní i sledování pokroku na jednom místě.",
    items: [
      { title: "Tvorba během pár minut", text: "Šablony, obrázky, zvuky, kresby a videa z YouTube po ruce. Bez programování." },
      { title: "Více než 20 aktivit", text: "Spojování, přetahování, pexeso, řazení, psaní, malování, obtahování symbolů a mnohem víc." },
      { title: "Sdílení pomocí kódu", text: "Pošlete kód nebo odkaz a hrajte na jakémkoli zařízení, bez instalace." },
      { title: "Okamžité výsledky", text: "Odpovědi, žebříčky a statistiky každého kvízu a pokrok každého žáka." },
      { title: "Třídy", text: "Žáci se připojí pomocí kódu. Sdílejte projekty s celou třídou, nebo jen s některými žáky." },
      { title: "Vždy synchronizováno", text: "Vaše projekty se ukládají do cloudu a jsou připravené na webu i v aplikaci." },
    ],
  },
  create: {
    kicker: "Tvorba",
    title: "Navrhněte si projekt po svém",
    text: "Vyberte aktivitu, přidejte obrázky, texty a zvuky a projekt je hotový. Vše, co uděláte, se automaticky ukládá.",
    bullets: [
      "Knihovny s tisíci obrázků a zvuků",
      "Nahrajte vlastní hlas",
      "Kreslete přímo na stránku",
      "Přidávejte videa z YouTube",
      "Šablony, písma a hudba na pozadí",
    ],
  },
  play: {
    kicker: "Hry",
    title: "Pro ty nejmenší",
    text: "Hry odměňují snahu: chyby se netrestají, děti mohou zkoušet, kolikrát potřebují, a nemusí ani umět číst.",
    bullets: ["Pozitivní motivace na každé stránce", "Pokyny s textem a zvukem", "Ke hraní není potřeba jméno ani účet"],
  },
  quiz: {
    kicker: "Kvízy",
    title: "Kvízy se skóre a žebříčkem",
    text: "Otázky s časovým limitem, automatické hodnocení a žebříček v reálném čase. Uvidíte odpovědi každého hráče i statistiky každé otázky.",
    bullets: ["Otázka, pravda, nebo nepravda, napsání odpovědi, matematika…", "Časový limit na otázku", "Žebříček a statistiky"],
  },
  share: {
    kicker: "Sdílení",
    title: "Jeden kód a hrajte",
    text: "Každý projekt má šestimístný kód a odkaz. Žáci ho otevřou v prohlížeči nebo v aplikaci, aniž by si museli zakládat účet.",
    code: "Kód",
    link: "Odkaz",
    classroom: "Nebo ho sdílejte se třídou",
  },
  classes: {
    kicker: "Třídy",
    title: "Sledujte každého žáka",
    text: "Vytvořte třídu, dejte žákům její kód a sdílejte s nimi projekty – automaticky se všemi, nebo jen s některými.",
    bullets: ["Žáci se připojí pomocí kódu", "Výsledky každé hry a každého kvízu", "Sdílení s celou třídou, nebo jen s některými"],
    mockTitle: "Výsledky",
    mockCols: ["Žák", "Skóre", "Čas"],
  },
  activities: { title: "Více než 20 typů aktivit", sub: "Pro hry i kvízy, připravené k naplnění vlastním obsahem.", games: "Hry", quizzes: "Kvízy" },
  videos: {
    title: "Objevte Make It",
    sub: "Podívejte se, jak se projekt vytvoří za pár minut.",
    main: "Co je Make It?",
    tutorials: "Videonávody",
    quiz: "Jak vytvořit kvíz",
    game: "Jak vytvořit hru",
    share: "Jak sdílet projekt",
    all: "Zobrazit všechny návody",
  },
  demos: {
    title: "Vyzkoušejte si to hned",
    sub: "Zahrajte si některou z našich ukázek, bez registrace.",
    games: "Hry",
    quizzes: "Kvízy",
    play: "Hrát",
    pages: "str.",
  },
  plans: {
    title: "Tarify pro každou třídu",
    sub: "Začněte zdarma a na Premium přejděte, až budete potřebovat víc.",
    perYear: "/rok",
    free: "Zdarma",
    freePrice: "0 €",
    freeFeatures: ["2 projekty", "1 třída", "1 složka", "Téměř všechny funkce"],
    educators: "Pedagogové",
    educatorsFeatures: ["1 pedagog", "Až 100 žáků", "Neomezeně projektů, tříd a složek", "Výsledky a statistiky každého žáka"],
    teams: "Týmy",
    teamsFeatures: ["5 pedagogů", "Neomezený počet žáků", "Správce licence", "Vše z tarifu Pedagogové"],
    schools: "Školy",
    schoolsFeatures: ["20 pedagogů", "Neomezený počet žáků", "Správce licence", "Vše z tarifu Pedagogové"],
    startFree: "Začít zdarma",
    tryFree: "Vyzkoušet zdarma",
    subscribe: "Předplatit",
    note: "Žáci používají Make It vždy zdarma.",
  },
  faq: {
    title: "Časté dotazy",
    sub: "Máte nějaké otázky? Tady jsou nejčastější odpovědi.",
    items: [
      {
        q: "Co jsou kvízy?",
        a: "Kvízy jsou zábavný způsob, jak ověřovat znalosti, učit se nebo opakovat jakékoli téma. Automaticky zaznamenávají odpovědi a skóre každého hráče a vy uvidíte odpovědi, žebříček i statistiky.",
      },
      {
        q: "Co jsou hry?",
        a: "Hry jsou určené pro malé děti, i pro ty, které ještě neumějí číst. Zaměřují se na pozitivní motivaci: chyby se netrestají a počet pokusů je neomezený. Děti ke hraní nezadávají jméno.",
      },
      {
        q: "Jak uvidím výsledky her?",
        a: "Protože děti při hraní nezadávají své jméno, výsledky a statistiky her uvidíte jen tehdy, když hru sdílíte přes třídu, nikoli pomocí kódu nebo odkazu.",
      },
      {
        q: "Jaká jsou omezení bezplatné verze?",
        a: "Můžete používat téměř všechny funkce. V bezplatné verzi můžete vytvořit 2 projekty, 1 třídu a 1 složku.",
      },
      { q: "Co získám předplatným?", a: "Žádná omezení: tolik projektů, tříd a složek, kolik chcete, a výsledky a statistiky všech vašich žáků." },
      {
        q: "Potřebuji účet?",
        a: "Ke hraní ne. S účtem můžete vytvářet projekty a ukládat je do cloudu, synchronizovat je mezi zařízeními a zakládat třídy, ve kterých projekty sdílíte se svými žáky.",
      },
      { q: "Musí žáci platit?", a: "Ne. Žáci používají Make It vždy zdarma." },
      {
        q: "Jak mohu sdílet projekt?",
        a: "Otevřete projekt a přejděte na „Sdílet“: uvidíte jeho šestimístný kód a odkaz. Kdo je má, může projekt hrát na jakémkoli zařízení. Můžete ho také sdílet se třídou, takže žáci nepotřebují žádný kód.",
      },
      {
        q: "Co jsou třídy a jak fungují?",
        a: "Třídy vám umožňují spravovat skupiny žáků. Vytvořte třídu, dejte žákům její kód, aby se mohli připojit, a sdílejte s nimi projekty: se všemi, nebo jen s některými. Uvidíte výsledky každého žáka.",
      },
      {
        q: "Mohu Make It používat na jakémkoli zařízení?",
        a: "Ano. Tvořit a hrát můžete na webu a aplikace je k dispozici pro iPad, iPhone, Android a Mac. Vaše projekty zůstávají synchronizované.",
      },
    ],
  },
  download: {
    title: "Make It na všech vašich zařízeních",
    sub: "Používejte ho přímo tady v prohlížeči, nebo si stáhněte aplikaci.",
    web: "Otevřít na webu",
    available: "K dispozici na",
  },
  final: { title: "Učení, které baví", sub: "Vytvořte svůj první projekt ještě dnes. Je to zdarma." },
  footer: {
    tagline: "Vytvářejte hry, kvízy, aktivity a příběhy a sdílejte je kdekoli.",
    product: "Produkt",
    help: "Nápověda",
    legal: "Právní informace",
    contact: "Kontakt",
    terms: "Smluvní podmínky",
    privacy: "Ochrana soukromí",
    cookies: "Zásady používání cookies",
    follow: "Sledujte nás",
    support: "S podporou ACCIÓ",
    demos: "Ukázky",
  },
};

const sk: LandingText = {
  nav: {
    features: "Funkcie",
    activities: "Aktivity",
    videos: "Videá",
    plans: "Plány",
    faq: "Časté otázky",
    download: "Stiahnuť",
    login: "Prihlásiť sa",
    signup: "Registrácia zadarmo",
    student: "Som žiak",
    menu: "Ponuka",
    language: "Jazyk",
  },
  hero: {
    eyebrow: "Vzdelávacie hry a kvízy",
    title: "Vytvárajte vzdelávacie hry a kvízy za pár minút",
    sub: "Navrhujte projekty s viac ako 20 typmi aktivít, zdieľajte ich s kýmkoľvek a výsledky uvidíte okamžite. V prehliadači, na tablete aj v mobile.",
    start: "Začať zadarmo",
    demo: "Vyskúšať ukážku",
    codeTitle: "Máte kód?",
    codeText: "Zadajte kód, ktorý ste dostali, a hrajte",
    free: "Začnite zadarmo · Bez platobnej karty",
  },
  stats: { downloads: "stiahnutí", users: "aktívnych používateľov", projects: "vytvorených projektov", countries: "krajín" },
  grid: {
    title: "Všetko, čo potrebujete na učenie hrou",
    sub: "Make It spája tvorbu, hranie a sledovanie pokroku na jednom mieste.",
    items: [
      { title: "Tvorba za pár minút", text: "Šablóny, obrázky, zvuky, kresby a videá z YouTube máte po ruke. Bez programovania." },
      {
        title: "Viac ako 20 aktivít",
        text: "Spájanie, presúvanie, hľadanie dvojíc, zoraďovanie, písanie, maľovanie, obťahovanie symbolov a oveľa viac.",
      },
      { title: "Zdieľanie cez kód", text: "Pošlite kód alebo odkaz a hrajte na akomkoľvek zariadení bez inštalácie." },
      { title: "Okamžité výsledky", text: "Odpovede, rebríčky a štatistiky každého kvízu a pokrok každého žiaka." },
      { title: "Triedy", text: "Žiaci sa pripoja pomocou kódu. Zdieľajte projekty s celou triedou alebo len s niektorými žiakmi." },
      { title: "Vždy synchronizované", text: "Vaše projekty sú uložené v cloude a pripravené na webe aj v aplikácii." },
    ],
  },
  create: {
    kicker: "Tvorba",
    title: "Navrhnite si projekt po svojom",
    text: "Vyberte aktivitu, pridajte obrázky, texty a zvuky a projekt je hotový. Všetko, čo urobíte, sa automaticky ukladá.",
    bullets: [
      "Knižnice s tisíckami obrázkov a zvukov",
      "Nahrávanie vlastného hlasu",
      "Kreslenie priamo na stranu",
      "Videá z YouTube",
      "Šablóny, písma a hudba v pozadí",
    ],
  },
  play: {
    kicker: "Hry",
    title: "Vytvorené pre najmenších",
    text: "Hry odmeňujú snahu: chyby sa netrestajú, deti môžu skúšať toľkokrát, koľko potrebujú, a nemusia ani vedieť čítať.",
    bullets: ["Pozitívna motivácia na každej strane", "Pokyny s textom a zvukom", "Na hranie netreba meno ani účet"],
  },
  quiz: {
    kicker: "Kvízy",
    title: "Kvízy so skóre a rebríčkami",
    text: "Otázky s časovým limitom, automatické bodovanie a rebríček v reálnom čase. Vidíte odpovede každého hráča a štatistiky každej otázky.",
    bullets: ["Otázka, pravda alebo nepravda, napísanie odpovede, matematika…", "Časový limit na otázku", "Rebríček a štatistiky"],
  },
  share: {
    kicker: "Zdieľanie",
    title: "Jeden kód a hrá sa",
    text: "Každý projekt má šesťmiestny kód a odkaz. Žiaci ho otvoria v prehliadači alebo v aplikácii bez vytvárania účtu.",
    code: "Kód",
    link: "Odkaz",
    classroom: "Alebo ho zdieľajte s triedou",
  },
  classes: {
    kicker: "Triedy",
    title: "Sledujte každého žiaka",
    text: "Vytvorte triedu, dajte žiakom jej kód a zdieľajte s nimi projekty – automaticky so všetkými alebo len s niektorými.",
    bullets: ["Žiaci sa pripoja pomocou kódu", "Výsledky každej hry a každého kvízu", "Zdieľanie s celou triedou alebo len s niekoľkými žiakmi"],
    mockTitle: "Výsledky",
    mockCols: ["Žiak", "Skóre", "Čas"],
  },
  activities: { title: "Viac ako 20 typov aktivít", sub: "Pre hry aj kvízy, pripravené na váš vlastný obsah.", games: "Hry", quizzes: "Kvízy" },
  videos: {
    title: "Objavte Make It",
    sub: "Pozrite sa, ako za pár minút vznikne projekt.",
    main: "Čo je Make It?",
    tutorials: "Videonávody",
    quiz: "Ako vytvoriť kvíz",
    game: "Ako vytvoriť hru",
    share: "Ako zdieľať projekt",
    all: "Pozrieť všetky návody",
  },
  demos: {
    title: "Vyskúšajte to hneď",
    sub: "Zahrajte si niektoré z našich ukážok, bez registrácie.",
    games: "Hry",
    quizzes: "Kvízy",
    play: "Hrať",
    pages: "str.",
  },
  plans: {
    title: "Plány pre každú triedu",
    sub: "Začnite zadarmo a na Premium prejdite, keď budete potrebovať viac.",
    perYear: "/rok",
    free: "Zadarmo",
    freePrice: "0 €",
    freeFeatures: ["2 projekty", "1 trieda", "1 priečinok", "Takmer všetky funkcie"],
    educators: "Pedagógovia",
    educatorsFeatures: ["1 pedagóg", "Až 100 žiakov", "Neobmedzený počet projektov, tried a priečinkov", "Výsledky a štatistiky každého žiaka"],
    teams: "Tímy",
    teamsFeatures: ["5 pedagógov", "Neobmedzený počet žiakov", "Správca licencie", "Všetko z plánu Pedagógovia"],
    schools: "Školy",
    schoolsFeatures: ["20 pedagógov", "Neobmedzený počet žiakov", "Správca licencie", "Všetko z plánu Pedagógovia"],
    startFree: "Začať zadarmo",
    tryFree: "Vyskúšať zadarmo",
    subscribe: "Predplatiť",
    note: "Žiaci používajú Make It vždy zadarmo.",
  },
  faq: {
    title: "Často kladené otázky",
    sub: "Máte otázky? Tu sú najčastejšie odpovede.",
    items: [
      {
        q: "Čo sú kvízy?",
        a: "Kvízy sú zábavný spôsob, ako hodnotiť, učiť sa alebo opakovať akýkoľvek predmet. Automaticky zaznamenávajú odpovede a skóre každého hráča a vy si môžete pozrieť odpovede, rebríček aj štatistiky.",
      },
      {
        q: "Čo sú hry?",
        a: "Hry sú určené pre malé deti, aj pre tie, ktoré ešte nevedia čítať. Stavajú na pozitívnej motivácii: chyby sa netrestajú a počet pokusov je neobmedzený. Deti na hranie nezadávajú meno.",
      },
      {
        q: "Ako si môžem pozrieť výsledky hier?",
        a: "Keďže sa deti pri hraní neidentifikujú, výsledky a štatistiky hier vidno len vtedy, keď je hra zdieľaná cez triedu, nie cez kód alebo odkaz.",
      },
      {
        q: "Aké sú obmedzenia bezplatnej verzie?",
        a: "Môžete používať takmer všetky funkcie. Bezplatná verzia vám umožňuje vytvoriť 2 projekty, 1 triedu a 1 priečinok.",
      },
      {
        q: "Čo získam predplatným?",
        a: "Žiadne obmedzenia: toľko projektov, tried a priečinkov, koľko chcete, a výsledky a štatistiky všetkých vašich žiakov.",
      },
      {
        q: "Potrebujem účet?",
        a: "Na hranie nie. S účtom môžete vytvárať projekty a ukladať ich v cloude, synchronizovať ich medzi zariadeniami a vytvárať triedy na zdieľanie projektov so žiakmi.",
      },
      { q: "Musia žiaci platiť?", a: "Nie. Žiaci používajú Make It vždy zadarmo." },
      {
        q: "Ako zdieľam projekt?",
        a: "Otvorte projekt a prejdite na „Zdieľať“: uvidíte jeho šesťmiestny kód a odkaz. Kto ich má, môže projekt hrať na akomkoľvek zariadení. Môžete ho zdieľať aj s triedou, aby žiaci nepotrebovali žiadny kód.",
      },
      {
        q: "Čo sú triedy a ako fungujú?",
        a: "Triedy vám umožňujú spravovať skupiny žiakov. Vytvorte triedu, dajte žiakom jej kód, aby sa mohli pripojiť, a zdieľajte s nimi projekty: so všetkými alebo len s niektorými. Uvidíte výsledky každého žiaka.",
      },
      {
        q: "Môžem Make It používať na akomkoľvek zariadení?",
        a: "Áno. Tvoriť a hrať môžete na webe a aplikácia je dostupná pre iPad, iPhone, Android a Mac. Vaše projekty sa synchronizujú.",
      },
    ],
  },
  download: {
    title: "Make It na všetkých vašich zariadeniach",
    sub: "Používajte ho priamo tu v prehliadači alebo si stiahnite aplikáciu.",
    web: "Otvoriť na webe",
    available: "Stiahnite si z",
  },
  final: { title: "Učenie, ktoré baví", sub: "Vytvorte svoj prvý projekt ešte dnes. Je to zadarmo." },
  footer: {
    tagline: "Vytvárajte hry, kvízy, aktivity a príbehy a zdieľajte ich kdekoľvek.",
    product: "Produkt",
    help: "Pomoc",
    legal: "Právne informácie",
    contact: "Kontakt",
    terms: "Zmluvné podmienky",
    privacy: "Ochrana súkromia",
    cookies: "Zásady používania súborov cookie",
    follow: "Sledujte nás",
    support: "S podporou agentúry ACCIÓ",
    demos: "Ukážky",
  },
};

const hu: LandingText = {
  nav: {
    features: "Funkciók",
    activities: "Feladatok",
    videos: "Videók",
    plans: "Csomagok",
    faq: "GYIK",
    download: "Letöltés",
    login: "Bejelentkezés",
    signup: "Ingyenes regisztráció",
    student: "Diák vagyok",
    menu: "Menü",
    language: "Nyelv",
  },
  hero: {
    eyebrow: "Oktatási játékok és kvízek",
    title: "Készíts oktatójátékokat és kvízeket percek alatt",
    sub: "Tervezz projekteket több mint 20 feladattípussal, oszd meg őket bárkivel, és lásd azonnal az eredményeket. Böngészőben, tableten vagy telefonon.",
    start: "Kezdd el ingyen",
    demo: "Próbálj ki egy demót",
    codeTitle: "Van kódod?",
    codeText: "Írd be a kapott kódot, és játssz",
    free: "Ingyenes kezdés · Nem kell bankkártya",
  },
  stats: { downloads: "letöltés", users: "aktív felhasználó", projects: "létrehozott projekt", countries: "ország" },
  grid: {
    title: "Minden, ami a játékos tanuláshoz kell",
    sub: "A Make It egy helyen egyesíti a készítést, a játékot és a nyomon követést.",
    items: [
      { title: "Készíts percek alatt", text: "Sablonok, képek, hangok, rajzok és YouTube-videók kéznél. Nincs szükség programozásra." },
      { title: "Több mint 20 feladattípus", text: "Összekötés, húzás, párkereső, sorba rendezés, gépelés, festés, jelek átrajzolása és még sok más." },
      { title: "Megosztás kóddal", text: "Küldj egy kódot vagy linket, és már játszhatnak is bármilyen eszközön, telepítés nélkül." },
      { title: "Azonnali eredmények", text: "Válaszok, ranglisták és statisztikák minden kvízhez, és minden diák előrehaladása." },
      { title: "Osztályok", text: "A diákok kóddal csatlakoznak. Oszd meg a projekteket az egész osztállyal vagy csak néhány diákkal." },
      { title: "Mindig szinkronban", text: "A projektjeid a felhőbe mentődnek, és elérhetők a weben és az alkalmazásban is." },
    ],
  },
  create: {
    kicker: "Készítés",
    title: "Tervezd meg a projektedet a saját elképzelésed szerint",
    text: "Válassz egy feladatot, add hozzá a képeidet, szövegeidet és hangjaidat, és kész is a projekted. Minden, amit csinálsz, automatikusan mentődik.",
    bullets: [
      "Több ezer képet és hangot tartalmazó könyvtárak",
      "Saját hang felvétele",
      "Rajzolás közvetlenül az oldalon",
      "YouTube-videók beillesztése",
      "Sablonok, betűtípusok és háttérzene",
    ],
  },
  play: {
    kicker: "Játékok",
    title: "A legkisebbeknek készült",
    text: "A játékok az erőfeszítést jutalmazzák: a hibákért nem jár büntetés, a gyerekek annyiszor próbálkozhatnak, ahányszor csak kell, és még olvasniuk sem kell.",
    bullets: ["Pozitív megerősítés minden oldalon", "Utasítások szöveggel és hanggal", "A játékhoz nem kell név vagy fiók"],
  },
  quiz: {
    kicker: "Kvízek",
    title: "Kvízek pontozással és ranglistával",
    text: "Időre menő kérdések, automatikus pontozás és élő ranglista. Látod minden játékos válaszait és minden kérdés statisztikáját.",
    bullets: ["Kérdés, igaz vagy hamis, válasz beírása, matek…", "Időkorlát kérdésenként", "Ranglista és statisztika"],
  },
  share: {
    kicker: "Megosztás",
    title: "Egy kód, és már játszhatsz is",
    text: "Minden projektnek van egy hatjegyű kódja és egy linkje. A diákok böngészőben vagy az alkalmazásban nyitják meg, fiók létrehozása nélkül.",
    code: "Kód",
    link: "Link",
    classroom: "Vagy oszd meg egy osztállyal",
  },
  classes: {
    kicker: "Osztályok",
    title: "Kövesd nyomon minden diák haladását",
    text: "Hozz létre egy osztályt, add meg a diákjaidnak a kódját, és oszd meg velük a projekteket: automatikusan mindenkivel, vagy csak néhányukkal.",
    bullets: ["A diákok kóddal csatlakoznak", "Minden játék és minden kvíz eredménye", "Megosztás az egész osztállyal vagy csak néhány diákkal"],
    mockTitle: "Eredmények",
    mockCols: ["Diák", "Pontszám", "Idő"],
  },
  activities: {
    title: "Több mint 20 feladattípus",
    sub: "Játékokhoz és kvízekhez, készen arra, hogy megtöltsd őket saját tartalommal.",
    games: "Játékok",
    quizzes: "Kvízek",
  },
  videos: {
    title: "Fedezd fel a Make It világát",
    sub: "Nézd meg, hogyan készül el egy projekt néhány perc alatt.",
    main: "Mi az a Make It?",
    tutorials: "Oktatóvideók",
    quiz: "Hogyan készíts kvízt?",
    game: "Hogyan készíts játékot?",
    share: "Hogyan oszd meg a projektet?",
    all: "Az összes oktatóvideó",
  },
  demos: {
    title: "Próbáld ki most",
    sub: "Játssz néhány demónkkal, regisztráció nélkül.",
    games: "Játékok",
    quizzes: "Kvízek",
    play: "Játssz",
    pages: "oldal",
  },
  plans: {
    title: "Csomagok minden osztálynak",
    sub: "Kezdd ingyen, és válts a Premium csomagra, ha többre van szükséged.",
    perYear: "/év",
    free: "Ingyenes",
    freePrice: "0 €",
    freeFeatures: ["2 projekt", "1 osztály", "1 mappa", "Szinte minden funkció"],
    educators: "Pedagógus",
    educatorsFeatures: ["1 pedagógus", "Legfeljebb 100 diák", "Korlátlan projekt, osztály és mappa", "Minden diák eredményei és statisztikái"],
    teams: "Csapat",
    teamsFeatures: ["5 pedagógus", "Korlátlan számú diák", "Licenckezelés", "Minden, ami a Pedagógus csomagban"],
    schools: "Iskola",
    schoolsFeatures: ["20 pedagógus", "Korlátlan számú diák", "Licenckezelés", "Minden, ami a Pedagógus csomagban"],
    startFree: "Kezdd el ingyen",
    tryFree: "Próbáld ki ingyen",
    subscribe: "Előfizetés",
    note: "A diákoknak a Make It mindig ingyenes.",
  },
  faq: {
    title: "Gyakori kérdések",
    sub: "Kérdésed van? Itt találod a leggyakoribb válaszokat.",
    items: [
      {
        q: "Mik azok a kvízek?",
        a: "A kvízekkel szórakoztató módon mérheted fel, tanulhatod vagy ismételheted át bármelyik tantárgy anyagát. Automatikusan rögzítik minden játékos válaszait és pontszámát, te pedig megnézheted a válaszokat, a ranglistát és a statisztikákat.",
      },
      {
        q: "Mik azok a játékok?",
        a: "A játékokat kisgyerekeknek terveztük, még azoknak is, akik nem tudnak olvasni. A pozitív megerősítésre épülnek: a hibákért nem jár büntetés, és korlátlan a próbálkozások száma. A gyerekeknek nem kell nevet megadniuk a játékhoz.",
      },
      {
        q: "Hogyan láthatom a játékok eredményeit?",
        a: "Mivel a gyerekek játék közben nem azonosítják magukat, a játékok eredményei és statisztikái csak akkor láthatók, ha a játékot osztályon keresztül osztod meg, nem kóddal vagy linkkel.",
      },
      {
        q: "Mik az ingyenes verzió korlátai?",
        a: "Szinte minden funkciót használhatsz. Az ingyenes verzióval 2 projektet, 1 osztályt és 1 mappát hozhatsz létre.",
      },
      {
        q: "Mit kapok az előfizetéssel?",
        a: "Nincsenek korlátok: annyi projektet, osztályt és mappát hozhatsz létre, amennyit csak szeretnél, és láthatod az összes diákod eredményeit és statisztikáit.",
      },
      {
        q: "Kell hozzá fiók?",
        a: "A játékhoz nem. Fiókkal létrehozhatod és a felhőbe mentheted a projektjeidet, szinkronizálhatod őket az eszközeid között, és osztályokat hozhatsz létre, hogy megoszd a projekteket a diákjaiddal.",
      },
      { q: "Fizetniük kell a diákoknak?", a: "Nem. A diákoknak a Make It mindig ingyenes." },
      {
        q: "Hogyan oszthatok meg egy projektet?",
        a: "Nyisd meg a projektet, és lépj a „Megosztás” fülre: ott látod a hatjegyű kódját és a linkjét. Aki megkapja ezeket, bármilyen eszközön játszhat vele. Egy osztállyal is megoszthatod, így a diákoknak semmilyen kódra nincs szükségük.",
      },
      {
        q: "Mik azok az osztályok, és hogyan működnek?",
        a: "Az osztályokkal diákcsoportokat kezelhetsz. Hozz létre egy osztályt, add meg a diákjaidnak a kódját, hogy csatlakozhassanak, és oszd meg velük a projekteket: mindenkivel vagy csak néhányukkal. Minden diák eredményét látni fogod.",
      },
      {
        q: "Bármilyen eszközön használható a Make It?",
        a: "Igen. A weben is készíthetsz projekteket és játszhatsz, az alkalmazás pedig elérhető iPad, iPhone, Android és Mac eszközökre. A projektjeid szinkronban maradnak.",
      },
    ],
  },
  download: {
    title: "A Make It minden eszközödön",
    sub: "Használd itt, a böngészőben, vagy töltsd le az alkalmazást.",
    web: "Megnyitás a weben",
    available: "Elérhető itt:",
  },
  final: { title: "Tedd szórakoztatóvá a tanulást", sub: "Készítsd el az első projektedet még ma. Ingyenes." },
  footer: {
    tagline: "Készíts játékokat, kvízeket, feladatokat és történeteket, és oszd meg őket bárhol.",
    product: "Termék",
    help: "Súgó",
    legal: "Jogi információk",
    contact: "Kapcsolat",
    terms: "Általános szerződési feltételek",
    privacy: "Adatvédelem",
    cookies: "Sütiszabályzat",
    follow: "Kövess minket",
    support: "Az ACCIÓ támogatásával",
    demos: "Demók",
  },
};

const hr: LandingText = {
  nav: {
    features: "Značajke",
    activities: "Aktivnosti",
    videos: "Videozapisi",
    plans: "Planovi",
    faq: "Česta pitanja",
    download: "Preuzmi",
    login: "Prijava",
    signup: "Registriraj se besplatno",
    student: "Učenik sam",
    menu: "Izbornik",
    language: "Jezik",
  },
  hero: {
    eyebrow: "Obrazovne igre i kvizovi",
    title: "Izradi igre i kvizove za učenje u nekoliko minuta",
    sub: "Osmisli projekte s više od 20 vrsta aktivnosti, podijeli ih s kim god želiš i odmah pogledaj rezultate. U pregledniku, na tabletu ili na mobitelu.",
    start: "Započni besplatno",
    demo: "Isprobaj primjer",
    codeTitle: "Imaš kôd?",
    codeText: "Upiši dobiveni kôd i igraj",
    free: "Besplatno za početak · Bez kreditne kartice",
  },
  stats: { downloads: "preuzimanja", users: "aktivnih korisnika", projects: "izrađenih projekata", countries: "zemalja" },
  grid: {
    title: "Sve što ti treba za učenje kroz igru",
    sub: "Make It na jednom mjestu spaja izradu, igru i praćenje.",
    items: [
      { title: "Izradi u nekoliko minuta", text: "Predlošci, slike, zvukovi, crteži i YouTube videozapisi na dohvat ruke. Bez programiranja." },
      {
        title: "Više od 20 aktivnosti",
        text: "Povezuj, povlači, pronalazi parove, slaži redoslijed, upisuj, boji, iscrtavaj simbole i još mnogo toga.",
      },
      { title: "Dijeli pomoću kôda", text: "Pošalji kôd ili poveznicu i igraj na bilo kojem uređaju, bez instaliranja." },
      { title: "Trenutačni rezultati", text: "Odgovori, ljestvice i statistika za svaki kviz te napredak svakog učenika." },
      { title: "Razredi", text: "Učenici se pridružuju pomoću kôda. Dijeli projekte s cijelim razredom ili samo s nekima." },
      { title: "Uvijek sinkronizirano", text: "Tvoji su projekti spremljeni u oblaku i spremni na webu i u aplikaciji." },
    ],
  },
  create: {
    kicker: "Izradi",
    title: "Osmisli projekt na svoj način",
    text: "Odaberi aktivnost, dodaj svoje slike, tekstove i zvukove i projekt je gotov. Sve što napraviš automatski se sprema.",
    bullets: [
      "Zbirke s tisućama slika i zvukova",
      "Snimi vlastiti glas",
      "Crtaj izravno na stranici",
      "Dodaj YouTube videozapise",
      "Predlošci, fontovi i glazba u pozadini",
    ],
  },
  play: {
    kicker: "Igre",
    title: "Stvoreno za najmlađe",
    text: "Igre nagrađuju trud: pogreške se ne kažnjavaju, djeca mogu pokušavati koliko god im treba, a ne moraju čak ni znati čitati.",
    bullets: ["Pozitivno potkrepljenje na svakoj stranici", "Upute s tekstom i zvukom", "Za igru nisu potrebni ni ime ni račun"],
  },
  quiz: {
    kicker: "Kvizovi",
    title: "Kvizovi s bodovima i ljestvicom",
    text: "Pitanja s vremenskim ograničenjem, automatsko bodovanje i ljestvica uživo. Vidiš odgovore svakog igrača i statistiku svakog pitanja.",
    bullets: ["Pitanje, točno ili netočno, upiši odgovor, matematika…", "Vremensko ograničenje po pitanju", "Ljestvica i statistika"],
  },
  share: {
    kicker: "Dijeli",
    title: "Jedan kôd i igra počinje",
    text: "Svaki projekt ima šesteroznamenkasti kôd i poveznicu. Učenici ga otvaraju u pregledniku ili u aplikaciji, bez izrade računa.",
    code: "Kôd",
    link: "Poveznica",
    classroom: "Ili ga podijeli s razredom",
  },
  classes: {
    kicker: "Razredi",
    title: "Prati svakog učenika",
    text: "Izradi razred, daj učenicima njegov kôd i dijeli projekte s njima, automatski sa svima ili samo s nekima.",
    bullets: ["Učenici se pridružuju pomoću kôda", "Rezultati svake igre i svakog kviza", "Dijeli s cijelim razredom ili samo s nekima"],
    mockTitle: "Rezultati",
    mockCols: ["Učenik", "Bodovi", "Vrijeme"],
  },
  activities: { title: "Više od 20 vrsta aktivnosti", sub: "Za igre i kvizove, spremne za tvoj sadržaj.", games: "Igre", quizzes: "Kvizovi" },
  videos: {
    title: "Upoznaj Make It",
    sub: "Pogledaj kako se projekt izrađuje u nekoliko minuta.",
    main: "Što je Make It?",
    tutorials: "Video vodiči",
    quiz: "Kako izraditi kviz",
    game: "Kako izraditi igru",
    share: "Kako podijeliti projekt",
    all: "Pogledaj sve vodiče",
  },
  demos: {
    title: "Isprobaj odmah",
    sub: "Zaigraj neke od naših primjera, bez registracije.",
    games: "Igre",
    quizzes: "Kvizovi",
    play: "Igraj",
    pages: "stranica",
  },
  plans: {
    title: "Planovi za svaki razred",
    sub: "Započni besplatno i prijeđi na Premium kad ti zatreba više.",
    perYear: "/godišnje",
    free: "Besplatno",
    freePrice: "0 €",
    freeFeatures: ["2 projekta", "1 razred", "1 mapa", "Gotovo sve značajke"],
    educators: "Učitelji",
    educatorsFeatures: ["1 učitelj", "Do 100 učenika", "Neograničen broj projekata, razreda i mapa", "Rezultati i statistika svakog učenika"],
    teams: "Timovi",
    teamsFeatures: ["5 učitelja", "Neograničen broj učenika", "Upravitelj licence", "Sve iz plana Učitelji"],
    schools: "Škole",
    schoolsFeatures: ["20 učitelja", "Neograničen broj učenika", "Upravitelj licence", "Sve iz plana Učitelji"],
    startFree: "Započni besplatno",
    tryFree: "Isprobaj besplatno",
    subscribe: "Pretplati se",
    note: "Učenici Make It uvijek koriste besplatno.",
  },
  faq: {
    title: "Česta pitanja",
    sub: "Imaš nedoumica? Ovo su najčešći odgovori.",
    items: [
      {
        q: "Što su kvizovi?",
        a: "Kvizovi su zabavan način za provjeru znanja, učenje ili ponavljanje bilo kojeg predmeta. Automatski bilježe odgovore i bodove svakog igrača, a ti možeš vidjeti odgovore, ljestvicu i statistiku.",
      },
      {
        q: "Što su igre?",
        a: "Igre su osmišljene za malu djecu, čak i onu koja još ne znaju čitati. Usmjerene su na pozitivno potkrepljenje: pogreške se ne kažnjavaju, a broj pokušaja je neograničen. Djeca ne upisuju ime da bi igrala.",
      },
      {
        q: "Kako mogu vidjeti rezultate igara?",
        a: "Budući da se djeca ne identificiraju kad igraju, rezultati i statistika igara mogu se vidjeti samo kad je igra podijeljena preko razreda, a ne kôdom ili poveznicom.",
      },
      {
        q: "Koja su ograničenja besplatne verzije?",
        a: "Možeš koristiti gotovo sve značajke. Besplatna verzija omogućuje izradu 2 projekta, 1 razreda i 1 mape.",
      },
      {
        q: "Što dobivam pretplatom?",
        a: "Bez ograničenja: projekata, razreda i mapa koliko god želiš te rezultate i statistiku svih tvojih učenika.",
      },
      {
        q: "Trebam li račun?",
        a: "Za igranje ne. S računom možeš izrađivati projekte i spremati ih u oblak, sinkronizirati ih na svim uređajima i izrađivati razrede za dijeljenje projekata sa svojim učenicima.",
      },
      { q: "Moraju li učenici plaćati?", a: "Ne. Učenici Make It uvijek koriste besplatno." },
      {
        q: "Kako podijeliti projekt?",
        a: "Otvori projekt i idi na „Podijeli“: vidjet ćeš njegov šesteroznamenkasti kôd i poveznicu. Svatko tko ih ima može ga igrati na bilo kojem uređaju. Možeš ga podijeliti i s razredom pa učenicima neće trebati nikakav kôd.",
      },
      {
        q: "Što su razredi i kako funkcioniraju?",
        a: "Razredi ti omogućuju upravljanje skupinama učenika. Izradi razred, daj učenicima njegov kôd da se pridruže i dijeli projekte s njima: sa svima ili samo s nekima. Vidjet ćeš rezultate svakog učenika.",
      },
      {
        q: "Mogu li koristiti Make It na bilo kojem uređaju?",
        a: "Da. Možeš izrađivati i igrati na webu, a aplikacija je dostupna za iPad, iPhone, Android i Mac. Tvoji projekti ostaju sinkronizirani.",
      },
    ],
  },
  download: {
    title: "Make It na svim tvojim uređajima",
    sub: "Koristi ga upravo ovdje u pregledniku ili preuzmi aplikaciju.",
    web: "Otvori na webu",
    available: "Preuzmi na",
  },
  final: { title: "Učini učenje zabavnim", sub: "Izradi svoj prvi projekt već danas. Besplatno je." },
  footer: {
    tagline: "Izrađuj igre, kvizove, aktivnosti i priče i dijeli ih bilo gdje.",
    product: "Proizvod",
    help: "Pomoć",
    legal: "Pravne informacije",
    contact: "Kontakt",
    terms: "Uvjeti i odredbe",
    privacy: "Privatnost",
    cookies: "Pravila o kolačićima",
    follow: "Prati nas",
    support: "Uz potporu agencije ACCIÓ",
    demos: "Primjeri",
  },
};

const bg: LandingText = {
  nav: {
    features: "Функции",
    activities: "Дейности",
    videos: "Видео",
    plans: "Планове",
    faq: "Въпроси",
    download: "Изтегляне",
    login: "Вход",
    signup: "Безплатна регистрация",
    student: "Аз съм ученик",
    menu: "Меню",
    language: "Език",
  },
  hero: {
    eyebrow: "Образователни игри и викторини",
    title: "Създавайте образователни игри и викторини за минути",
    sub: "Съчетайте над 20 вида дейности в своите проекти, споделете ги с когото пожелаете и вижте резултатите веднага. В браузъра, на таблет или на телефон.",
    start: "Започнете безплатно",
    demo: "Опитайте демо",
    codeTitle: "Имате код?",
    codeText: "Въведете получения код и играйте",
    free: "Безплатно начало · Без кредитна карта",
  },
  stats: { downloads: "изтегляния", users: "активни потребители", projects: "създадени проекта", countries: "държави" },
  grid: {
    title: "Всичко необходимо за учене чрез игра",
    sub: "Make It обединява създаването, играта и проследяването на едно място.",
    items: [
      { title: "Създавайте за минути", text: "Шаблони, изображения, звуци, рисунки и видеа от YouTube под ръка. Не е нужно програмиране." },
      {
        title: "Над 20 дейности",
        text: "Свързване, плъзгане, намиране на двойки, подреждане, писане, оцветяване, очертаване на символи и още много.",
      },
      { title: "Споделяне с код", text: "Изпратете код или връзка и играйте на всяко устройство, без да инсталирате нищо." },
      { title: "Резултати веднага", text: "Отговори, класиране и статистика за всяка викторина, както и напредъкът на всеки ученик." },
      { title: "Класове", text: "Учениците се присъединяват с код. Споделяйте проекти с целия клас или само с някои ученици." },
      { title: "Винаги синхронизирано", text: "Проектите ви се съхраняват в облака и са на разположение в уеб версията и в приложението." },
    ],
  },
  create: {
    kicker: "Създаване",
    title: "Създайте проекта си по свой вкус",
    text: "Изберете дейност, добавете свои изображения, текстове и звуци и проектът ви е готов. Всичко, което правите, се запазва автоматично.",
    bullets: [
      "Библиотеки с хиляди изображения и звуци",
      "Запишете собствения си глас",
      "Рисувайте направо върху страницата",
      "Добавяйте видеа от YouTube",
      "Шаблони, шрифтове и фонова музика",
    ],
  },
  play: {
    kicker: "Игри",
    title: "Създадени за най-малките",
    text: "Игрите възнаграждават усилието: грешките не се наказват, децата могат да опитват колкото пъти им е нужно и дори не е нужно да могат да четат.",
    bullets: ["Положително подкрепление на всяка страница", "Инструкции с текст и звук", "За игра не са нужни име или профил"],
  },
  quiz: {
    kicker: "Викторини",
    title: "Викторини с точки и класиране",
    text: "Въпроси с ограничено време, автоматично оценяване и класиране на живо. Виждате отговорите на всеки играч и статистиката за всеки въпрос.",
    bullets: ["Въпрос, вярно или невярно, писмен отговор, математика…", "Ограничено време за всеки въпрос", "Класиране и статистика"],
  },
  share: {
    kicker: "Споделяне",
    title: "Само код – и играта започва",
    text: "Всеки проект има шестцифрен код и връзка. Учениците го отварят в браузъра или в приложението, без да създават профил.",
    code: "Код",
    link: "Връзка",
    classroom: "Или го споделете с клас",
  },
  classes: {
    kicker: "Класове",
    title: "Следете всеки ученик",
    text: "Създайте клас, дайте кода му на учениците си и споделяйте проекти с тях – автоматично с всички или само с някои.",
    bullets: ["Учениците се присъединяват с код", "Резултати от всяка игра и всяка викторина", "Споделяне с целия клас или с няколко ученици"],
    mockTitle: "Резултати",
    mockCols: ["Ученик", "Резултат", "Време"],
  },
  activities: { title: "Над 20 вида дейности", sub: "За игри и за викторини, готови да ги попълните със свое съдържание.", games: "Игри", quizzes: "Викторини" },
  videos: {
    title: "Открийте Make It",
    sub: "Вижте как се прави проект за няколко минути.",
    main: "Какво е Make It?",
    tutorials: "Видео уроци",
    quiz: "Как да създадете викторина",
    game: "Как да създадете игра",
    share: "Как да споделите проект",
    all: "Вижте всички уроци",
  },
  demos: {
    title: "Опитайте сега",
    sub: "Изиграйте някой от нашите демо проекти, без регистрация.",
    games: "Игри",
    quizzes: "Викторини",
    play: "Играйте",
    pages: "страници",
  },
  plans: {
    title: "Планове за всеки клас",
    sub: "Започнете безплатно и преминете към Premium, когато ви трябва повече.",
    perYear: "/година",
    free: "Безплатен",
    freePrice: "0 €",
    freeFeatures: ["2 проекта", "1 клас", "1 папка", "Почти всички функции"],
    educators: "Преподаватели",
    educatorsFeatures: ["1 преподавател", "До 100 ученици", "Неограничен брой проекти, класове и папки", "Резултати и статистика за всеки ученик"],
    teams: "Екипи",
    teamsFeatures: ["5 преподаватели", "Неограничен брой ученици", "Администратор на лиценза", "Всичко от „Преподаватели“"],
    schools: "Училища",
    schoolsFeatures: ["20 преподаватели", "Неограничен брой ученици", "Администратор на лиценза", "Всичко от „Преподаватели“"],
    startFree: "Започнете безплатно",
    tryFree: "Опитайте безплатно",
    subscribe: "Абонирайте се",
    note: "Учениците винаги използват Make It безплатно.",
  },
  faq: {
    title: "Често задавани въпроси",
    sub: "Имате въпроси? Ето отговорите на най-честите от тях.",
    items: [
      {
        q: "Какво са викторините?",
        a: "Викторините са забавен начин за оценяване, учене или преговор по всеки предмет. Те записват автоматично отговорите и резултата на всеки играч, а вие можете да видите отговорите, класирането и статистиката.",
      },
      {
        q: "Какво са игрите?",
        a: "Игрите са създадени за малки деца, дори за тези, които още не могат да четат. Те залагат на положителното подкрепление: грешките не се наказват, а опитите са неограничени. Децата не въвеждат име, за да играят.",
      },
      {
        q: "Как мога да видя резултатите от игрите?",
        a: "Тъй като децата не се идентифицират, когато играят, резултатите и статистиката на игрите могат да се видят само когато играта е споделена чрез клас, а не с код или връзка.",
      },
      {
        q: "Какви са ограниченията на безплатната версия?",
        a: "Можете да използвате почти всички функции. Безплатната версия ви позволява да създадете 2 проекта, 1 клас и 1 папка.",
      },
      {
        q: "Какво получавам с абонамент?",
        a: "Без ограничения: колкото искате проекти, класове и папки, както и резултатите и статистиката на всички ваши ученици.",
      },
      {
        q: "Нужен ли ми е профил?",
        a: "За да играете – не. С профил можете да създавате и запазвате проектите си в облака, да ги синхронизирате между устройствата си и да създавате класове, за да споделяте проекти с учениците си.",
      },
      { q: "Трябва ли учениците да плащат?", a: "Не. Учениците винаги използват Make It безплатно." },
      {
        q: "Как да споделя проект?",
        a: "Отворете проекта и отидете в „Споделяне“: ще видите шестцифрения му код и връзката му. Всеки, който ги има, може да го играе на всяко устройство. Можете също да го споделите с клас, така че на учениците да не им трябва код.",
      },
      {
        q: "Какво са класовете и как работят?",
        a: "Класовете ви позволяват да управлявате групи ученици. Създайте клас, дайте кода му на учениците си, за да се присъединят, и споделяйте проекти с тях: с всички или само с някои. Ще виждате резултатите на всеки ученик.",
      },
      {
        q: "Мога ли да използвам Make It на всяко устройство?",
        a: "Да. Можете да създавате и играете в уеб версията, а приложението е налично за iPad, iPhone, Android и Mac. Проектите ви се синхронизират.",
      },
    ],
  },
  download: {
    title: "Make It на всичките ви устройства",
    sub: "Използвайте го направо тук, в браузъра, или изтеглете приложението.",
    web: "Отваряне в браузъра",
    available: "Изтеглете от",
  },
  final: { title: "Направете ученето забавно", sub: "Създайте първия си проект още днес. Безплатно е." },
  footer: {
    tagline: "Създавайте игри, викторини, дейности и истории и ги споделяйте навсякъде.",
    product: "Продукт",
    help: "Помощ",
    legal: "Правна информация",
    contact: "Контакт",
    terms: "Общи условия",
    privacy: "Поверителност",
    cookies: "Политика за бисквитките",
    follow: "Последвайте ни",
    support: "С подкрепата на ACCIÓ",
    demos: "Демо",
  },
};

const da: LandingText = {
  nav: {
    features: "Funktioner",
    activities: "Aktiviteter",
    videos: "Videoer",
    plans: "Priser",
    faq: "Spørgsmål",
    download: "Hent appen",
    login: "Log ind",
    signup: "Opret gratis konto",
    student: "Jeg er elev",
    menu: "Menu",
    language: "Sprog",
  },
  hero: {
    eyebrow: "Lærerige spil og quizzer",
    title: "Lav lærerige spil og quizzer på få minutter",
    sub: "Design projekter med mere end 20 typer aktiviteter, del dem med hvem som helst, og se resultaterne med det samme. I browseren, på en tablet eller på en telefon.",
    start: "Kom gratis i gang",
    demo: "Prøv en demo",
    codeTitle: "Har du en kode?",
    codeText: "Skriv den kode, du har fået, og spil",
    free: "Gratis at starte · Intet betalingskort",
  },
  stats: { downloads: "downloads", users: "aktive brugere", projects: "oprettede projekter", countries: "lande" },
  grid: {
    title: "Alt, hvad du skal bruge for at lære gennem leg",
    sub: "Make It samler det at skabe, spille og følge op ét sted.",
    items: [
      { title: "Lav det på få minutter", text: "Skabeloner, billeder, lyde, tegninger og YouTube-videoer lige ved hånden. Ingen kodning nødvendig." },
      { title: "Mere end 20 aktiviteter", text: "Forbind, træk, find parret, sæt i rækkefølge, skriv, mal, tegn symboler efter og meget mere." },
      { title: "Del med en kode", text: "Send en kode eller et link, og spil på enhver enhed uden at installere noget." },
      { title: "Resultater med det samme", text: "Svar, ranglister og statistik for hver quiz og hver elevs fremskridt." },
      { title: "Klasser", text: "Eleverne tilmelder sig med en kode. Del projekter med hele klassen eller kun med nogle få." },
      { title: "Altid synkroniseret", text: "Dine projekter gemmes i skyen og er klar på nettet og i appen." },
    ],
  },
  create: {
    kicker: "Skab",
    title: "Design dit projekt, som du vil",
    text: "Vælg en aktivitet, tilføj dine billeder, tekster og lyde, og dit projekt er klar. Alt, hvad du laver, gemmes automatisk.",
    bullets: [
      "Biblioteker med tusindvis af billeder og lyde",
      "Optag din egen stemme",
      "Tegn direkte på siden",
      "Tilføj YouTube-videoer",
      "Skabeloner, skrifttyper og baggrundsmusik",
    ],
  },
  play: {
    kicker: "Spil",
    title: "Lavet til de mindste",
    text: "Spillene belønner indsatsen: fejl straffes ikke, børnene kan prøve så mange gange, de har brug for, og de behøver ikke engang at kunne læse.",
    bullets: ["Positiv forstærkning på hver side", "Instruktioner med tekst og lyd", "Spil uden navn eller konto"],
  },
  quiz: {
    kicker: "Quizzer",
    title: "Quizzer med point og ranglister",
    text: "Spørgsmål med tidsgrænse, automatisk pointgivning og en rangliste i realtid. Du ser hver deltagers svar og statistikken for hvert spørgsmål.",
    bullets: ["Spørgsmål, sandt eller falsk, skriv svaret, matematik…", "Tidsgrænse pr. spørgsmål", "Rangliste og statistik"],
  },
  share: {
    kicker: "Del",
    title: "Én kode, og så er I i gang",
    text: "Hvert projekt har en sekscifret kode og et link. Eleverne åbner det i browseren eller i appen uden at oprette en konto.",
    code: "Kode",
    link: "Link",
    classroom: "Eller del det med en klasse",
  },
  classes: {
    kicker: "Klasser",
    title: "Følg hver enkelt elev",
    text: "Opret en klasse, giv dine elever klassekoden, og del projekter med dem – automatisk med alle eller kun med nogle.",
    bullets: ["Eleverne tilmelder sig med en kode", "Resultater fra hvert spil og hver quiz", "Del med hele klassen eller med nogle få"],
    mockTitle: "Resultater",
    mockCols: ["Elev", "Point", "Tid"],
  },
  activities: {
    title: "Mere end 20 typer aktiviteter",
    sub: "Til spil og quizzer, klar til at blive fyldt med dit eget indhold.",
    games: "Spil",
    quizzes: "Quizzer",
  },
  videos: {
    title: "Opdag Make It",
    sub: "Se, hvordan et projekt bliver til på få minutter.",
    main: "Hvad er Make It?",
    tutorials: "Videovejledninger",
    quiz: "Sådan laver du en quiz",
    game: "Sådan laver du et spil",
    share: "Sådan deler du et projekt",
    all: "Se alle vejledningerne",
  },
  demos: { title: "Prøv det nu", sub: "Spil nogle af vores demoer – uden tilmelding.", games: "Spil", quizzes: "Quizzer", play: "Spil", pages: "sider" },
  plans: {
    title: "Et abonnement til enhver klasse",
    sub: "Start gratis, og skift til Premium, når du har brug for mere.",
    perYear: "/år",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 projekter", "1 klasse", "1 mappe", "Næsten alle funktioner"],
    educators: "Undervisere",
    educatorsFeatures: ["1 underviser", "Op til 100 elever", "Ubegrænset antal projekter, klasser og mapper", "Resultater og statistik for hver elev"],
    teams: "Teams",
    teamsFeatures: ["5 undervisere", "Ubegrænset antal elever", "Licensadministrator", "Alt i Undervisere"],
    schools: "Skoler",
    schoolsFeatures: ["20 undervisere", "Ubegrænset antal elever", "Licensadministrator", "Alt i Undervisere"],
    startFree: "Kom gratis i gang",
    tryFree: "Prøv det gratis",
    subscribe: "Abonner",
    note: "Eleverne bruger altid Make It gratis.",
  },
  faq: {
    title: "Ofte stillede spørgsmål",
    sub: "Er du i tvivl om noget? Her er svarene på de mest almindelige spørgsmål.",
    items: [
      {
        q: "Hvad er quizzer?",
        a: "Quizzer er en sjov måde at evaluere, lære eller repetere ethvert fag på. De registrerer automatisk hver deltagers svar og point, og du kan se svarene, ranglisten og statistikken.",
      },
      {
        q: "Hvad er spil?",
        a: "Spillene er lavet til små børn, også dem, der endnu ikke kan læse. De fokuserer på positiv forstærkning: fejl straffes ikke, og der er ubegrænsede forsøg. Børnene skal ikke skrive et navn for at spille.",
      },
      {
        q: "Hvordan kan jeg se resultaterne af spil?",
        a: "Da børnene ikke identificerer sig, når de spiller, kan resultaterne og statistikken for spil kun ses, når spillet deles via en klasse, ikke via kode eller link.",
      },
      {
        q: "Hvad er begrænsningerne i gratisversionen?",
        a: "Du kan bruge næsten alle funktioner. Med gratisversionen kan du oprette 2 projekter, 1 klasse og 1 mappe.",
      },
      {
        q: "Hvad får jeg ved at abonnere?",
        a: "Ingen begrænsninger: så mange projekter, klasser og mapper du vil, og resultaterne og statistikken for alle dine elever.",
      },
      {
        q: "Skal jeg have en konto?",
        a: "Ikke for at spille. Med en konto kan du oprette og gemme dine projekter i skyen, synkronisere dem mellem dine enheder og oprette klasser, så du kan dele projekter med dine elever.",
      },
      { q: "Skal eleverne betale?", a: "Nej. Eleverne bruger altid Make It gratis." },
      {
        q: "Hvordan deler jeg et projekt?",
        a: "Åbn projektet, og gå til Del: der ser du projektets sekscifrede kode og dets link. Alle, der har dem, kan spille det på enhver enhed. Du kan også dele det med en klasse, så eleverne ikke behøver nogen kode.",
      },
      {
        q: "Hvad er klasser, og hvordan fungerer de?",
        a: "Med klasser kan du administrere grupper af elever. Opret en klasse, giv dine elever klassekoden, så de kan tilmelde sig, og del projekter med dem: med alle eller kun med nogle. Du kan se hver elevs resultater.",
      },
      {
        q: "Kan jeg bruge Make It på alle enheder?",
        a: "Ja. Du kan lave og spille projekter på nettet, og appen findes til iPad, iPhone, Android og Mac. Dine projekter holdes synkroniseret.",
      },
    ],
  },
  download: { title: "Make It på alle dine enheder", sub: "Brug det direkte her i browseren, eller hent appen.", web: "Åbn på nettet", available: "Fås på" },
  final: { title: "Gør læring sjov", sub: "Lav dit første projekt i dag. Det er gratis." },
  footer: {
    tagline: "Lav spil, quizzer, aktiviteter og historier, og del dem overalt.",
    product: "Produkt",
    help: "Hjælp",
    legal: "Juridisk",
    contact: "Kontakt",
    terms: "Vilkår og betingelser",
    privacy: "Privatliv",
    cookies: "Cookiepolitik",
    follow: "Følg os",
    support: "Med støtte fra ACCIÓ",
    demos: "Demoer",
  },
};

const nb: LandingText = {
  nav: {
    features: "Funksjoner",
    activities: "Aktiviteter",
    videos: "Videoer",
    plans: "Priser",
    faq: "Vanlige spørsmål",
    download: "Last ned",
    login: "Logg inn",
    signup: "Registrer deg gratis",
    student: "Jeg er elev",
    menu: "Meny",
    language: "Språk",
  },
  hero: {
    eyebrow: "Pedagogiske spill og quizer",
    title: "Lag læringsspill og quizer på få minutter",
    sub: "Lag prosjekter med over 20 typer aktiviteter, del dem med hvem som helst og se resultatene med en gang. I nettleseren, på nettbrett eller på mobilen.",
    start: "Kom i gang gratis",
    demo: "Prøv en demo",
    codeTitle: "Har du en kode?",
    codeText: "Skriv inn koden du har fått, og spill",
    free: "Gratis å komme i gang · Uten kredittkort",
  },
  stats: { downloads: "nedlastinger", users: "aktive brukere", projects: "prosjekter laget", countries: "land" },
  grid: {
    title: "Alt du trenger for å lære gjennom lek",
    sub: "Make It samler det å lage, spille og følge opp på ett sted.",
    items: [
      { title: "Lag på få minutter", text: "Maler, bilder, lyder, tegninger og YouTube-videoer lett tilgjengelig. Ingen koding nødvendig." },
      { title: "Over 20 aktiviteter", text: "Koble sammen, dra, finn paret, sorter, skriv, fargelegg, spor symboler og mye mer." },
      { title: "Del med en kode", text: "Send en kode eller en lenke og spill på hvilken som helst enhet, uten å installere noe." },
      { title: "Resultater med en gang", text: "Svar, topplister og statistikk for hver quiz, og fremgangen til hver elev." },
      { title: "Klasser", text: "Elevene blir med via en kode. Del prosjekter med hele klassen eller bare noen få." },
      { title: "Alltid synkronisert", text: "Prosjektene dine lagres i skyen og er klare på nettet og i appen." },
    ],
  },
  create: {
    kicker: "Lag",
    title: "Lag prosjektet ditt på din måte",
    text: "Velg en aktivitet, legg til bilder, tekster og lyder, så er prosjektet klart. Alt du gjør, lagres automatisk.",
    bullets: [
      "Biblioteker med tusenvis av bilder og lyder",
      "Ta opp din egen stemme",
      "Tegn rett på siden",
      "Legg til YouTube-videoer",
      "Maler, skrifttyper og bakgrunnsmusikk",
    ],
  },
  play: {
    kicker: "Spill",
    title: "Laget for de minste",
    text: "Spillene belønner innsats: feil gir ikke trekk, barna kan prøve så mange ganger de trenger, og de trenger ikke engang å kunne lese.",
    bullets: ["Positiv forsterkning på hver side", "Instruksjoner med tekst og lyd", "Verken navn eller konto trengs for å spille"],
  },
  quiz: {
    kicker: "Quizer",
    title: "Quizer med poeng og topplister",
    text: "Spørsmål på tid, automatisk poenggiving og toppliste i sanntid. Du ser svarene til hver spiller og statistikken for hvert spørsmål.",
    bullets: ["Spørsmål, sant eller usant, skriv svaret, matte …", "Tidsgrense per spørsmål", "Toppliste og statistikk"],
  },
  share: {
    kicker: "Del",
    title: "Én kode, og spill i vei",
    text: "Hvert prosjekt har en sekssifret kode og en lenke. Elevene åpner den i nettleseren eller i appen, uten å opprette konto.",
    code: "Kode",
    link: "Lenke",
    classroom: "Eller del det med en klasse",
  },
  classes: {
    kicker: "Klasser",
    title: "Følg hver elev",
    text: "Opprett en klasse, gi elevene koden og del prosjekter med dem, automatisk med alle eller bare med noen.",
    bullets: ["Elevene blir med via en kode", "Resultater for hvert spill og hver quiz", "Del med hele klassen eller med noen få"],
    mockTitle: "Resultater",
    mockCols: ["Elev", "Poeng", "Tid"],
  },
  activities: { title: "Over 20 typer aktiviteter", sub: "For spill og quizer, klare til å fylles med ditt eget innhold.", games: "Spill", quizzes: "Quizer" },
  videos: {
    title: "Bli kjent med Make It",
    sub: "Se hvordan et prosjekt lages på noen få minutter.",
    main: "Hva er Make It?",
    tutorials: "Videoveiledninger",
    quiz: "Slik lager du en quiz",
    game: "Slik lager du et spill",
    share: "Slik deler du et prosjekt",
    all: "Se alle veiledningene",
  },
  demos: {
    title: "Prøv det nå",
    sub: "Spill noen av demoene våre, uten å registrere deg.",
    games: "Spill",
    quizzes: "Quizer",
    play: "Spill",
    pages: "sider",
  },
  plans: {
    title: "Abonnementer for alle klasser",
    sub: "Kom i gang gratis og gå over til Premium når du trenger mer.",
    perYear: "/år",
    free: "Gratis",
    freePrice: "0 €",
    freeFeatures: ["2 prosjekter", "1 klasse", "1 mappe", "Nesten alle funksjoner"],
    educators: "Pedagoger",
    educatorsFeatures: ["1 pedagog", "Opptil 100 elever", "Ubegrenset antall prosjekter, klasser og mapper", "Resultater og statistikk for hver elev"],
    teams: "Team",
    teamsFeatures: ["5 pedagoger", "Ubegrenset antall elever", "Lisensadministrasjon", "Alt som er med i Pedagoger"],
    schools: "Skoler",
    schoolsFeatures: ["20 pedagoger", "Ubegrenset antall elever", "Lisensadministrasjon", "Alt som er med i Pedagoger"],
    startFree: "Kom i gang gratis",
    tryFree: "Prøv gratis",
    subscribe: "Abonner",
    note: "Elevene bruker alltid Make It gratis.",
  },
  faq: {
    title: "Ofte stilte spørsmål",
    sub: "Lurer du på noe? Her er svarene på de vanligste spørsmålene.",
    items: [
      {
        q: "Hva er quizer?",
        a: "Quizer er en morsom måte å vurdere, lære eller repetere et hvilket som helst fag på. De registrerer svarene og poengsummen til hver spiller automatisk, og du kan se svarene, topplisten og statistikken.",
      },
      {
        q: "Hva er spill?",
        a: "Spillene er laget for små barn, også de som ikke kan lese ennå. De legger vekt på positiv forsterkning: feil gir ikke trekk, og barna kan prøve så mange ganger de vil. Barna skriver ikke inn noe navn for å spille.",
      },
      {
        q: "Hvordan kan jeg se resultatene fra spill?",
        a: "Siden barna ikke oppgir hvem de er når de spiller, kan resultater og statistikk for spill bare ses når spillet deles via en klasse, ikke via kode eller lenke.",
      },
      {
        q: "Hvilke begrensninger har gratisversjonen?",
        a: "Du kan bruke nesten alle funksjonene. Med gratisversjonen kan du lage 2 prosjekter, 1 klasse og 1 mappe.",
      },
      {
        q: "Hva får jeg ved å abonnere?",
        a: "Ingen begrensninger: så mange prosjekter, klasser og mapper du vil, og resultater og statistikk for alle elevene dine.",
      },
      {
        q: "Trenger jeg en konto?",
        a: "For å spille trenger du ikke det. Med en konto kan du lage og lagre prosjektene dine i skyen, synkronisere dem mellom enheter og opprette klasser for å dele prosjekter med elevene dine.",
      },
      { q: "Må elevene betale?", a: "Nei. Elevene bruker alltid Make It gratis." },
      {
        q: "Hvordan deler jeg et prosjekt?",
        a: "Åpne prosjektet og gå til «Del»: der ser du den sekssifrede koden og lenken. Alle som har dem, kan spille det på hvilken som helst enhet. Du kan også dele det med en klasse, så elevene ikke trenger noen kode.",
      },
      {
        q: "Hva er klasser, og hvordan fungerer de?",
        a: "Med klasser kan du administrere elevgrupper. Opprett en klasse, gi elevene koden slik at de kan bli med, og del prosjekter med dem: med alle eller bare med noen. Du ser resultatene til hver elev.",
      },
      {
        q: "Kan jeg bruke Make It på alle enheter?",
        a: "Ja. Du kan lage og spille på nettet, og appen finnes for iPad, iPhone, Android og Mac. Prosjektene dine holdes synkronisert.",
      },
    ],
  },
  download: {
    title: "Make It på alle enhetene dine",
    sub: "Bruk det rett her i nettleseren, eller last ned appen.",
    web: "Åpne på nettet",
    available: "Last ned fra",
  },
  final: { title: "Gjør læring morsomt", sub: "Lag ditt første prosjekt i dag. Det er gratis." },
  footer: {
    tagline: "Lag spill, quizer, aktiviteter og historier, og del dem hvor som helst.",
    product: "Produkt",
    help: "Hjelp",
    legal: "Juridisk",
    contact: "Kontakt",
    terms: "Vilkår og betingelser",
    privacy: "Personvern",
    cookies: "Retningslinjer for informasjonskapsler",
    follow: "Følg oss",
    support: "Med støtte fra ACCIÓ",
    demos: "Demoer",
  },
};

const fi: LandingText = {
  nav: {
    features: "Ominaisuudet",
    activities: "Tehtävät",
    videos: "Videot",
    plans: "Hinnat",
    faq: "UKK",
    download: "Lataa",
    login: "Kirjaudu sisään",
    signup: "Rekisteröidy ilmaiseksi",
    student: "Olen oppilas",
    menu: "Valikko",
    language: "Kieli",
  },
  hero: {
    eyebrow: "Opettavaisia pelejä ja tietovisoja",
    title: "Luo oppimispelejä ja tietovisoja minuuteissa",
    sub: "Suunnittele projekteja yli 20 tehtävätyypillä, jaa ne kenelle tahansa ja näe tulokset heti. Selaimessa, tabletilla tai puhelimella.",
    start: "Aloita ilmaiseksi",
    demo: "Kokeile demoa",
    codeTitle: "Onko sinulla koodi?",
    codeText: "Kirjoita saamasi koodi ja pelaa",
    free: "Aloittaminen on ilmaista · Ei luottokorttia",
  },
  stats: { downloads: "latausta", users: "aktiivista käyttäjää", projects: "luotua projektia", countries: "maata" },
  grid: {
    title: "Kaikki, mitä tarvitset leikkien oppimiseen",
    sub: "Make It kokoaa luomisen, pelaamisen ja seurannan yhteen paikkaan.",
    items: [
      { title: "Luo minuuteissa", text: "Pohjat, kuvat, äänet, piirrokset ja YouTube-videot aina käden ulottuvilla. Ohjelmointitaitoja ei tarvita." },
      { title: "Yli 20 tehtävätyyppiä", text: "Yhdistä, vedä, etsi pari, järjestä, kirjoita, maalaa, jäljennä merkkejä ja paljon muuta." },
      { title: "Jaa koodilla", text: "Lähetä koodi tai linkki ja pelaa millä tahansa laitteella asentamatta mitään." },
      { title: "Tulokset heti", text: "Vastaukset, tulostaulut ja tilastot jokaisesta tietovisasta sekä jokaisen oppilaan edistyminen." },
      { title: "Luokat", text: "Oppilaat liittyvät koodilla. Jaa projekteja koko luokalle tai vain muutamalle." },
      { title: "Aina synkronoitu", text: "Projektisi tallentuvat pilveen ja ovat valmiina sekä verkossa että sovelluksessa." },
    ],
  },
  create: {
    kicker: "Luo",
    title: "Suunnittele projektisi omalla tavallasi",
    text: "Valitse tehtävä, lisää kuvat, tekstit ja äänet, ja projektisi on valmis. Kaikki tekemäsi tallentuu automaattisesti.",
    bullets: [
      "Kirjastoissa tuhansia kuvia ja ääniä",
      "Äänitä oma äänesi",
      "Piirrä suoraan sivulle",
      "Lisää YouTube-videoita",
      "Pohjat, fontit ja taustamusiikki",
    ],
  },
  play: {
    kicker: "Pelit",
    title: "Tehty pienimmille",
    text: "Pelit palkitsevat yrittämisestä: virheistä ei rangaista, lapset voivat yrittää niin monta kertaa kuin tarvitsevat, eikä heidän tarvitse edes osata lukea.",
    bullets: ["Positiivinen kannustus jokaisella sivulla", "Ohjeet tekstinä ja äänenä", "Pelaamiseen ei tarvita nimeä eikä tiliä"],
  },
  quiz: {
    kicker: "Tietovisat",
    title: "Tietovisoja pisteillä ja tulostauluilla",
    text: "Ajastetut kysymykset, automaattinen pisteytys ja reaaliaikainen tulostaulu. Näet jokaisen pelaajan vastaukset ja jokaisen kysymyksen tilastot.",
    bullets: ["Kysymys, totta vai tarua, kirjoita vastaus, matematiikka…", "Aikaraja kysymystä kohden", "Tulostaulu ja tilastot"],
  },
  share: {
    kicker: "Jaa",
    title: "Yksi koodi, ja pelaamaan",
    text: "Jokaisella projektilla on kuusinumeroinen koodi ja linkki. Oppilaat avaavat sen selaimessa tai sovelluksessa luomatta tiliä.",
    code: "Koodi",
    link: "Linkki",
    classroom: "Tai jaa se luokalle",
  },
  classes: {
    kicker: "Luokat",
    title: "Seuraa jokaista oppilasta",
    text: "Luo luokka, anna oppilaillesi sen koodi ja jaa heille projekteja: automaattisesti kaikille tai vain osalle.",
    bullets: ["Oppilaat liittyvät koodilla", "Jokaisen pelin ja tietovisan tulokset", "Jaa koko luokalle tai muutamalle"],
    mockTitle: "Tulokset",
    mockCols: ["Oppilas", "Pisteet", "Aika"],
  },
  activities: {
    title: "Yli 20 tehtävätyyppiä",
    sub: "Peleihin ja tietovisoihin, valmiina täytettäviksi omalla sisällölläsi.",
    games: "Pelit",
    quizzes: "Tietovisat",
  },
  videos: {
    title: "Tutustu Make It -palveluun",
    sub: "Katso, miten projekti tehdään muutamassa minuutissa.",
    main: "Mikä on Make It?",
    tutorials: "Opetusvideot",
    quiz: "Näin luot tietovisan",
    game: "Näin luot pelin",
    share: "Näin jaat projektin",
    all: "Katso kaikki opetusvideot",
  },
  demos: {
    title: "Kokeile heti",
    sub: "Pelaa demojamme, rekisteröitymistä ei tarvita.",
    games: "Pelit",
    quizzes: "Tietovisat",
    play: "Pelaa",
    pages: "sivua",
  },
  plans: {
    title: "Sopiva paketti jokaiseen luokkaan",
    sub: "Aloita ilmaiseksi ja siirry Premiumiin, kun tarvitset enemmän.",
    perYear: "/vuosi",
    free: "Ilmainen",
    freePrice: "0 €",
    freeFeatures: ["2 projektia", "1 luokka", "1 kansio", "Lähes kaikki ominaisuudet"],
    educators: "Opettaja",
    educatorsFeatures: ["1 opettaja", "Enintään 100 oppilasta", "Rajattomasti projekteja, luokkia ja kansioita", "Jokaisen oppilaan tulokset ja tilastot"],
    teams: "Tiimi",
    teamsFeatures: ["5 opettajaa", "Rajattomasti oppilaita", "Lisenssinhallinta", "Kaikki Opettaja-paketin edut"],
    schools: "Koulu",
    schoolsFeatures: ["20 opettajaa", "Rajattomasti oppilaita", "Lisenssinhallinta", "Kaikki Opettaja-paketin edut"],
    startFree: "Aloita ilmaiseksi",
    tryFree: "Kokeile ilmaiseksi",
    subscribe: "Tilaa",
    note: "Oppilaille Make It on aina ilmainen.",
  },
  faq: {
    title: "Usein kysytyt kysymykset",
    sub: "Onko sinulla kysyttävää? Tässä yleisimmät vastaukset.",
    items: [
      {
        q: "Mitä tietovisat ovat?",
        a: "Tietovisat ovat hauska tapa arvioida, oppia tai kerrata mitä tahansa aihetta. Ne tallentavat jokaisen pelaajan vastaukset ja pisteet automaattisesti, ja näet vastaukset, tulostaulun ja tilastot.",
      },
      {
        q: "Mitä pelit ovat?",
        a: "Pelit on suunniteltu pienille lapsille, myös niille, jotka eivät vielä osaa lukea. Niissä keskitytään positiiviseen kannustukseen: virheistä ei rangaista ja yrityksiä on rajattomasti. Lasten ei tarvitse kirjoittaa nimeään pelatakseen.",
      },
      {
        q: "Miten näen pelien tulokset?",
        a: "Koska lapset eivät kerro nimeään pelatessaan, pelien tulokset ja tilastot näkyvät vain, kun peli jaetaan luokan kautta, ei koodilla tai linkillä.",
      },
      {
        q: "Mitkä ovat ilmaisversion rajoitukset?",
        a: "Voit käyttää lähes kaikkia ominaisuuksia. Ilmaisversiolla voit luoda 2 projektia, 1 luokan ja 1 kansion.",
      },
      {
        q: "Mitä saan tilaamalla?",
        a: "Ei rajoituksia: niin monta projektia, luokkaa ja kansiota kuin haluat sekä kaikkien oppilaidesi tulokset ja tilastot.",
      },
      {
        q: "Tarvitsenko tilin?",
        a: "Pelaamiseen et. Tilillä voit luoda projekteja ja tallentaa ne pilveen, synkronoida ne laitteidesi välillä ja luoda luokkia, joiden kautta jaat projekteja oppilaillesi.",
      },
      { q: "Pitääkö oppilaiden maksaa?", a: "Ei. Oppilaille Make It on aina ilmainen." },
      {
        q: "Miten jaan projektin?",
        a: "Avaa projekti ja siirry kohtaan ”Jaa”: näet sen kuusinumeroisen koodin ja linkin. Kuka tahansa, jolla ne ovat, voi pelata sitä millä tahansa laitteella. Voit myös jakaa projektin luokalle, jolloin oppilaat eivät tarvitse koodia.",
      },
      {
        q: "Mitä luokat ovat ja miten ne toimivat?",
        a: "Luokkien avulla hallitset oppilasryhmiä. Luo luokka, anna oppilaillesi sen koodi, jotta he voivat liittyä, ja jaa heille projekteja: kaikille tai vain osalle. Näet jokaisen oppilaan tulokset.",
      },
      {
        q: "Voinko käyttää Make It -palvelua millä tahansa laitteella?",
        a: "Kyllä. Voit luoda ja pelata verkossa, ja sovellus on saatavilla iPadille, iPhonelle, Androidille ja Macille. Projektisi pysyvät synkronoituina.",
      },
    ],
  },
  download: {
    title: "Make It kaikilla laitteillasi",
    sub: "Käytä sitä suoraan täällä selaimessa tai lataa sovellus.",
    web: "Avaa verkossa",
    available: "Saatavilla",
  },
  final: { title: "Tee oppimisesta hauskaa", sub: "Luo ensimmäinen projektisi jo tänään. Se on ilmaista." },
  footer: {
    tagline: "Luo pelejä, tietovisoja, tehtäviä ja tarinoita ja jaa ne missä tahansa.",
    product: "Tuote",
    help: "Ohje",
    legal: "Oikeudelliset tiedot",
    contact: "Yhteystiedot",
    terms: "Käyttöehdot",
    privacy: "Tietosuoja",
    cookies: "Evästekäytäntö",
    follow: "Seuraa meitä",
    support: "Tukijana ACCIÓ",
    demos: "Demot",
  },
};

const fa: LandingText = {
  nav: {
    features: "ویژگی‌ها",
    activities: "فعالیت‌ها",
    videos: "ویدیوها",
    plans: "طرح‌ها",
    faq: "پرسش‌های متداول",
    download: "دانلود",
    login: "ورود",
    signup: "ثبت‌نام رایگان",
    student: "دانش‌آموز هستم",
    menu: "منو",
    language: "زبان",
  },
  hero: {
    eyebrow: "بازی‌ها و آزمونک‌های آموزشی",
    title: "در چند دقیقه بازی و آزمونک آموزشی بسازید",
    sub: "با بیش از 20 نوع فعالیت پروژه طراحی کنید، آن را با هر کسی به اشتراک بگذارید و نتایج را فوراً ببینید. در مرورگر، روی تبلت یا روی گوشی.",
    start: "رایگان شروع کنید",
    demo: "یک نمونه را امتحان کنید",
    codeTitle: "کد دارید؟",
    codeText: "کدی را که به شما داده‌اند وارد کنید و بازی کنید",
    free: "شروع رایگان · بدون نیاز به کارت بانکی",
  },
  stats: { downloads: "دانلود", users: "کاربر فعال", projects: "پروژه ساخته‌شده", countries: "کشور" },
  grid: {
    title: "هر آنچه برای یادگیری با بازی لازم دارید",
    sub: "Make It ساختن، بازی کردن و پیگیری را در یک جا جمع می‌کند.",
    items: [
      { title: "ساختن در چند دقیقه", text: "قالب‌ها، تصاویر، صداها، نقاشی‌ها و ویدیوهای YouTube در دسترس شما. بدون نیاز به برنامه‌نویسی." },
      { title: "بیش از 20 فعالیت", text: "وصل کردن، کشیدن، پیدا کردن جفت‌ها، مرتب کردن، تایپ کردن، رنگ‌آمیزی، رسم نمادها و خیلی چیزهای دیگر." },
      { title: "اشتراک‌گذاری با یک کد", text: "یک کد یا پیوند بفرستید و روی هر دستگاهی بازی کنید، بدون نصب هیچ چیز." },
      { title: "نتایج فوری", text: "پاسخ‌ها، رتبه‌بندی‌ها و آمار هر آزمونک، و پیشرفت هر دانش‌آموز." },
      { title: "کلاس‌ها", text: "دانش‌آموزان با یک کد به کلاس می‌پیوندند. پروژه‌ها را با کل کلاس یا فقط با چند نفر به اشتراک بگذارید." },
      { title: "همیشه همگام", text: "پروژه‌هایتان در فضای ابری ذخیره می‌شوند و در وب و در برنامه آماده‌اند." },
    ],
  },
  create: {
    kicker: "ساختن",
    title: "پروژه‌تان را به سلیقه خودتان طراحی کنید",
    text: "یک فعالیت انتخاب کنید، تصاویر، متن‌ها و صداهای خودتان را اضافه کنید و پروژه‌تان آماده است. هر کاری انجام دهید به‌طور خودکار ذخیره می‌شود.",
    bullets: [
      "کتابخانه‌هایی با هزاران تصویر و صدا",
      "ضبط صدای خودتان",
      "نقاشی مستقیم روی صفحه",
      "افزودن ویدیوهای YouTube",
      "قالب‌ها، قلم‌ها و موسیقی پس‌زمینه",
    ],
  },
  play: {
    kicker: "بازی‌ها",
    title: "ساخته‌شده برای کوچولوها",
    text: "بازی‌ها به تلاش پاداش می‌دهند: اشتباه‌ها جریمه نمی‌شوند، بچه‌ها هر چند بار که لازم باشد می‌توانند دوباره تلاش کنند و حتی لازم نیست خواندن بلد باشند.",
    bullets: ["تقویت مثبت در هر صفحه", "دستورالعمل‌ها با متن و صدا", "بدون نیاز به نام یا حساب برای بازی"],
  },
  quiz: {
    kicker: "آزمونک‌ها",
    title: "آزمونک‌هایی با امتیاز و رتبه‌بندی",
    text: "سؤال‌های زمان‌دار، امتیازدهی خودکار و رتبه‌بندی زنده. پاسخ‌های هر بازیکن و آمار هر سؤال را می‌بینید.",
    bullets: ["سؤال، درست یا نادرست، تایپ پاسخ، ریاضی…", "محدودیت زمانی برای هر سؤال", "رتبه‌بندی و آمار"],
  },
  share: {
    kicker: "اشتراک‌گذاری",
    title: "یک کد و شروع بازی",
    text: "هر پروژه یک کد شش‌رقمی و یک پیوند دارد. دانش‌آموزان آن را در مرورگر یا در برنامه باز می‌کنند، بدون اینکه حسابی بسازند.",
    code: "کد",
    link: "پیوند",
    classroom: "یا آن را با یک کلاس به اشتراک بگذارید",
  },
  classes: {
    kicker: "کلاس‌ها",
    title: "هر دانش‌آموز را دنبال کنید",
    text: "یک کلاس بسازید، کد آن را به دانش‌آموزانتان بدهید و پروژه‌ها را با آن‌ها به اشتراک بگذارید: به‌طور خودکار با همه یا فقط با بعضی.",
    bullets: ["دانش‌آموزان با یک کد به کلاس می‌پیوندند", "نتایج هر بازی و هر آزمونک", "اشتراک‌گذاری با کل کلاس یا با چند نفر"],
    mockTitle: "نتایج",
    mockCols: ["دانش‌آموز", "امتیاز", "زمان"],
  },
  activities: {
    title: "بیش از 20 نوع فعالیت",
    sub: "برای بازی‌ها و آزمونک‌ها، آماده برای اینکه با محتوای خودتان پرشان کنید.",
    games: "بازی‌ها",
    quizzes: "آزمونک‌ها",
  },
  videos: {
    title: "با Make It آشنا شوید",
    sub: "ببینید چطور یک پروژه در چند دقیقه ساخته می‌شود.",
    main: "Make It چیست؟",
    tutorials: "آموزش‌های ویدیویی",
    quiz: "چطور یک آزمونک بسازیم",
    game: "چطور یک بازی بسازیم",
    share: "چطور یک پروژه را به اشتراک بگذاریم",
    all: "دیدن همه آموزش‌ها",
  },
  demos: {
    title: "همین حالا امتحان کنید",
    sub: "چند نمونه از ما را بازی کنید، بدون نیاز به ثبت‌نام.",
    games: "بازی‌ها",
    quizzes: "آزمونک‌ها",
    play: "بازی کنید",
    pages: "صفحه",
  },
  plans: {
    title: "طرح‌هایی برای هر کلاس",
    sub: "رایگان شروع کنید و هر وقت به امکانات بیشتری نیاز داشتید، به Premium ارتقا دهید.",
    perYear: "/سال",
    free: "رایگان",
    freePrice: "0 €",
    freeFeatures: ["2 پروژه", "1 کلاس", "1 پوشه", "تقریباً همه امکانات"],
    educators: "مربیان",
    educatorsFeatures: ["1 مربی", "تا 100 دانش‌آموز", "پروژه، کلاس و پوشه نامحدود", "نتایج و آمار هر دانش‌آموز"],
    teams: "تیم‌ها",
    teamsFeatures: ["5 مربی", "دانش‌آموز نامحدود", "مدیر لایسنس", "همه امکانات طرح مربیان"],
    schools: "مدرسه‌ها",
    schoolsFeatures: ["20 مربی", "دانش‌آموز نامحدود", "مدیر لایسنس", "همه امکانات طرح مربیان"],
    startFree: "رایگان شروع کنید",
    tryFree: "رایگان امتحان کنید",
    subscribe: "خرید اشتراک",
    note: "دانش‌آموزان همیشه رایگان از Make It استفاده می‌کنند.",
  },
  faq: {
    title: "پرسش‌های متداول",
    sub: "سؤالی دارید؟ رایج‌ترین پاسخ‌ها اینجاست.",
    items: [
      {
        q: "آزمونک‌ها چه هستند؟",
        a: "آزمونک‌ها روشی سرگرم‌کننده برای سنجش، یادگیری یا مرور هر موضوعی هستند. پاسخ‌ها و امتیاز هر بازیکن را به‌طور خودکار ثبت می‌کنند و شما می‌توانید پاسخ‌ها، رتبه‌بندی و آمار را ببینید.",
      },
      {
        q: "بازی‌ها چه هستند؟",
        a: "بازی‌ها برای کودکان خردسال طراحی شده‌اند، حتی آن‌هایی که هنوز خواندن بلد نیستند. تمرکز آن‌ها بر تقویت مثبت است: اشتباه‌ها جریمه نمی‌شوند و تعداد تلاش‌ها نامحدود است. کودکان برای بازی کردن نامی وارد نمی‌کنند.",
      },
      {
        q: "چطور نتایج بازی‌ها را ببینم؟",
        a: "از آنجا که کودکان هنگام بازی خود را معرفی نمی‌کنند، نتایج و آمار بازی‌ها فقط وقتی دیده می‌شود که بازی از طریق یک کلاس به اشتراک گذاشته شده باشد، نه با کد یا پیوند.",
      },
      {
        q: "محدودیت‌های نسخه رایگان چیست؟",
        a: "می‌توانید تقریباً از همه امکانات استفاده کنید. نسخه رایگان به شما اجازه می‌دهد 2 پروژه، 1 کلاس و 1 پوشه بسازید.",
      },
      { q: "با خرید اشتراک چه چیزی به دست می‌آورم؟", a: "بدون محدودیت: هر تعداد پروژه، کلاس و پوشه که بخواهید، و نتایج و آمار همه دانش‌آموزانتان." },
      {
        q: "آیا به حساب نیاز دارم؟",
        a: "برای بازی کردن، نه. با یک حساب می‌توانید پروژه‌هایتان را بسازید و در فضای ابری ذخیره کنید، آن‌ها را بین دستگاه‌هایتان همگام کنید و کلاس بسازید تا پروژه‌ها را با دانش‌آموزانتان به اشتراک بگذارید.",
      },
      { q: "آیا دانش‌آموزان باید پول بپردازند؟", a: "نه. دانش‌آموزان همیشه رایگان از Make It استفاده می‌کنند." },
      {
        q: "چطور یک پروژه را به اشتراک بگذارم؟",
        a: "پروژه را باز کنید و به «اشتراک‌گذاری» بروید: کد شش‌رقمی و پیوند آن را می‌بینید. هر کسی که آن‌ها را داشته باشد می‌تواند روی هر دستگاهی بازی کند. همچنین می‌توانید پروژه را با یک کلاس به اشتراک بگذارید تا دانش‌آموزان به هیچ کدی نیاز نداشته باشند.",
      },
      {
        q: "کلاس‌ها چه هستند و چطور کار می‌کنند؟",
        a: "کلاس‌ها به شما امکان می‌دهند گروه‌های دانش‌آموزان را مدیریت کنید. یک کلاس بسازید، کد آن را به دانش‌آموزانتان بدهید تا به آن بپیوندند و پروژه‌ها را با آن‌ها به اشتراک بگذارید: با همه یا فقط با بعضی. نتایج هر دانش‌آموز را خواهید دید.",
      },
      {
        q: "آیا می‌توانم روی هر دستگاهی از Make It استفاده کنم؟",
        a: "بله. می‌توانید در وب بسازید و بازی کنید، و برنامه برای آیپد، آیفون، اندروید و مک در دسترس است. پروژه‌هایتان همیشه همگام می‌مانند.",
      },
    ],
  },
  download: {
    title: "Make It روی همه دستگاه‌های شما",
    sub: "همین‌جا در مرورگر از آن استفاده کنید یا برنامه را دانلود کنید.",
    web: "باز کردن در وب",
    available: "دریافت از",
  },
  final: { title: "یادگیری را سرگرم‌کننده کنید", sub: "همین امروز اولین پروژه‌تان را بسازید. رایگان است." },
  footer: {
    tagline: "بازی، آزمونک، فعالیت و داستان بسازید و آن‌ها را هر جا که بخواهید به اشتراک بگذارید.",
    product: "محصول",
    help: "راهنما",
    legal: "حقوقی",
    contact: "تماس",
    terms: "شرایط و ضوابط",
    privacy: "حریم خصوصی",
    cookies: "سیاست کوکی‌ها",
    follow: "ما را دنبال کنید",
    support: "با حمایت ACCIÓ",
    demos: "نمونه‌ها",
  },
};

const ur: LandingText = {
  nav: {
    features: "خصوصیات",
    activities: "سرگرمیاں",
    videos: "ویڈیوز",
    plans: "پلانز",
    faq: "عام سوالات",
    download: "ڈاؤن لوڈ",
    login: "سائن ان کریں",
    signup: "مفت سائن اپ کریں",
    student: "میں طالب علم ہوں",
    menu: "مینو",
    language: "زبان",
  },
  hero: {
    eyebrow: "تعلیمی گیمز اور کوئزز",
    title: "منٹوں میں سیکھنے سکھانے کے گیمز اور کوئزز بنائیں",
    sub: "20 سے زیادہ قسم کی سرگرمیوں سے پروجیکٹس ڈیزائن کریں، انہیں کسی کے بھی ساتھ شیئر کریں اور نتائج فوراً دیکھیں۔ براؤزر میں، ٹیبلیٹ پر یا فون پر۔",
    start: "مفت شروع کریں",
    demo: "ڈیمو آزمائیں",
    codeTitle: "کوڈ ملا ہے؟",
    codeText: "آپ کو جو کوڈ دیا گیا ہے وہ لکھیں اور کھیلیں",
    free: "شروع کرنا مفت · کریڈٹ کارڈ کی ضرورت نہیں",
  },
  stats: { downloads: "ڈاؤن لوڈز", users: "فعال صارفین", projects: "بنائے گئے پروجیکٹس", countries: "ممالک" },
  grid: {
    title: "کھیل کھیل میں سیکھنے کے لیے سب کچھ",
    sub: "Make It بنانے، کھیلنے اور پیش رفت پر نظر رکھنے کو ایک ہی جگہ اکٹھا کرتا ہے۔",
    items: [
      { title: "منٹوں میں بنائیں", text: "ٹیمپلیٹس، تصاویر، آوازیں، ڈرائنگز اور YouTube ویڈیوز، سب آپ کی دسترس میں۔ کوڈنگ کی کوئی ضرورت نہیں۔" },
      { title: "20 سے زیادہ سرگرمیاں", text: "جوڑنا، گھسیٹنا، جوڑی تلاش کرنا، ترتیب دینا، لکھنا، رنگ بھرنا، علامتوں پر لکھنا اور بہت کچھ۔" },
      { title: "کوڈ سے شیئر کریں", text: "کوڈ یا لنک بھیجیں اور کسی بھی ڈیوائس پر کھیلیں، کچھ بھی انسٹال کیے بغیر۔" },
      { title: "فوری نتائج", text: "ہر کوئز کے جوابات، درجہ بندی اور اعداد و شمار، اور ہر طالب علم کی پیش رفت۔" },
      { title: "کلاس رومز", text: "طلبہ کوڈ سے شامل ہوتے ہیں۔ پروجیکٹس پوری کلاس کے ساتھ شیئر کریں یا صرف چند طلبہ کے ساتھ۔" },
      { title: "ہمیشہ ہم آہنگ", text: "آپ کے پروجیکٹس کلاؤڈ میں محفوظ ہوتے ہیں اور ویب اور ایپ دونوں پر تیار رہتے ہیں۔" },
    ],
  },
  create: {
    kicker: "بنائیں",
    title: "اپنا پروجیکٹ اپنے انداز میں ڈیزائن کریں",
    text: "ایک سرگرمی چنیں، اپنی تصاویر، متن اور آوازیں شامل کریں، اور آپ کا پروجیکٹ تیار ہے۔ آپ جو کچھ بھی کرتے ہیں وہ خود بخود محفوظ ہو جاتا ہے۔",
    bullets: [
      "ہزاروں تصاویر اور آوازوں والی لائبریریاں",
      "اپنی آواز ریکارڈ کریں",
      "سیدھا صفحے پر ڈرائنگ کریں",
      "YouTube ویڈیوز شامل کریں",
      "ٹیمپلیٹس، فونٹس اور پس منظر موسیقی",
    ],
  },
  play: {
    kicker: "گیمز",
    title: "ننھے بچوں کے لیے بنائے گئے",
    text: "گیمز محنت کو سراہتے ہیں: غلطیوں پر سزا نہیں ملتی، بچے جتنی بار چاہیں کوشش کر سکتے ہیں اور انہیں پڑھنا آنا بھی ضروری نہیں۔",
    bullets: ["ہر صفحے پر مثبت حوصلہ افزائی", "متن اور آواز کے ساتھ ہدایات", "کھیلنے کے لیے نہ نام چاہیے نہ اکاؤنٹ"],
  },
  quiz: {
    kicker: "کوئزز",
    title: "اسکور اور درجہ بندی والے کوئزز",
    text: "وقت کی حد والے سوالات، خودکار اسکورنگ اور لائیو درجہ بندی۔ آپ ہر کھلاڑی کے جوابات اور ہر سوال کے اعداد و شمار دیکھ سکتے ہیں۔",
    bullets: ["سوال، صحیح یا غلط، جواب لکھیں، ریاضی…", "فی سوال وقت کی حد", "درجہ بندی اور اعداد و شمار"],
  },
  share: {
    kicker: "شیئر کریں",
    title: "ایک کوڈ، اور کھیل شروع",
    text: "ہر پروجیکٹ کا چھ ہندسوں کا ایک کوڈ اور ایک لنک ہوتا ہے۔ طلبہ اسے براؤزر یا ایپ میں کھولتے ہیں، اکاؤنٹ بنائے بغیر۔",
    code: "کوڈ",
    link: "لنک",
    classroom: "یا اسے کسی کلاس روم کے ساتھ شیئر کریں",
  },
  classes: {
    kicker: "کلاس رومز",
    title: "ہر طالب علم پر نظر رکھیں",
    text: "کلاس روم بنائیں، اپنے طلبہ کو اس کا کوڈ دیں اور ان کے ساتھ پروجیکٹس شیئر کریں: خود بخود سب کے ساتھ یا صرف کچھ کے ساتھ۔",
    bullets: ["طلبہ کوڈ سے شامل ہوتے ہیں", "ہر گیم اور ہر کوئز کے نتائج", "پوری کلاس کے ساتھ یا چند طلبہ کے ساتھ شیئر کریں"],
    mockTitle: "نتائج",
    mockCols: ["طالب علم", "اسکور", "وقت"],
  },
  activities: { title: "20 سے زیادہ قسم کی سرگرمیاں", sub: "گیمز اور کوئزز کے لیے، آپ کے اپنے مواد سے بھرنے کے لیے تیار۔", games: "گیمز", quizzes: "کوئزز" },
  videos: {
    title: "Make It کو دریافت کریں",
    sub: "دیکھیں کہ چند منٹوں میں پروجیکٹ کیسے بنتا ہے۔",
    main: "Make It کیا ہے؟",
    tutorials: "ویڈیو ٹیوٹوریلز",
    quiz: "کوئز کیسے بنائیں",
    game: "گیم کیسے بنائیں",
    share: "پروجیکٹ کیسے شیئر کریں",
    all: "تمام ٹیوٹوریلز دیکھیں",
  },
  demos: { title: "ابھی آزمائیں", sub: "ہمارے کچھ ڈیمو کھیلیں، سائن اپ کی ضرورت نہیں۔", games: "گیمز", quizzes: "کوئزز", play: "کھیلیں", pages: "صفحات" },
  plans: {
    title: "ہر کلاس روم کے لیے پلانز",
    sub: "مفت شروع کریں اور جب مزید ضرورت ہو تو Premium پر جائیں۔",
    perYear: "/سال",
    free: "مفت",
    freePrice: "0 €",
    freeFeatures: ["2 پروجیکٹس", "1 کلاس روم", "1 فولڈر", "تقریباً تمام خصوصیات"],
    educators: "اساتذہ",
    educatorsFeatures: ["1 استاد", "100 طلبہ تک", "لامحدود پروجیکٹس، کلاس رومز اور فولڈرز", "ہر طالب علم کے نتائج اور اعداد و شمار"],
    teams: "ٹیمیں",
    teamsFeatures: ["5 اساتذہ", "لامحدود طلبہ", "لائسنس منیجر", "«اساتذہ» پلان کی ہر چیز"],
    schools: "اسکول",
    schoolsFeatures: ["20 اساتذہ", "لامحدود طلبہ", "لائسنس منیجر", "«اساتذہ» پلان کی ہر چیز"],
    startFree: "مفت شروع کریں",
    tryFree: "مفت آزمائیں",
    subscribe: "سبسکرائب کریں",
    note: "طلبہ ہمیشہ Make It مفت استعمال کرتے ہیں۔",
  },
  faq: {
    title: "اکثر پوچھے جانے والے سوالات",
    sub: "کوئی سوال ہے؟ یہاں سب سے عام جوابات ہیں۔",
    items: [
      {
        q: "کوئزز کیا ہیں؟",
        a: "کوئزز کسی بھی مضمون کو جانچنے، سیکھنے یا دہرانے کا ایک دلچسپ طریقہ ہیں۔ یہ ہر کھلاڑی کے جوابات اور اسکور خود بخود محفوظ کرتے ہیں، اور آپ جوابات، درجہ بندی اور اعداد و شمار دیکھ سکتے ہیں۔",
      },
      {
        q: "گیمز کیا ہیں؟",
        a: "گیمز چھوٹے بچوں کے لیے بنائے گئے ہیں، ان کے لیے بھی جو ابھی پڑھ نہیں سکتے۔ ان کا مرکز مثبت حوصلہ افزائی ہے: غلطیوں پر سزا نہیں ملتی اور کوششیں لامحدود ہیں۔ کھیلنے کے لیے بچے اپنا نام درج نہیں کرتے۔",
      },
      {
        q: "گیمز کے نتائج کیسے دیکھے جا سکتے ہیں؟",
        a: "چونکہ بچے کھیلتے وقت اپنی شناخت نہیں کراتے، اس لیے گیمز کے نتائج اور اعداد و شمار صرف اسی صورت میں دیکھے جا سکتے ہیں جب گیم کلاس روم کے ذریعے شیئر کیا گیا ہو، کوڈ یا لنک کے ذریعے نہیں۔",
      },
      {
        q: "مفت ورژن کی حدود کیا ہیں؟",
        a: "آپ تقریباً تمام خصوصیات استعمال کر سکتے ہیں۔ مفت ورژن میں آپ 2 پروجیکٹس، 1 کلاس روم اور 1 فولڈر بنا سکتے ہیں۔",
      },
      {
        q: "سبسکرائب کرنے سے مجھے کیا ملتا ہے؟",
        a: "کوئی حد نہیں: جتنے چاہیں پروجیکٹس، کلاس رومز اور فولڈرز، اور آپ کے تمام طلبہ کے نتائج اور اعداد و شمار۔",
      },
      {
        q: "کیا مجھے اکاؤنٹ کی ضرورت ہے؟",
        a: "کھیلنے کے لیے نہیں۔ اکاؤنٹ کے ساتھ آپ اپنے پروجیکٹس بنا کر کلاؤڈ میں محفوظ کر سکتے ہیں، انہیں اپنی تمام ڈیوائسز پر ہم آہنگ رکھ سکتے ہیں اور اپنے طلبہ کے ساتھ پروجیکٹس شیئر کرنے کے لیے کلاس رومز بنا سکتے ہیں۔",
      },
      { q: "کیا طلبہ کو ادائیگی کرنی پڑتی ہے؟", a: "نہیں۔ طلبہ ہمیشہ Make It مفت استعمال کرتے ہیں۔" },
      {
        q: "میں پروجیکٹ کیسے شیئر کروں؟",
        a: "پروجیکٹ کھولیں اور «شیئر کریں» پر جائیں: آپ کو اس کا چھ ہندسوں کا کوڈ اور اس کا لنک نظر آئے گا۔ جس کے پاس بھی یہ ہوں وہ اسے کسی بھی ڈیوائس پر کھیل سکتا ہے۔ آپ اسے کسی کلاس روم کے ساتھ بھی شیئر کر سکتے ہیں تاکہ طلبہ کو کسی کوڈ کی ضرورت نہ پڑے۔",
      },
      {
        q: "کلاس رومز کیا ہیں اور یہ کیسے کام کرتے ہیں؟",
        a: "کلاس رومز کے ذریعے آپ طلبہ کے گروپس کا انتظام کر سکتے ہیں۔ کلاس روم بنائیں، اپنے طلبہ کو اس کا کوڈ دیں تاکہ وہ شامل ہو سکیں، اور ان کے ساتھ پروجیکٹس شیئر کریں: سب کے ساتھ یا صرف کچھ کے ساتھ۔ آپ ہر طالب علم کے نتائج دیکھ سکیں گے۔",
      },
      {
        q: "کیا Make It کسی بھی ڈیوائس پر استعمال ہو سکتا ہے؟",
        a: "جی ہاں۔ آپ ویب پر بنا بھی سکتے ہیں اور کھیل بھی سکتے ہیں، اور ایپ iPad، iPhone، Android اور Mac کے لیے دستیاب ہے۔ آپ کے پروجیکٹس ہم آہنگ رہتے ہیں۔",
      },
    ],
  },
  download: {
    title: "Make It آپ کی تمام ڈیوائسز پر",
    sub: "اسے یہیں براؤزر میں استعمال کریں یا ایپ ڈاؤن لوڈ کریں۔",
    web: "ویب پر کھولیں",
    available: "یہاں دستیاب ہے",
  },
  final: { title: "سیکھنے کو مزے دار بنائیں", sub: "آج ہی اپنا پہلا پروجیکٹ بنائیں۔ یہ مفت ہے۔" },
  footer: {
    tagline: "گیمز، کوئزز، سرگرمیاں اور کہانیاں بنائیں، اور انہیں کہیں بھی شیئر کریں۔",
    product: "پروڈکٹ",
    help: "مدد",
    legal: "قانونی",
    contact: "رابطہ",
    terms: "شرائط و ضوابط",
    privacy: "رازداری",
    cookies: "کوکیز کی پالیسی",
    follow: "ہمیں فالو کریں",
    support: "ACCIÓ کے تعاون سے",
    demos: "ڈیمو",
  },
};

const ms: LandingText = {
  nav: {
    features: "Ciri-ciri",
    activities: "Aktiviti",
    videos: "Video",
    plans: "Pelan",
    faq: "Soalan lazim",
    download: "Muat turun",
    login: "Log masuk",
    signup: "Daftar percuma",
    student: "Saya murid",
    menu: "Menu",
    language: "Bahasa",
  },
  hero: {
    eyebrow: "Permainan dan kuiz pendidikan",
    title: "Cipta permainan pembelajaran dan kuiz dalam beberapa minit",
    sub: "Reka projek dengan lebih daripada 20 jenis aktiviti, kongsikannya dengan sesiapa sahaja dan lihat keputusannya serta-merta. Dalam pelayar, pada tablet atau pada telefon.",
    start: "Mula secara percuma",
    demo: "Cuba demo",
    codeTitle: "Ada kod?",
    codeText: "Taip kod yang diberikan kepada anda dan mula bermain",
    free: "Percuma untuk bermula · Tanpa kad kredit",
  },
  stats: { downloads: "muat turun", users: "pengguna aktif", projects: "projek dicipta", countries: "negara" },
  grid: {
    title: "Semua yang anda perlukan untuk belajar sambil bermain",
    sub: "Make It menghimpunkan aktiviti mencipta, bermain dan memantau di satu tempat.",
    items: [
      {
        title: "Cipta dalam beberapa minit",
        text: "Templat, imej, bunyi, lukisan dan video YouTube sedia untuk digunakan. Tiada pengekodan diperlukan.",
      },
      { title: "Lebih daripada 20 aktiviti", text: "Sambung, seret, cari pasangan, susun, taip, warnakan, surih simbol dan banyak lagi." },
      { title: "Kongsi dengan kod", text: "Hantar kod atau pautan dan main pada mana-mana peranti, tanpa perlu memasang apa-apa." },
      { title: "Keputusan serta-merta", text: "Jawapan, kedudukan dan statistik bagi setiap kuiz, serta kemajuan setiap murid." },
      { title: "Kelas", text: "Murid menyertai kelas dengan kod. Kongsi projek dengan seluruh kelas atau dengan beberapa murid sahaja." },
      { title: "Sentiasa disegerakkan", text: "Projek anda disimpan dalam awan dan sedia digunakan di web dan dalam aplikasi." },
    ],
  },
  create: {
    kicker: "Cipta",
    title: "Reka projek anda mengikut cara anda",
    text: "Pilih aktiviti, tambah imej, teks dan bunyi anda, dan projek anda sudah siap. Semua yang anda lakukan disimpan secara automatik.",
    bullets: [
      "Pustaka dengan beribu-ribu imej dan bunyi",
      "Rakam suara anda sendiri",
      "Lukis terus pada halaman",
      "Tambah video YouTube",
      "Templat, fon dan muzik latar",
    ],
  },
  play: {
    kicker: "Permainan",
    title: "Direka untuk si kecil",
    text: "Permainan menghargai usaha: kesilapan tidak dikenakan penalti, kanak-kanak boleh mencuba seberapa banyak kali yang mereka perlukan dan mereka tidak perlu tahu membaca pun.",
    bullets: ["Peneguhan positif pada setiap halaman", "Arahan dengan teks dan bunyi", "Tidak perlu nama atau akaun untuk bermain"],
  },
  quiz: {
    kicker: "Kuiz",
    title: "Kuiz dengan skor dan kedudukan",
    text: "Soalan bermasa, pemarkahan automatik dan kedudukan secara langsung. Anda dapat melihat jawapan setiap pemain dan statistik bagi setiap soalan.",
    bullets: ["Soalan, betul atau salah, taip jawapan, matematik…", "Had masa bagi setiap soalan", "Kedudukan dan statistik"],
  },
  share: {
    kicker: "Kongsi",
    title: "Satu kod, terus main",
    text: "Setiap projek mempunyai kod enam digit dan pautan. Murid membukanya dalam pelayar atau dalam aplikasi, tanpa perlu membuat akaun.",
    code: "Kod",
    link: "Pautan",
    classroom: "Atau kongsi dengan kelas",
  },
  classes: {
    kicker: "Kelas",
    title: "Pantau setiap murid",
    text: "Cipta kelas, berikan kodnya kepada murid anda dan kongsi projek dengan mereka, secara automatik dengan semua murid atau dengan beberapa orang sahaja.",
    bullets: ["Murid menyertai kelas dengan kod", "Keputusan setiap permainan dan setiap kuiz", "Kongsi dengan seluruh kelas atau dengan beberapa murid"],
    mockTitle: "Keputusan",
    mockCols: ["Murid", "Skor", "Masa"],
  },
  activities: {
    title: "Lebih daripada 20 jenis aktiviti",
    sub: "Untuk permainan dan kuiz, sedia untuk diisi dengan kandungan anda sendiri.",
    games: "Permainan",
    quizzes: "Kuiz",
  },
  videos: {
    title: "Kenali Make It",
    sub: "Lihat cara sesebuah projek dihasilkan dalam beberapa minit.",
    main: "Apakah Make It?",
    tutorials: "Tutorial video",
    quiz: "Cara mencipta kuiz",
    game: "Cara mencipta permainan",
    share: "Cara berkongsi projek",
    all: "Lihat semua tutorial",
  },
  demos: {
    title: "Cuba sekarang",
    sub: "Mainkan beberapa demo kami, tanpa perlu mendaftar.",
    games: "Permainan",
    quizzes: "Kuiz",
    play: "Main",
    pages: "halaman",
  },
  plans: {
    title: "Pelan untuk setiap kelas",
    sub: "Mula secara percuma dan naik taraf ke Premium apabila anda memerlukan lebih banyak.",
    perYear: "/tahun",
    free: "Percuma",
    freePrice: "0 €",
    freeFeatures: ["2 projek", "1 kelas", "1 folder", "Hampir semua ciri"],
    educators: "Pendidik",
    educatorsFeatures: ["1 pendidik", "Sehingga 100 murid", "Projek, kelas dan folder tanpa had", "Keputusan dan statistik setiap murid"],
    teams: "Pasukan",
    teamsFeatures: ["5 pendidik", "Murid tanpa had", "Pengurus lesen", "Semua ciri pelan Pendidik"],
    schools: "Sekolah",
    schoolsFeatures: ["20 pendidik", "Murid tanpa had", "Pengurus lesen", "Semua ciri pelan Pendidik"],
    startFree: "Mula secara percuma",
    tryFree: "Cuba secara percuma",
    subscribe: "Langgan",
    note: "Murid sentiasa menggunakan Make It secara percuma.",
  },
  faq: {
    title: "Soalan lazim",
    sub: "Ada sebarang keraguan? Berikut ialah jawapan kepada soalan yang paling kerap ditanya.",
    items: [
      {
        q: "Apakah itu kuiz?",
        a: "Kuiz ialah cara yang menyeronokkan untuk menilai, mempelajari atau mengulang kaji apa-apa mata pelajaran. Kuiz merekodkan jawapan dan skor setiap pemain secara automatik, dan anda boleh melihat jawapan, kedudukan dan statistiknya.",
      },
      {
        q: "Apakah itu permainan?",
        a: "Permainan direka untuk kanak-kanak kecil, termasuk mereka yang belum tahu membaca. Permainan mengutamakan peneguhan positif: kesilapan tidak dikenakan penalti dan percubaan tidak terhad. Kanak-kanak tidak perlu memasukkan nama untuk bermain.",
      },
      {
        q: "Bagaimanakah saya boleh melihat keputusan permainan?",
        a: "Oleh sebab kanak-kanak tidak memperkenalkan diri semasa bermain, keputusan dan statistik permainan hanya boleh dilihat apabila permainan itu dikongsi melalui kelas, bukan melalui kod atau pautan.",
      },
      {
        q: "Apakah had versi percuma?",
        a: "Anda boleh menggunakan hampir semua ciri. Versi percuma membolehkan anda mencipta 2 projek, 1 kelas dan 1 folder.",
      },
      {
        q: "Apakah yang saya dapat dengan melanggan?",
        a: "Tiada had: seberapa banyak projek, kelas dan folder yang anda mahu, serta keputusan dan statistik semua murid anda.",
      },
      {
        q: "Adakah saya memerlukan akaun?",
        a: "Untuk bermain, tidak. Dengan akaun, anda boleh mencipta dan menyimpan projek anda dalam awan, menyegerakkannya merentas peranti dan mencipta kelas untuk berkongsi projek dengan murid anda.",
      },
      { q: "Adakah murid perlu membayar?", a: "Tidak. Murid sentiasa menggunakan Make It secara percuma." },
      {
        q: "Bagaimanakah saya berkongsi projek?",
        a: "Buka projek dan pergi ke “Kongsi”: anda akan melihat kod enam digit dan pautannya. Sesiapa yang memilikinya boleh memainkan projek itu pada mana-mana peranti. Anda juga boleh berkongsi projek dengan kelas supaya murid tidak memerlukan sebarang kod.",
      },
      {
        q: "Apakah itu kelas dan bagaimanakah ia berfungsi?",
        a: "Kelas membolehkan anda mengurus kumpulan murid. Cipta kelas, berikan kodnya kepada murid anda supaya mereka boleh menyertainya, dan kongsi projek dengan mereka: dengan semua murid atau dengan beberapa orang sahaja. Anda akan melihat keputusan setiap murid.",
      },
      {
        q: "Bolehkah saya menggunakan Make It pada mana-mana peranti?",
        a: "Ya. Anda boleh mencipta dan bermain di web, dan aplikasinya tersedia untuk iPad, iPhone, Android dan Mac. Projek anda sentiasa disegerakkan.",
      },
    ],
  },
  download: {
    title: "Make It pada semua peranti anda",
    sub: "Gunakannya terus di sini dalam pelayar atau muat turun aplikasinya.",
    web: "Buka di web",
    available: "Dapatkan di",
  },
  final: { title: "Jadikan pembelajaran menyeronokkan", sub: "Cipta projek pertama anda hari ini. Percuma." },
  footer: {
    tagline: "Cipta permainan, kuiz, aktiviti dan cerita, dan kongsikannya di mana-mana sahaja.",
    product: "Produk",
    help: "Bantuan",
    legal: "Perundangan",
    contact: "Hubungi",
    terms: "Terma dan syarat",
    privacy: "Privasi",
    cookies: "Dasar kuki",
    follow: "Ikuti kami",
    support: "Dengan sokongan ACCIÓ",
    demos: "Demo",
  },
};

const az: LandingText = {
  nav: {
    features: "İmkanlar",
    activities: "Fəaliyyətlər",
    videos: "Videolar",
    plans: "Planlar",
    faq: "Suallar",
    download: "Yüklə",
    login: "Daxil ol",
    signup: "Pulsuz qeydiyyat",
    student: "Mən şagirdəm",
    menu: "Menu",
    language: "Dil",
  },
  hero: {
    eyebrow: "Təhsil oyunları və testlər",
    title: "Bir neçə dəqiqəyə öyrədici oyunlar və testlər yaradın",
    sub: "20-dən çox fəaliyyət növü ilə layihələr hazırlayın, onları istənilən şəxslə paylaşın və nəticələri dərhal görün. Brauzerdə, planşetdə və ya telefonda.",
    start: "Pulsuz başlayın",
    demo: "Demonu sınayın",
    codeTitle: "Kodunuz var?",
    codeText: "Sizə verilən kodu yazın və oynayın",
    free: "Başlamaq pulsuzdur · Kredit kartı tələb olunmur",
  },
  stats: {
    downloads: "yükləmə",
    users: "aktiv istifadəçi",
    projects: "yaradılmış layihə",
    countries: "ölkə",
  },
  grid: {
    title: "Oynayaraq öyrənmək üçün lazım olan hər şey",
    sub: "Make It yaratmağı, oynamağı və izləməyi bir yerdə birləşdirir.",
    items: [
      {
        title: "Bir neçə dəqiqəyə yaradın",
        text: "Şablonlar, şəkillər, səslər, rəsmlər və YouTube videoları əlinizin altında. Proqramlaşdırma bilmək lazım deyil.",
      },
      {
        title: "20-dən çox fəaliyyət",
        text: "Birləşdirin, sürüşdürün, cütü tapın, sıralayın, yazın, rəngləyin, simvollar çəkin və daha çoxu.",
      },
      {
        title: "Kodla paylaşın",
        text: "Kod və ya keçid göndərin və heç nə quraşdırmadan istənilən cihazda oynayın.",
      },
      {
        title: "Ani nəticələr",
        text: "Hər test üçün cavablar, reytinqlər və statistika, həmçinin hər şagirdin irəliləyişi.",
      },
      {
        title: "Siniflər",
        text: "Şagirdlər kodla qoşulur. Layihələri bütün siniflə və ya yalnız bir neçə şagirdlə paylaşın.",
      },
      {
        title: "Həmişə sinxron",
        text: "Layihələriniz buludda saxlanılır və vebdə də, tətbiqdə də hazırdır.",
      },
    ],
  },
  create: {
    kicker: "Yaradın",
    title: "Layihənizi istədiyiniz kimi hazırlayın",
    text: "Fəaliyyət seçin, şəkillərinizi, mətnlərinizi və səslərinizi əlavə edin, layihəniz hazırdır. Etdiyiniz hər şey avtomatik yadda saxlanılır.",
    bullets: [
      "Minlərlə şəkil və səsdən ibarət kitabxanalar",
      "Öz səsinizi yazın",
      "Birbaşa səhifənin üzərində rəsm çəkin",
      "YouTube videoları əlavə edin",
      "Şablonlar, şriftlər və fon musiqisi",
    ],
  },
  play: {
    kicker: "Oyunlar",
    title: "Balacalar üçün hazırlanıb",
    text: "Oyunlar səyi mükafatlandırır: səhvlər cəzalandırılmır, uşaqlar istədikləri qədər cəhd edə bilər və hətta oxumağı bilmələri də lazım deyil.",
    bullets: [
      "Hər səhifədə müsbət stimul",
      "Mətnli və səsli təlimatlar",
      "Oynamaq üçün ad və ya hesab lazım deyil",
    ],
  },
  quiz: {
    kicker: "Testlər",
    title: "Xallı və reytinqli testlər",
    text: "Vaxtla məhdud suallar, avtomatik qiymətləndirmə və canlı reytinq. Hər oyunçunun cavablarını və hər sualın statistikasını görürsünüz.",
    bullets: [
      "Sual, doğru və ya yanlış, cavabı yaz, riyaziyyat…",
      "Hər sual üçün vaxt limiti",
      "Reytinq və statistika",
    ],
  },
  share: {
    kicker: "Paylaşın",
    title: "Bir kod və oynayın",
    text: "Hər layihənin altırəqəmli kodu və keçidi var. Şagirdlər onu hesab yaratmadan brauzerdə və ya tətbiqdə açırlar.",
    code: "Kod",
    link: "Keçid",
    classroom: "Və ya siniflə paylaşın",
  },
  classes: {
    kicker: "Siniflər",
    title: "Hər şagirdi izləyin",
    text: "Sinif yaradın, onun kodunu şagirdlərinizə verin və layihələri onlarla paylaşın: avtomatik olaraq hamı ilə və ya yalnız bəziləri ilə.",
    bullets: [
      "Şagirdlər kodla qoşulur",
      "Hər oyunun və hər testin nəticələri",
      "Bütün siniflə və ya bir neçə şagirdlə paylaşın",
    ],
    mockTitle: "Nəticələr",
    mockCols: [
      "Şagird",
      "Xal",
      "Vaxt",
    ],
  },
  activities: {
    title: "20-dən çox fəaliyyət növü",
    sub: "Oyunlar və testlər üçün, öz məzmununuzla doldurmağa hazırdır.",
    games: "Oyunlar",
    quizzes: "Testlər",
  },
  videos: {
    title: "Make It ilə tanış olun",
    sub: "Layihənin bir neçə dəqiqəyə necə hazırlandığına baxın.",
    main: "Make It nədir?",
    tutorials: "Video dərsliklər",
    quiz: "Test necə yaradılır",
    game: "Oyun necə yaradılır",
    share: "Layihə necə paylaşılır",
    all: "Bütün dərsliklərə baxın",
  },
  demos: {
    title: "İndi sınayın",
    sub: "Qeydiyyatsız demolarımızdan bəzilərini oynayın.",
    games: "Oyunlar",
    quizzes: "Testlər",
    play: "Oyna",
    pages: "səhifə",
  },
  plans: {
    title: "Hər sinif üçün planlar",
    sub: "Pulsuz başlayın, daha çoxu lazım olduqda Premium-a keçin.",
    perYear: "/il",
    free: "Pulsuz",
    freePrice: "0 €",
    freeFeatures: [
      "2 layihə",
      "1 sinif",
      "1 folder",
      "Demək olar ki, bütün imkanlar",
    ],
    educators: "Müəllimlər",
    educatorsFeatures: [
      "1 müəllim",
      "100 şagirdə qədər",
      "Limitsiz layihə, sinif və qovluq",
      "Hər şagirdin nəticələri və statistikası",
    ],
    teams: "Komandalar",
    teamsFeatures: [
      "5 müəllim",
      "Limitsiz şagird",
      "Lisenziya meneceri",
      "«Müəllimlər» planındakı hər şey",
    ],
    schools: "Məktəblər",
    schoolsFeatures: [
      "20 müəllim",
      "Limitsiz şagird",
      "Lisenziya meneceri",
      "«Müəllimlər» planındakı hər şey",
    ],
    startFree: "Pulsuz başlayın",
    tryFree: "Pulsuz sınayın",
    subscribe: "Abunə ol",
    note: "Şagirdlər Make It-dən həmişə pulsuz istifadə edir.",
  },
  faq: {
    title: "Tez-tez verilən suallar",
    sub: "Sualınız var? Ən çox verilən sualların cavabları buradadır.",
    items: [
      {
        q: "Testlər nədir?",
        a: "Testlər istənilən fənni qiymətləndirmək, öyrənmək və ya təkrarlamaq üçün əyləncəli üsuldur. Onlar hər oyunçunun cavablarını və xalını avtomatik qeyd edir, siz isə cavablara, reytinqə və statistikaya baxa bilərsiniz.",
      },
      {
        q: "Oyunlar nədir?",
        a: "Oyunlar balaca uşaqlar, hətta hələ oxuya bilməyənlər üçün nəzərdə tutulub. Onlarda əsas diqqət müsbət stimula yönəlib: səhvlər cəzalandırılmır və cəhdlərin sayı limitsizdir. Uşaqlar oynamaq üçün ad daxil etmirlər.",
      },
      {
        q: "Oyunların nəticələrinə necə baxa bilərəm?",
        a: "Uşaqlar oynayarkən özlərini təqdim etmədikləri üçün oyunların nəticələri və statistikası yalnız oyun kod və ya keçidlə deyil, sinif vasitəsilə paylaşıldıqda görünür.",
      },
      {
        q: "Pulsuz versiyanın məhdudiyyətləri nələrdir?",
        a: "Demək olar ki, bütün imkanlardan istifadə edə bilərsiniz. Pulsuz versiyada 2 layihə, 1 sinif və 1 qovluq yarada bilərsiniz.",
      },
      {
        q: "Abunə olmaqla nə əldə edirəm?",
        a: "Heç bir məhdudiyyət yoxdur: istədiyiniz qədər layihə, sinif və qovluq, həmçinin bütün şagirdlərinizin nəticələri və statistikası.",
      },
      {
        q: "Mənə hesab lazımdır?",
        a: "Oynamaq üçün yox. Hesabla layihələrinizi yaradıb buludda saxlaya, cihazlar arasında sinxronlaşdıra və layihələri şagirdlərinizlə paylaşmaq üçün siniflər yarada bilərsiniz.",
      },
      {
        q: "Şagirdlər ödəniş etməlidirmi?",
        a: "Xeyr. Şagirdlər Make It-dən həmişə pulsuz istifadə edir.",
      },
      {
        q: "Layihəni necə paylaşım?",
        a: "Layihəni açın və «Paylaş» bölməsinə keçin: onun altırəqəmli kodunu və keçidini görəcəksiniz. Bunlar kimdə varsa, istənilən cihazda oynaya bilər. Layihəni siniflə də paylaşa bilərsiniz, onda şagirdlərə heç bir kod lazım olmur.",
      },
      {
        q: "Siniflər nədir və necə işləyir?",
        a: "Siniflər şagird qruplarını idarə etməyə imkan verir. Sinif yaradın, qoşulmaları üçün onun kodunu şagirdlərinizə verin və layihələri onlarla paylaşın: hamı ilə və ya yalnız bəziləri ilə. Hər şagirdin nəticələrini görəcəksiniz.",
      },
      {
        q: "Make It-dən istənilən cihazda istifadə edə bilərəm?",
        a: "Bəli. Vebdə yarada və oynaya bilərsiniz, tətbiq isə iPad, iPhone, Android və Mac üçün mövcuddur. Layihələriniz sinxron saxlanılır.",
      },
    ],
  },
  download: {
    title: "Make It bütün cihazlarınızda",
    sub: "Elə burada, brauzerdə istifadə edin və ya tətbiqi yükləyin.",
    web: "Vebdə aç",
    available: "Yükləyin:",
  },
  final: {
    title: "Öyrənməyi əyləncəli edin",
    sub: "İlk layihənizi bu gün yaradın. Pulsuzdur.",
  },
  footer: {
    tagline: "Oyunlar, testlər, fəaliyyətlər və hekayələr yaradın və onları hər yerdə paylaşın.",
    product: "Məhsul",
    help: "Kömək",
    legal: "Hüquqi məlumat",
    contact: "Əlaqə",
    terms: "Şərtlər və qaydalar",
    privacy: "Məxfilik",
    cookies: "Kuki siyasəti",
    follow: "Bizi izləyin",
    support: "ACCIÓ-nun dəstəyi ilə",
    demos: "Demolar",
  },
};

const uz: LandingText = {
  nav: {
    features: "Imkoniyatlar",
    activities: "Mashgʻulotlar",
    videos: "Videolar",
    plans: "Tariflar",
    faq: "Savol-javob",
    download: "Yuklab olish",
    login: "Kirish",
    signup: "Bepul roʻyxatdan oʻtish",
    student: "Men oʻquvchiman",
    menu: "Menu",
    language: "Til",
  },
  hero: {
    eyebrow: "Taʼlimiy oʻyinlar va viktorinalar",
    title: "Bir necha daqiqada oʻquv oʻyinlari va viktorinalar yarating",
    sub: "20 dan ortiq turdagi mashgʻulotlar bilan loyihalar yarating, ularni istalgan kishi bilan ulashing va natijalarni darhol koʻring. Brauzerda, planshetda yoki telefonda.",
    start: "Bepul boshlash",
    demo: "Namunani sinab koʻring",
    codeTitle: "Kodingiz bormi?",
    codeText: "Sizga berilgan kodni kiriting va oʻynang",
    free: "Boshlash bepul · Bank kartasi shart emas",
  },
  stats: {
    downloads: "yuklab olish",
    users: "faol foydalanuvchi",
    projects: "yaratilgan loyiha",
    countries: "mamlakat",
  },
  grid: {
    title: "Oʻynab oʻrganish uchun kerakli hamma narsa",
    sub: "Make It yaratish, oʻynash va natijalarni kuzatishni bir joyga jamlaydi.",
    items: [
      {
        title: "Bir necha daqiqada yarating",
        text: "Shablonlar, rasmlar, tovushlar, chizmalar va YouTube videolari doim qoʻl ostida. Dasturlash shart emas.",
      },
      {
        title: "20 dan ortiq mashgʻulot",
        text: "Bogʻlash, tortish, juftini topish, tartiblash, yozish, boʻyash, belgilarni chizish va yana koʻp narsalar.",
      },
      {
        title: "Kod orqali ulashing",
        text: "Kod yoki havola yuboring va hech narsa oʻrnatmasdan istalgan qurilmada oʻynang.",
      },
      {
        title: "Tezkor natijalar",
        text: "Har bir viktorina boʻyicha javoblar, reyting va statistika hamda har bir oʻquvchining oʻzlashtirishi.",
      },
      {
        title: "Sinflar",
        text: "Oʻquvchilar kod orqali qoʻshiladi. Loyihalarni butun sinf bilan yoki faqat ayrim oʻquvchilar bilan ulashing.",
      },
      {
        title: "Doim sinxron",
        text: "Loyihalaringiz bulutda saqlanadi va vebda ham, ilovada ham tayyor turadi.",
      },
    ],
  },
  create: {
    kicker: "Yaratish",
    title: "Loyihangizni oʻzingiz xohlagandek yarating",
    text: "Mashgʻulotni tanlang, rasmlar, matnlar va tovushlarni qoʻshing, loyihangiz tayyor. Barcha oʻzgarishlar avtomatik saqlanadi.",
    bullets: [
      "Minglab rasm va tovushlardan iborat kutubxonalar",
      "Oʻz ovozingizni yozib oling",
      "Toʻgʻridan-toʻgʻri sahifada chizing",
      "YouTube videolarini qoʻshing",
      "Shablonlar, shriftlar va fon musiqasi",
    ],
  },
  play: {
    kicker: "Oʻyinlar",
    title: "Eng kichiklar uchun yaratilgan",
    text: "Oʻyinlar harakatni ragʻbatlantiradi: xatolar uchun jazo yoʻq, bolalar kerakli marta urinib koʻrishi mumkin va hatto oʻqishni bilishlari shart emas.",
    bullets: [
      "Har bir sahifada ijobiy ragʻbatlantirish",
      "Matnli va ovozli koʻrsatmalar",
      "Oʻynash uchun ism ham, hisob ham kerak emas",
    ],
  },
  quiz: {
    kicker: "Viktorinalar",
    title: "Ball va reytingli viktorinalar",
    text: "Vaqtli savollar, avtomatik baholash va jonli reyting. Har bir ishtirokchining javoblarini va har bir savol statistikasini koʻrasiz.",
    bullets: [
      "Savol, toʻgʻri yoki notoʻgʻri, javobni yozing, matematika…",
      "Har bir savol uchun vaqt chegarasi",
      "Reyting va statistika",
    ],
  },
  share: {
    kicker: "Ulashish",
    title: "Bitta kod va oʻynayvering",
    text: "Har bir loyihaning olti xonali kodi va havolasi bor. Oʻquvchilar uni hisob yaratmasdan brauzerda yoki ilovada ochadi.",
    code: "Kod",
    link: "Havola",
    classroom: "Yoki uni sinf bilan ulashing",
  },
  classes: {
    kicker: "Sinflar",
    title: "Har bir oʻquvchini kuzatib boring",
    text: "Sinf yarating, oʻquvchilaringizga uning kodini bering va loyihalarni ular bilan ulashing: avtomatik ravishda hammasi bilan yoki faqat ayrimlari bilan.",
    bullets: [
      "Oʻquvchilar kod orqali qoʻshiladi",
      "Har bir oʻyin va har bir viktorina natijalari",
      "Butun sinf yoki bir nechta oʻquvchi bilan ulashing",
    ],
    mockTitle: "Natijalar",
    mockCols: [
      "Oʻquvchi",
      "Ball",
      "Vaqt",
    ],
  },
  activities: {
    title: "20 dan ortiq turdagi mashgʻulotlar",
    sub: "Oʻyinlar va viktorinalar uchun, oʻz mazmuningiz bilan toʻldirishga tayyor.",
    games: "Oʻyinlar",
    quizzes: "Viktorinalar",
  },
  videos: {
    title: "Make It bilan tanishing",
    sub: "Loyiha bir necha daqiqada qanday yaratilishini koʻring.",
    main: "Make It nima?",
    tutorials: "Video darsliklar",
    quiz: "Viktorina qanday yaratiladi",
    game: "Oʻyin qanday yaratiladi",
    share: "Loyiha qanday ulashiladi",
    all: "Barcha darsliklarni koʻrish",
  },
  demos: {
    title: "Hoziroq sinab koʻring",
    sub: "Namunalarimizdan birini oʻynang, roʻyxatdan oʻtish shart emas.",
    games: "Oʻyinlar",
    quizzes: "Viktorinalar",
    play: "Oʻynash",
    pages: "sahifa",
  },
  plans: {
    title: "Har bir sinf uchun tariflar",
    sub: "Bepul boshlang, koʻproq kerak boʻlganda Premium tarifiga oʻting.",
    perYear: "/yil",
    free: "Bepul",
    freePrice: "0 €",
    freeFeatures: [
      "2 ta loyiha",
      "1 ta sinf",
      "1 folder",
      "Deyarli barcha imkoniyatlar",
    ],
    educators: "Pedagoglar",
    educatorsFeatures: [
      "1 pedagog",
      "100 tagacha oʻquvchi",
      "Cheksiz loyihalar, sinflar va jildlar",
      "Har bir oʻquvchining natijalari va statistikasi",
    ],
    teams: "Jamoalar",
    teamsFeatures: [
      "5 pedagog",
      "Cheksiz oʻquvchilar",
      "Litsenziya administratori",
      "\"Pedagoglar\" tarifidagi hamma narsa",
    ],
    schools: "Maktablar",
    schoolsFeatures: [
      "20 pedagog",
      "Cheksiz oʻquvchilar",
      "Litsenziya administratori",
      "\"Pedagoglar\" tarifidagi hamma narsa",
    ],
    startFree: "Bepul boshlash",
    tryFree: "Bepul sinab koʻring",
    subscribe: "Obuna boʻlish",
    note: "Oʻquvchilar Make It xizmatidan doim bepul foydalanadi.",
  },
  faq: {
    title: "Koʻp beriladigan savollar",
    sub: "Savollaringiz bormi? Eng koʻp uchraydigan javoblar shu yerda.",
    items: [
      {
        q: "Viktorinalar nima?",
        a: "Viktorinalar istalgan fanni qiziqarli tarzda baholash, oʻrganish yoki takrorlash usulidir. Ular har bir ishtirokchining javoblari va ballini avtomatik qayd etadi, siz esa javoblar, reyting va statistikani koʻrishingiz mumkin.",
      },
      {
        q: "Oʻyinlar nima?",
        a: "Oʻyinlar kichik yoshdagi bolalar, hatto hali oʻqishni bilmaydiganlar uchun moʻljallangan. Ularda asosiy eʼtibor ijobiy ragʻbatlantirishga qaratilgan: xatolar uchun jazo yoʻq va urinishlar soni cheklanmagan. Bolalar oʻynash uchun ism kiritmaydi.",
      },
      {
        q: "Oʻyin natijalarini qanday koʻrish mumkin?",
        a: "Bolalar oʻynayotganda oʻzini tanishtirmagani sababli, oʻyin natijalari va statistikasini faqat oʻyin sinf orqali ulashilganda koʻrish mumkin, kod yoki havola orqali emas.",
      },
      {
        q: "Bepul versiyaning qanday cheklovlari bor?",
        a: "Deyarli barcha imkoniyatlardan foydalanishingiz mumkin. Bepul versiyada 2 ta loyiha, 1 ta sinf va 1 ta jild yaratish mumkin.",
      },
      {
        q: "Obuna menga nima beradi?",
        a: "Hech qanday cheklov yoʻq: istalgancha loyiha, sinf va jild hamda barcha oʻquvchilaringizning natijalari va statistikasi.",
      },
      {
        q: "Menga hisob kerakmi?",
        a: "Oʻynash uchun kerak emas. Hisob bilan loyihalaringizni yaratib, bulutda saqlashingiz, ularni qurilmalar oʻrtasida sinxronlashingiz va oʻquvchilaringiz bilan loyiha ulashish uchun sinflar yaratishingiz mumkin.",
      },
      {
        q: "Oʻquvchilar pul toʻlashi kerakmi?",
        a: "Yoʻq. Oʻquvchilar Make It xizmatidan doim bepul foydalanadi.",
      },
      {
        q: "Loyihani qanday ulashaman?",
        a: "Loyihani oching va \"Ulashish\" boʻlimiga oʻting: uning olti xonali kodi va havolasini koʻrasiz. Ular kimda boʻlsa, istalgan qurilmada oʻynay oladi. Shuningdek, loyihani sinf bilan ulashishingiz mumkin, shunda oʻquvchilarga hech qanday kod kerak boʻlmaydi.",
      },
      {
        q: "Sinflar nima va ular qanday ishlaydi?",
        a: "Sinflar oʻquvchilar guruhlarini boshqarish imkonini beradi. Sinf yarating, oʻquvchilar qoʻshilishi uchun ularga sinf kodini bering va loyihalarni ular bilan ulashing: hammasi bilan yoki faqat ayrimlari bilan. Har bir oʻquvchining natijalarini koʻrasiz.",
      },
      {
        q: "Make It xizmatidan istalgan qurilmada foydalansa boʻladimi?",
        a: "Ha. Vebda yaratishingiz va oʻynashingiz mumkin, ilova esa iPad, iPhone, Android va Mac uchun mavjud. Loyihalaringiz doim sinxronlanadi.",
      },
    ],
  },
  download: {
    title: "Make It barcha qurilmalaringizda",
    sub: "Shu yerning oʻzida brauzerda foydalaning yoki ilovani yuklab oling.",
    web: "Vebda ochish",
    available: "Yuklab oling:",
  },
  final: {
    title: "Oʻqishni qiziqarli qiling",
    sub: "Birinchi loyihangizni bugunoq yarating. Bu bepul.",
  },
  footer: {
    tagline: "Oʻyinlar, viktorinalar, mashgʻulotlar va hikoyalar yarating hamda ularni istalgan joyda ulashing.",
    product: "Mahsulot",
    help: "Yordam",
    legal: "Huquqiy maʼlumot",
    contact: "Aloqa",
    terms: "Shartlar va qoidalar",
    privacy: "Maxfiylik",
    cookies: "Cookie siyosati",
    follow: "Bizni kuzating",
    support: "ACCIÓ koʻmagida",
    demos: "Namunalar",
  },
};

const fil: LandingText = {
  nav: {
    features: "Mga Feature",
    activities: "Mga Aktibidad",
    videos: "Mga Video",
    plans: "Mga Plan",
    faq: "FAQ",
    download: "I-download",
    login: "Mag-sign in",
    signup: "Mag-sign up nang libre",
    student: "Estudyante ako",
    menu: "Menu",
    language: "Wika",
  },
  hero: {
    eyebrow: "Mga pang-edukasyong laro at quiz",
    title: "Gumawa ng mga laro at quiz para matuto sa loob ng ilang minuto",
    sub: "Magdisenyo ng mga proyekto gamit ang higit sa 20 uri ng aktibidad, i-share ang mga ito kahit kanino at makita agad ang mga resulta. Sa browser, sa tablet o sa phone.",
    start: "Magsimula nang libre",
    demo: "Subukan ang demo",
    codeTitle: "May code ka?",
    codeText: "I-type ang code na ibinigay sa iyo at maglaro",
    free: "Libreng magsimula · Walang credit card",
  },
  stats: {
    downloads: "download",
    users: "aktibong user",
    projects: "proyektong nagawa",
    countries: "bansa",
  },
  grid: {
    title: "Lahat ng kailangan mo para matuto habang naglalaro",
    sub: "Pinagsasama ng Make It sa iisang lugar ang paggawa, paglalaro at pagsubaybay.",
    items: [
      {
        title: "Gumawa sa loob ng ilang minuto",
        text: "Mga template, larawan, tunog, drawing at YouTube video na abot-kamay. Hindi kailangang marunong mag-code.",
      },
      {
        title: "Higit sa 20 aktibidad",
        text: "Pagdugtungin, i-drag, hanapin ang kapares, pagsunud-sunurin, i-type, kulayan, bakatin ang mga simbolo at marami pang iba.",
      },
      {
        title: "I-share gamit ang code",
        text: "Magpadala ng code o link at maglaro sa kahit anong device, nang walang ini-install.",
      },
      {
        title: "Agarang resulta",
        text: "Mga sagot, ranking at istatistika sa bawat quiz, at ang progreso ng bawat estudyante.",
      },
      {
        title: "Mga klase",
        text: "Sumasali ang mga estudyante gamit ang code. I-share ang mga proyekto sa buong klase o sa iilan lang.",
      },
      {
        title: "Laging naka-sync",
        text: "Naka-save sa cloud ang iyong mga proyekto at handa sa web at sa app.",
      },
    ],
  },
  create: {
    kicker: "Gumawa",
    title: "Idisenyo ang proyekto mo sa sarili mong paraan",
    text: "Pumili ng aktibidad, idagdag ang iyong mga larawan, teksto at tunog, at handa na ang proyekto mo. Awtomatikong nase-save ang lahat ng ginagawa mo.",
    bullets: [
      "Mga library na may libu-libong larawan at tunog",
      "I-record ang sarili mong boses",
      "Gumuhit mismo sa pahina",
      "Magdagdag ng YouTube video",
      "Mga template, font at background music",
    ],
  },
  play: {
    kicker: "Mga Laro",
    title: "Ginawa para sa mga bata",
    text: "Ginagantimpalaan ng mga laro ang pagsisikap: hindi pinaparusahan ang mga mali, puwedeng sumubok ang mga bata nang kahit ilang beses at hindi na nila kailangang marunong magbasa.",
    bullets: [
      "Positive reinforcement sa bawat pahina",
      "Mga instruction na may teksto at tunog",
      "Hindi kailangan ng pangalan o account para maglaro",
    ],
  },
  quiz: {
    kicker: "Mga Quiz",
    title: "Mga quiz na may iskor at ranking",
    text: "Mga tanong na may oras, awtomatikong pag-iskor at live na ranking. Makikita mo ang mga sagot ng bawat player at ang istatistika ng bawat tanong.",
    bullets: [
      "Tanong, tama o mali, i-type ang sagot, math…",
      "Limitasyon sa oras bawat tanong",
      "Ranking at istatistika",
    ],
  },
  share: {
    kicker: "I-share",
    title: "Isang code at laro na",
    text: "Bawat proyekto ay may anim na digit na code at link. Binubuksan ito ng mga estudyante sa browser o sa app, nang hindi gumagawa ng account.",
    code: "Code",
    link: "Link",
    classroom: "O i-share ito sa isang klase",
  },
  classes: {
    kicker: "Mga Klase",
    title: "Subaybayan ang bawat estudyante",
    text: "Gumawa ng klase, ibigay sa iyong mga estudyante ang code nito at i-share sa kanila ang mga proyekto, awtomatiko sa lahat o sa ilan lang.",
    bullets: [
      "Sumasali ang mga estudyante gamit ang code",
      "Resulta ng bawat laro at bawat quiz",
      "I-share sa buong klase o sa iilan",
    ],
    mockTitle: "Mga Resulta",
    mockCols: [
      "Estudyante",
      "Iskor",
      "Oras",
    ],
  },
  activities: {
    title: "Higit sa 20 uri ng aktibidad",
    sub: "Para sa mga laro at quiz, handang lagyan ng sarili mong content.",
    games: "Mga Laro",
    quizzes: "Mga Quiz",
  },
  videos: {
    title: "Kilalanin ang Make It",
    sub: "Tingnan kung paano gumawa ng proyekto sa loob ng ilang minuto.",
    main: "Ano ang Make It?",
    tutorials: "Mga video tutorial",
    quiz: "Paano gumawa ng quiz",
    game: "Paano gumawa ng laro",
    share: "Paano mag-share ng proyekto",
    all: "Tingnan ang lahat ng tutorial",
  },
  demos: {
    title: "Subukan na ngayon",
    sub: "Laruin ang ilan sa aming mga demo, hindi kailangang mag-sign up.",
    games: "Mga Laro",
    quizzes: "Mga Quiz",
    play: "Maglaro",
    pages: "pahina",
  },
  plans: {
    title: "Mga plan para sa bawat klase",
    sub: "Magsimula nang libre at mag-Premium kapag kailangan mo ng higit pa.",
    perYear: "/taon",
    free: "Libre",
    freePrice: "0 €",
    freeFeatures: [
      "2 proyekto",
      "1 klase",
      "1 folder",
      "Halos lahat ng feature",
    ],
    educators: "Mga Guro",
    educatorsFeatures: [
      "1 guro",
      "Hanggang 100 estudyante",
      "Walang limitasyong proyekto, klase at folder",
      "Resulta at istatistika ng bawat estudyante",
    ],
    teams: "Mga Team",
    teamsFeatures: [
      "5 guro",
      "Walang limitasyong estudyante",
      "License manager",
      "Lahat ng nasa Mga Guro",
    ],
    schools: "Mga Paaralan",
    schoolsFeatures: [
      "20 guro",
      "Walang limitasyong estudyante",
      "License manager",
      "Lahat ng nasa Mga Guro",
    ],
    startFree: "Magsimula nang libre",
    tryFree: "Subukan nang libre",
    subscribe: "Mag-subscribe",
    note: "Laging libre ang Make It para sa mga estudyante.",
  },
  faq: {
    title: "Mga madalas itanong",
    sub: "May tanong ka? Narito ang mga pinakakaraniwang sagot.",
    items: [
      {
        q: "Ano ang mga quiz?",
        a: "Ang mga quiz ay masayang paraan para mag-assess, matuto o mag-review ng anumang subject. Awtomatiko nilang itinatala ang mga sagot at iskor ng bawat player, at makikita mo ang mga sagot, ranking at istatistika.",
      },
      {
        q: "Ano ang mga laro?",
        a: "Ang mga laro ay dinisenyo para sa maliliit na bata, pati sa mga hindi pa marunong magbasa. Nakatuon ang mga ito sa positive reinforcement: hindi pinaparusahan ang mga mali at walang limitasyon ang pagsubok. Hindi naglalagay ng pangalan ang mga bata para maglaro.",
      },
      {
        q: "Paano ko makikita ang resulta ng mga laro?",
        a: "Dahil hindi nagpapakilala ang mga bata kapag naglalaro sila, makikita lang ang mga resulta at istatistika ng mga laro kapag na-share ang laro sa pamamagitan ng klase, hindi sa code o link.",
      },
      {
        q: "Ano ang mga limitasyon ng libreng bersyon?",
        a: "Magagamit mo ang halos lahat ng feature. Sa libreng bersyon, puwede kang gumawa ng 2 proyekto, 1 klase at 1 folder.",
      },
      {
        q: "Ano ang makukuha ko kapag nag-subscribe ako?",
        a: "Walang limitasyon: kahit ilang proyekto, klase at folder ang gusto mo, at ang mga resulta at istatistika ng lahat ng iyong estudyante.",
      },
      {
        q: "Kailangan ko ba ng account?",
        a: "Para maglaro, hindi. Kapag may account ka, puwede kang gumawa at mag-save ng mga proyekto sa cloud, i-sync ang mga ito sa iba't ibang device at gumawa ng mga klase para i-share ang mga proyekto sa iyong mga estudyante.",
      },
      {
        q: "Kailangan bang magbayad ng mga estudyante?",
        a: "Hindi. Laging libre ang Make It para sa mga estudyante.",
      },
      {
        q: "Paano ako magse-share ng proyekto?",
        a: "Buksan ang proyekto at pumunta sa I-share: makikita mo ang anim na digit na code at link nito. Puwede itong laruin ng sinumang may hawak ng mga ito sa kahit anong device. Puwede mo rin itong i-share sa isang klase para hindi na kailangan ng mga estudyante ng code.",
      },
      {
        q: "Ano ang mga klase at paano gumagana ang mga ito?",
        a: "Sa mga klase, mapapamahalaan mo ang mga grupo ng estudyante. Gumawa ng klase, ibigay sa iyong mga estudyante ang code nito para makasali sila, at i-share sa kanila ang mga proyekto: sa lahat o sa ilan lang. Makikita mo ang resulta ng bawat estudyante.",
      },
      {
        q: "Magagamit ko ba ang Make It sa kahit anong device?",
        a: "Oo. Puwede kang gumawa at maglaro sa web, at available ang app para sa iPad, iPhone, Android at Mac. Laging naka-sync ang iyong mga proyekto.",
      },
    ],
  },
  download: {
    title: "Make It sa lahat ng iyong device",
    sub: "Gamitin ito rito mismo sa browser o i-download ang app.",
    web: "Buksan sa web",
    available: "Kunin sa",
  },
  final: {
    title: "Gawing masaya ang pag-aaral",
    sub: "Gawin ang una mong proyekto ngayon. Libre ito.",
  },
  footer: {
    tagline: "Gumawa ng mga laro, quiz, aktibidad at kuwento, at i-share ang mga ito kahit saan.",
    product: "Produkto",
    help: "Tulong",
    legal: "Legal",
    contact: "Makipag-ugnayan",
    terms: "Mga tuntunin at kundisyon",
    privacy: "Privacy",
    cookies: "Patakaran sa cookies",
    follow: "I-follow kami",
    support: "Sa suporta ng ACCIÓ",
    demos: "Mga Demo",
  },
};

const TEXTS: Record<string, LandingText> = { en, ca, es, fr, pt, it, de, ar, he, eu, gl, nl, pl, ro, sv, el, tr, ru, uk, id, vi, cs, sk, hu, hr, bg, da, nb, fi, fa, ur, ms, az, uz, fil };

/** The landing texts of the UI language (English when there are none). */
export function landingText(): LandingText {
  return TEXTS[currentLanguage()] ?? en;
}

/** Native names for the language picker. */
export const LANGUAGE_NAMES: Record<string, string> = { ca: "Català", es: "Español", en: "English", fr: "Français", pt: "Português", it: "Italiano", de: "Deutsch", eu: "Euskara", gl: "Galego", nl: "Nederlands", da: "Dansk", sv: "Svenska", nb: "Norsk bokmål", fi: "Suomi", pl: "Polski", cs: "Čeština", sk: "Slovenčina", hu: "Magyar", ro: "Română", hr: "Hrvatski", el: "Ελληνικά", bg: "Български", tr: "Türkçe", az: "Azərbaycanca", uz: "Oʻzbekcha", ru: "Русский", uk: "Українська", id: "Bahasa Indonesia", ms: "Bahasa Melayu", fil: "Filipino", vi: "Tiếng Việt", ar: "العربية", he: "עברית", fa: "فارسی", ur: "اردو" };
